import { checkDocumentExist } from "@/api/documentexist";
import { ipValidate, luceneValidate, scrollValidate } from "@/api/formValidate";
import { _dateFormat } from "@/libs/dateformat.js";
import { wafActionToString, wafCodeToValue, sortGroupRoles } from "@/libs/utils.js";
import { diffString, diff } from "json-diff";
import { getFormOptions } from "@/api/formoptions";
import { resendOtpMail } from "@/api/user";
import { resetPwdMail } from "@/api/user";
import { removeFromGroup } from "@/api/user";
import { getMaxRecordLb, getPlatformPlan } from "@/api/system";
import { getMaxSubdomainLength } from "@/api/system";
import { _updateData } from "@/api/esdata";
import { getSlbExternCheckStats } from "@/api/monitor";
import { getGroupApiKeys } from "@/api/groupasset.js";
import enumList from "@/libs/enumList";

import {
    assignDefaultValue,
    arrayToObject,
    colorNames,
    statusToColor,
    switchStatusToI18n,
    boolToIcons,
    boolToColors,
    wafRuleToIcon,
    generateHtmlPreview,
    outgoingRateToStatusName,
    latencyToColorName,
    hasVaptchaApiKeys,
} from "@/libs/rcmTableFieldsFunction";

import prettier from "prettier-standalone";

// 賦予表單欄位選項
function assignOptions(vm, field, options) {
    try {
        let formItemIndex = vm.formDataSetup.findIndex((item) => item.bindCol == field);
        vm.formDataSetup[formItemIndex].options = options;
    } catch (err) {}
}

// 賦予表單欄位禁止選項
function assignDisabledOptions(vm, field, disabledOptions) {
    try {
        let formItemIndex = vm.formDataSetup.findIndex((item) => item.bindCol == field);
        vm.formDataSetup[formItemIndex].disabledOptions = disabledOptions;
    } catch (err) {}
}

// 賦予表單欄位多選最大值
function assignMaxItemsCount(vm, field, val) {
    try {
        let formItemIndex = vm.formDataSetup.findIndex((item) => item.bindCol == field);
        vm.formDataSetup[formItemIndex].maxItemsCount = val;
    } catch (err) {}
}

// 驗證合法域名
function isQualifiedServername(servername, vm) {
    var splitLen = servername.split(".").length;
    var s = 0;
    let errorMessage = "";
    servername.split(".").forEach((e, i) => {
        if (/^[a-z0-9\-\.]*$/.test(e) && /./g.test(e)) {
            s += 1;
        }
        if (e.startsWith("-") || e.endsWith("-")) {
            s -= 1;
            errorMessage = `${vm.$i18n.t("rcmtable.validateHyphen")}`;
        }
        if (e.length > 63) {
            s -= 1;
            errorMessage = `${vm.$i18n.t("rcmtable.exceedDomainLabelSizeLimit")}`;
        }
        // else 的 errorMessage 預設為 Illegal character, expect one of: a-z 0-9 - _
        if (errorMessage) {
            throw new Error(errorMessage);
        }
    });
    // console.log(splitLen, s);
    return splitLen == s ? true : false;
}

// 驗證合法Wildcard域名
function isQualifiedWildcardServername(servername, isAllowWildcardDomain) {
    var splitLen = servername.split(".").length;
    if (splitLen < 3) return false;

    var s = 0;
    servername.split(".").forEach((e, i) => {
        if ((/^[a-z0-9\-\.]*$/.test(e) && /./g.test(e)) || (i == 0 && e == "_wildcard" && isAllowWildcardDomain)) {
            s += 1;
        }
        if (e.endsWith("-")) {
            s -= 1;
        }
        if (e.length > 63) {
            s -= 1;
            throw new Error(`${vm.$i18n.t("rcmtable.exceedDomainLabelSizeLimit")}`);
        }
    });
    // console.log(splitLen, s);
    return splitLen == s ? true : false;
}

const countryCode = require("./countryCode");
const formOptions = require("./formOptions");

// 固定欄位屬性
const tableColumnWidth = {
    selection: 32,
    groupName: 180,
    serviceGroupName: 250,
    appProfileName: 250,
    serverName: 250,
    recordLineGroupName: 200,
    createBy: 180,
    createdDate: 200,
    modifiedDate: 200,
    serviceIP: 180,
    serviceVersion: 180,
    connectState: 160,
    ruleName: 180,
    status: 180,
};

const inputAutoSize = { minRows: 4, maxRows: 6 };

const runningStatFilterOptions = [
    { label: "Success", value: "success" },
    { label: "Warning", value: "warning" },
    { label: "Error", value: "error" },
    { label: "Default", value: "default" },
];

/*
 * == 參數說明: 通用 ==
 * key: 資料欄位名稱
 * index: 後台對應索引
 * searchViews: 哪些頁面可以套用此物件
 * title: 顯示名稱
 *
 * == 參數說明: Table(表格) ==
 * withTable: 是否有 table 欄位, 預設 true
 * showInTable: 是否預設顯示(可以透過顯示欄位切換), 預設false
 * type: table 欄位屬性
 * width: table 欄位寬度
 * minWidth: table 欄位最小寬度
 * maxWidth: table 欄位最大寬度
 * sortable: table 是否支持排序. 預設: true
 * tooltip: 超出欄位不換行, 使用氣泡提示
 * filter: 進階搜尋參數(string, select, date, boolean, range, number, object)
 * render: table 欄位使用 render 函式渲染
 * exportOptions: 欄位匯出參數
 * exportOptions.true: 點擊匯出時是否匯出該欄位, 預設 true
 * exportOptions.callback: 欄位匯出format
 *
 * == 參數說明 Form(表單) ==
 * withForm: 是否有表單欄位, 預設 false
 * formTypes: 哪種表單可以出現(創建, 編輯, 批量編輯)
 * onFormInit: 表單資料就緒時的動作(頁面載入時)
 * onFormShown: 表單開啟時的動作
 * formRender: 表單型態(輸入, 選擇, 勾選等)
 * fieldType: 表單欄位屬性
 * isFormSlot: 是否為 slot 欄位, 預設 false; 若 true, 則提交前會刪除該欄位
 * vShowFieldVal: 是否顯示於表單, 0 代表不顯示
 * defaultVal: 表單預設值
 * quickAddProps: 提供表單快速新增的 template
 * watchField: 當表單值發生改變時的動作
 * disabled: 是否禁止填寫
 * placeholder:
 * sectionKey: 表單欄位所屬區塊
 * description: 欄位敘述
 * validate: 表單驗證
 * options: 表單選項, 可用 getFormOptions 獲取, assignOptions 帶入
 * disabledOptions: 禁用選項, 可用 getFormOptions 獲取, assignDisabledOptions 帶入
 * maxItemsCount: 多選最大限制, 可用 assignMaxItemsCount 帶入
 * autosize: 表單欄位是否自動調整高度(textarea使用)
 *
 * == 參數說明: Filter(表格) ==
 * filterDisabled: filter 是否禁止編輯, 預設 false
 *
 * */

export default function (vm) {
    const isAdmin = vm.$store.state.userData.privilege == "admin";
    const isShowCustomSettingEnabledGroups =
        vm.$store.state.platformPlan.multiTenancyConfigs.isOriginPolicyCustomSettingEnabled || [];
    const userInGroupNames = vm.$store.getters.inGroupNames;
    const resourceLimits = vm.$store.getters.resourceLimits;
    const { currentGroupResourceLimits } = vm.$store.getters;
    const checkFeature = (featureName) => {
        const { features } = vm.$store.getters;
        // console.log(features);
        return features.includes(featureName);
    };
    const checkFeatureLeastPlan = (featureName) => {
        const { planSpec } = vm.$store.state.platformPlan;
        let leastPlan = planSpec.find((e) => e.features.includes(featureName));
        return leastPlan.planName;
    };

    return [
        /*
         * 通用欄位
         * */
        {
            searchViews: ["*"],
            key: "selection",
            fixed: "left",
            width: 32,
            showInTable: true,
            type: "selection",
        },
        {
            searchViews: ["*"],
            key: "groupName",
            fixed: "left",
            title: vm.$i18n.t("domainsetting.groupName"),
            sectionKey: "essential",
            description: "<p>Which group you are assigning to. Resource from each group are independent.</p>",
            showInTable: true,
            withForm: true,
            minWidth: tableColumnWidth.groupName,
            formTypes: ["createForm"],
            formRender: "FormSelect",
            options: [],
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.groupNames = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`groupnames?${queryString}`);
                return result.data.options;
            },
            onFormShown: (formData) => {},
            watchField: function (item) {},
            defaultVal: vm.$store.state.queryGroupName == "all" ? "" : vm.$store.state.queryGroupName,
            filter: {
                type: "string",
                defaultValue: vm.$store.state.queryGroupName == "all" ? "*" : vm.$store.state.queryGroupName,
            },
            filterDisabled: vm.$store.state.queryGroupName !== "all",
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("domainsetting.groupName")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
            vShowFieldVal: () => 1,
            disabled: () => vm.$store.state.queryGroupName !== "all",
        },
        {
            searchViews: ["*"],
            key: "settingZoneExplain",
            withTable: false,
            description: "<p></p>",
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["*"],
            key: "createBy",
            minWidth: tableColumnWidth.createBy,
            title: vm.$i18n.t("domainsetting.createBy"),
            showInTable: false,
            withForm: false,
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["*"],
            key: "createdDate",
            minWidth: tableColumnWidth.createdDate,
            title: vm.$i18n.t("domainsetting.createdDate"),
            showInTable: false,
            withForm: false,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.createdDate, "YYYY-MM-DD HH:mm:ss"));
            },
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["*"],
            key: "modifiedDate",
            title: vm.$i18n.t("domainsetting.modifiedDate"),
            showInTable: false,
            withForm: false,
            minWidth: 250,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.modifiedDate, "YYYY-MM-DD HH:mm:ss"));
            },
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["*"],
            key: "formSlot",
            withForm: true,
            isFormSlot: true,
            withTable: false,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            onFormShown: (formData) => {},
            onFormInit: () => {},
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["*"],
            key: "status",
            title: vm.$i18n.t("domainsetting.status"),
            showInTable: true,
            withForm: false,
            minWidth: 100,
            defaultVal: "",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 0,
            render: (h, params) => {
                return h("div", switchStatusToI18n(vm, params.row.status));
            },
        },
        {
            // searchViews: ["DomainSetting", "AppProfile", "CacheRules", "ClearCache", "ProfileDomain"],
            searchViews: ["*"],
            key: "servername",
            fixed: "left",
            minWidth: tableColumnWidth.serverName,
            title: vm.$i18n.t("domainsetting.servername"),
            sectionKey: "essential",
            description:
                "<p>The <strong>FQDN</strong> you want to put on our CDN, this value must be uniqe, the cname value will generate after you filled up.</p><ul> <li>FQDN must be less than <strong>64 characters</strong> to apply Sectigo cert.</li></ul>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            placeholder: "www.example.com",
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("domainsetting.servername")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },

                {
                    validator: async (rule, value, callback, source, options) => {
                        let maxSubdomainLengthResult = await getMaxSubdomainLength();
                        let maxSubdomainLength = maxSubdomainLengthResult.data.data;
                        let { isAllowDomainPerGroup, isAllowWildcardDomain } =
                            vm.$store.state.platformPlan.multiTenancyConfigs;
                        let checkExistGroupName = isAllowDomainPerGroup ? vm.formData.groupName : undefined;
                        if (vm.withAfterBulkCreateModal) {
                            var serverNames = value.split("\n");
                            serverNames.forEach((e, i) => {
                                if (
                                    !isQualifiedServername(e, vm) &&
                                    !isQualifiedWildcardServername(e, isAllowWildcardDomain)
                                )
                                    throw new Error(
                                        `${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a-z 0-9 - _ .`
                                    );
                                if (e.split(".").length < 2)
                                    return callback(
                                        new Error(`${e} ${vm.$i18n.t("rcmtable.validateInputMustContainDots")}`)
                                    );
                                if (e.split(".").length > maxSubdomainLength)
                                    return callback(
                                        new Error(
                                            `${e} ${vm.$i18n.t(
                                                "rcmtable.validateSubdomainLengthText"
                                            )} + ${maxSubdomainLength}`
                                        )
                                    );
                                if (e.length > 63)
                                    return callback(
                                        new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 63`)
                                    );
                            });

                            var checkDocumentExistResult = await checkDocumentExist({
                                topic: "servername",
                                fieldVal: serverNames,
                                groupName: checkExistGroupName,
                            });
                            var resultMessage = checkDocumentExistResult.data.message;
                            if (!checkDocumentExistResult.data.success)
                                return callback(
                                    new Error(`${resultMessage} ${vm.$i18n.t("rcmtable.validateExistText")}`)
                                );
                            callback();
                        } else {
                            if (
                                !isQualifiedServername(value, vm) &&
                                !isQualifiedWildcardServername(value, isAllowWildcardDomain)
                            )
                                return callback(
                                    new Error(`${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a-z 0-9 - _ .`)
                                );
                            if (value.split(".").length < 2)
                                return callback(new Error(`${vm.$i18n.t("rcmtable.validateInputMustContainDots")}`));
                            // if (value.split(".").length > 3)
                            //   return callback(new Error("僅支持三級子域名"));

                            if (value.split(".").length > maxSubdomainLength)
                                return callback(
                                    new Error(
                                        `${vm.$i18n.t("rcmtable.validateSubdomainLengthText")}: ${maxSubdomainLength}`
                                    )
                                );
                            let checkDocumentExistResult = await checkDocumentExist({
                                topic: "servername",
                                fieldVal: value,
                                groupName: checkExistGroupName,
                            });
                            if (value.length > 63)
                                return callback(new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 63`));
                            if (!checkDocumentExistResult.data.success)
                                return callback(new Error(`${value} ${vm.$i18n.t("rcmtable.validateExistText")}`));
                            callback();
                        }
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (value, item) {
                if (vm.withAfterBulkCreateModal) {
                    var domains = value.split("\n");
                    if (domains.length > 1) {
                        vm.actionType = "bulk-create";
                    }
                    var domainItems = domains.map((domain) => {
                        var cnameTo = `${domain}.${vm.getEsResult.cnameRootDomain}`;

                        return {
                            servername: domain,
                            cnameHint: `${cnameTo}`,
                            cnameHtml: `<CopyText v-bind:text="'${cnameTo}'"></CopyText>`,
                        };
                    });
                    vm.$emit("passDomainList", domainItems);
                    if (vm.formType == "createForm") {
                        vm.formData.keypairname = "self-sign";
                    }
                } else {
                    let cnameTo = `${value}.${vm.getEsResult.cnameRootDomain}`;
                    if (item.cnameHint !== undefined)
                        item.cnameHint = `<p>${value} </br><strong>CNAME</strong> to </br> ${cnameTo} <CopyText v-bind:text="'${cnameTo}'"></CopyText></p>`;
                }
            },
            filter: {
                type: "string",
                defaultValue: vm.$route.query.servername,
            },
            render: (h, params) => {
                const p = h("p", params.row.servername);

                const tags = assignDefaultValue(params.row.tags, []);
                const servernameTags = tags.map((tagName) => h("Tag", {}, tagName));

                const isRequiredOwnershipValidate = assignDefaultValue(params.row.isRequiredOwnershipValidate, false);

                let servernameSplit = params.row.servername.split(".");
                let len = servernameSplit.length;
                let rootDomain = `${servernameSplit[len - 2]}.${servernameSplit[len - 1]}`;
                const p1 = [
                    h("span", `Create a TXT record for `),
                    h("span", { style: { "font-weight": "bold" } }, `${params.row.txtDomainRecord}.${rootDomain}`),
                    h("span", ` with following value`),
                ];
                const p2 = h("CopyText", {
                    props: { text: params.row.dcvTxtValue, copyText: `${params.row.dcvTxtValue}` },
                });
                const explain = [...p1, p2];

                const explainPoptip = h(
                    "Poptip",
                    {
                        props: {
                            title: `Domain Control Validation Required`,
                            transfer: true,
                        },
                    },
                    [
                        h(
                            "div",
                            {
                                slot: "content",
                            },
                            explain
                        ),
                        h("a", [
                            h("Icon", {
                                props: {
                                    type: "md-warning",
                                    size: 24,
                                    color: colorNames.orange,
                                },
                            }),
                        ]),
                    ]
                );
                const row1 = isRequiredOwnershipValidate == true ? [explainPoptip, p] : [p];
                return h("div", [
                    h(
                        "span",
                        {
                            style: {
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                            },
                        },
                        row1
                    ),
                    ...servernameTags,
                ]);
            },
        },
        {
            // searchViews: ["DomainSetting", "ProfileDomain", "AppProfile"],
            searchViews: ["*"],
            key: "appProfileName",
            fixed: "left",
            minWidth: tableColumnWidth.appProfileName,
            title: vm.$i18n.t("domainsetting.appProfileName"),
            sectionKey: "essential",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            defaultVal: "",
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.appProfileName = `*${queryValue}*`;
                // 選項依參數domainWithNoneAppProfile決定是否回傳none
                queryParams.withNone = vm.$store.getters.getDomainConfig.domainWithNoneAppProfile;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/app-profilenames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("domainsetting.appProfileName")}${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
            filter: {
                type: "string",
                defaultValue: vm.$route.query.appProfileName,
            },
        },
        {
            searchViews: ["AppProfile"],
            key: "appProfileDomainsLength",
            title: vm.$i18n.t("domainsetting.appProfileDomainsLength"),
            showInTable: true,
            withForm: false,
            minWidth: 200,
            render: (h, params) => {
                let appProfileDomainsLength =
                    params.row.appProfileDomainsLength == 0 ? "Ready to go" : params.row.appProfileDomainsLength;
                return h("span", appProfileDomainsLength);
            },
            exportOptions: { canExport: true, callback: (value) => value },
            sortable: false,
            filter: {
                type: "range",
            },
        },
        {
            // searchViews: ["DomainSetting", "AppProfile", "CacheRules", "ClearCache", "ServiceGroup"],
            searchViews: ["*"],
            key: "serviceGroupName",
            minWidth: tableColumnWidth.serviceGroupName,
            title: vm.$i18n.t("domainsetting.serviceGroupName"),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            options: [],
            optionStyle: 2,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.serviceGroupNames = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/servicegroupnames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            defaultVal: "",
            filter: {
                type: "string",
                defaultValue: vm.$route.query.serviceGroupName,
            },
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("domainsetting.serviceGroupName")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
            render: (h, params) => {
                let serviceGroup = vm.getEsResult.serviceGroups.find(
                    (e) => e.serviceGroupName == params.row.serviceGroupName && e.groupName == params.row.groupName
                );
                if (!serviceGroup) return h("p", {}, "");
                let nameEle = h("span", {}, serviceGroup.serviceGroupName);
                let descriptionEle = h(
                    "span",
                    {
                        style: {
                            float: "right",
                            color: "rgb(204, 204, 204)",
                        },
                    },
                    serviceGroup.description
                );
                let btn = h(
                    "getResourceBtn",
                    {
                        props: {
                            permissionKey: "groupResourcesPermission",
                            title: params.row.serviceGroupName,
                            endPoint: "servicegroup",
                            docId: `${serviceGroup._id}`,
                            groupName: params.row.groupName,
                            keyField: "serviceGroupName",
                            managePageName: "ServiceGroup",
                            renderFields: isAdmin
                                ? [
                                      { key: "groupName", title: vm.$i18n.t("domainsetting.groupName") },
                                      { key: "description", title: vm.$i18n.t("domainsetting.description") },
                                      { key: "isEnforceProtect", title: vm.$i18n.t("servicegroup.isEnforceProtect") },
                                      { key: "isenabled", title: vm.$i18n.t("servicegroup.isenabled") },
                                      { key: "protectSlbs", title: vm.$i18n.t("servicegroup.protectSlbs") },
                                      { key: "recordType", title: vm.$i18n.t("servicegroup.recordType") },
                                      { key: "slbs", title: vm.$i18n.t("servicegroup.slbs") },
                                      { key: "createBy", title: vm.$i18n.t("domainsetting.createBy") },
                                      {
                                          key: "createdDate",
                                          title: vm.$i18n.t("domainsetting.createdDate"),
                                          render: (h, params) => {
                                              return h("div", _dateFormat(params.createdDate, "YYYY-MM-DD HH:mm:ss"));
                                          },
                                      },
                                      {
                                          key: "modifiedDate",
                                          title: vm.$i18n.t("domainsetting.modifiedDate"),
                                          render: (h, params) => {
                                              return h("div", _dateFormat(params.modifiedDate, "YYYY-MM-DD HH:mm:ss"));
                                          },
                                      },
                                  ]
                                : [
                                      { key: "groupName", title: vm.$i18n.t("domainsetting.groupName") },
                                      { key: "description", title: vm.$i18n.t("servicegroup.description") },
                                      { key: "isenabled", title: vm.$i18n.t("servicegroup.isenabled") },
                                      { key: "recordType", title: vm.$i18n.t("servicegroup.recordType") },
                                      { key: "slbs", title: vm.$i18n.t("servicegroup.slbs") },
                                      { key: "createBy", title: vm.$i18n.t("domainsetting.createBy") },
                                      {
                                          key: "createdDate",
                                          title: vm.$i18n.t("domainsetting.createdDate"),
                                          render: (h, params) => {
                                              return h("div", _dateFormat(params.createdDate, "YYYY-MM-DD HH:mm:ss"));
                                          },
                                      },
                                      {
                                          key: "modifiedDate",
                                          title: vm.$i18n.t("domainsetting.modifiedDate"),
                                          render: (h, params) => {
                                              return h("div", _dateFormat(params.modifiedDate, "YYYY-MM-DD HH:mm:ss"));
                                          },
                                      },
                                  ],
                        },
                    },
                    ""
                );

                return vm.$can("read", "groupResourcesPermission", "ServiceGroup")
                    ? h("div", {}, [btn, descriptionEle])
                    : h("div", {}, [nameEle, descriptionEle]);
            },
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "errorHtmlPageName",
            title: vm.$i18n.t("domainsetting.errorHtmlPageName"),
            sectionKey: "advanced",
            description: checkFeature("CUSTOM_PAGE")
                ? `<p>Select custom page on 5xx error.</p>`
                : `<p>Select custom page on 5xx error.</p> <br> Available on: ${checkFeatureLeastPlan("CUSTOM_PAGE")}`,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSelect",
            multiple: false,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.htmlPageName = `*${queryValue}*`;
                queryParams.withDefault = true;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/htmlpagenames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            defaultVal: "default",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => (isAdmin || checkFeature("CUSTOM_PAGE") ? 1 : 0),
            disabled: !checkFeature("CUSTOM_PAGE"),
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "upstreamTimeout",
            title: vm.$i18n.t("domainsetting.upstreamTimeout"),
            sectionKey: "advanced",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            options: [
                { label: "60 s", value: 60 },
                { label: "120 s", value: 120 },
                { label: "180 s", value: 180 },
            ],
            defaultVal: 60,
            filter: {
                type: "range",
            },
            watchField: function (item) {},
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "setCustomHostHeader",
            title: vm.$i18n.t("domainsetting.setCustomHostHeader"),
            sectionKey: "origins",
            description:
                "<p>If the domain you are trying to access is different from current profile servername, this should be turned on.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: "off",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            trueValue: "on",
            falseValue: "off",
            render: (h, params) => {
                return h(
                    "Tag",
                    {
                        props: {
                            color: params.row.setCustomHostHeader == "on" ? "success" : "default",
                        },
                    },
                    params.row.setCustomHostHeader
                );
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "customHostHeader",
            title: vm.$i18n.t("domainsetting.customHostHeader"),
            sectionKey: "origins",
            description: "<p>This is the servername for the origin, default is the current profile servername.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.setCustomHostHeader === "on"){
                            if(value == "") {
                                return callback(
                                    new Error(
                                        `${vm.$i18n.t("domainsetting.customHostHeader")} ${vm.$i18n.t(
                                            "rcmtable.validateEmptyText"
                                        )}`
                                    )
                                );
                            }
                            ipValidate("origin", [value]).then((result) => {
                                if (!result.data.success) {
                                    return callback(new Error(result.data.message));
                                }
                                callback();
                            })
                        }
                    },
                    trigger: "blur",
                },
            ],
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "WafEvent"],
            key: "scheme",
            description:
                "<p>The scheme you want to setup for your site, <strong>http</strong> or <strong>https</strong>. We recommand to use both of them, as <strong>https</strong> provide fast and secure connection.</p>",
            explainPoptip: "ExplainScheme",
            title: vm.$i18n.t("domainsetting.scheme"),
            sectionKey: "http_https",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            formRender: "FormCheckboxGroup",
            options: ["http", "https"],
            defaultVal: ["http", "https"],
            vShowFieldVal: () => 1,
            watchField: function (value, item) {
            //     if (!value.includes("http") || !value.includes("https")) {
            //         item.redirecthttps = false;
            //     }
                if (!value.includes("https")) {
                    item.useSystemCert = false;
                }
            },
            filter: {
                type: "string",
            },
            validate: [
                {
                    required: true,
                    type: "array",
                    message: `${vm.$i18n.t("domainsetting.scheme")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
            render: (h, params) => {
                return h(
                    "div",
                    params.row.scheme.map(function (s) {
                        return h(
                            "Tag",
                            {
                                props: {},
                            },
                            s
                        );
                    })
                );
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "httpPort",
            description:
                "<p>If you want your site using different from the default(80) port of http, just change/add the value in this field. Other http usable ports are <strong>8080, 8880, 2052, 2082, 2086, 2095</strong></p>",
            title: vm.$i18n.t("domainsetting.httpPort"),
            sectionKey: "http_https",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            formWidth: "100px",
            tooltip: true,
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.origins = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/groupby/httpPort?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            selectAllowCreate: true,
            multiSelectWithToolbox: false,
            inputValType: "Number",
            placeholder: "add port",
            defaultVal: [80],
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.httpPort,
                        maxShow: "5",
                        title: vm.$i18n.t("domainsetting.httpPort"),
                        width: "350",
                    },
                });
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "httpPort 最少填寫一個",
                    trigger: "change",
                },
                {
                    required: true,
                    type: "array",
                    max: currentGroupResourceLimits.maxDomainListenPortCount,
                    message: `httpPort 最多 ${currentGroupResourceLimits.maxDomainListenPortCount} 個`,
                    trigger: "change",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        formOptions
                            .httpUseablePorts()
                            .then((useablePorts) => {
                                if (useablePorts.length > 0) {
                                    let errorPorts = value.filter((x) => !useablePorts.includes(parseInt(x)));
                                    return errorPorts.length > 0
                                        ? callback(new Error(`Port Out Of Range: ${errorPorts}`))
                                        : callback();
                                }
                            })
                            .catch((err) => {
                                console.log(err);
                            });
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "httpsPort",
            title: vm.$i18n.t("domainsetting.httpsPort"),
            sectionKey: "http_https",
            description:
                "<p>If you want your site using different from the default(443) port of https, just change/add the value in this field. Other https usable ports are: <ul> <li>Any ports in <strong>1001-4999</strong></li><li>Any ports in <strong>5000-6000, if set at System / BackEnd by administrator</strong></li><li>Any ports in <strong>6001-32767, excludes http useable ports, unsafe ports, and 9600 for Logstash</strong></li></ul></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            formWidth: "100px",
            tooltip: true,
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.origins = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/groupby/httpsPort?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            selectAllowCreate: true,
            multiSelectWithToolbox: false,
            inputValType: "Number",
            placeholder: "add port",
            defaultVal: [443],
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.httpsPort,
                        maxShow: "5",
                        title: vm.$i18n.t("domainsetting.httpsPort"),
                        width: "350",
                    },
                });
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "httpsPort 最少填寫一個",
                    trigger: "change",
                },
                {
                    required: true,
                    type: "array",
                    max: currentGroupResourceLimits.maxDomainListenPortCount,
                    message: `httpsPort 最多 ${currentGroupResourceLimits.maxDomainListenPortCount} 個`,
                    trigger: "change",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (
                            (vm.formData.scheme && vm.formData.scheme.includes("https")) ||
                            vm.formType == "batchEditForm"
                        ) {
                            formOptions
                                .httpsUseablePorts()
                                .then((useablePorts) => {
                                    if (useablePorts.length > 0) {
                                        let errorPorts = value.filter((x) => !useablePorts.includes(parseInt(x)));
                                        return errorPorts.length > 0
                                            ? callback(new Error(`Port Out Of Range: ${errorPorts}`))
                                            : callback();
                                    }
                                })
                                .catch((err) => {
                                    console.log(err);
                                });
                        }
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "redirecthttps",
            title: vm.$i18n.t("domainsetting.redirecthttps"),
            sectionKey: "http_https",
            description:
                "<p>This will enable a <strong>redirect</strong> from <strong>HTTP</strong> to <strong>HTTPS</strong>.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            validate: [
                {
                    validator: (rule, value, callback) => {
                        const formData = vm.formType === "batchEditForm" ? vm.$refs.BatchEditForm.batchFormData : vm.formData;
                        if (value === true && (!formData.scheme || !formData.scheme.includes("https"))) {
                            return callback(
                                new Error(
                                    `${vm.$i18n.t("domainsetting.scheme")} ${vm.$i18n.t(
                                        "rcmtable.schemeValidateError"
                                    )}`
                                )
                            );
                        } else {
                            callback();
                        }
                    },
                },
            ],
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "redirectHttpCode",
            title: vm.$i18n.t("domainsetting.redirectHttpCode"),
            sectionKey: "http_https",
            description: "<p>The http code for redirecting.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            width: 80,
            formRender: "FormSelect",
            multiple: false,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.codeName = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/redirect-http-code?${queryString}`);
                return result.data.options;
            },
            // queryParamsFields: [{ fieldName: "groupName", queryIf: val => val !== "" }],
            options: [],
            defaultVal: "302",
            filter: {
                type: "string",
            },
            vShowFieldVal: (item) => (item.redirecthttps ? 1 : 0),
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "GroupResource"],
            key: "useHttp2",
            title: vm.$i18n.t("domainsetting.useHttp2"),
            description: "<p>Toggle HTTP/2.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "useHsts",
            title: vm.$i18n.t("domainsetting.useHsts"),
            sectionKey: "http_https",
            description: "<p>Toggle HSTS.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "ProfileDomain"],
            key: "hasSystemCert",
            title: vm.$i18n.t("domainsetting.hasSystemCert"),
            align: "center",
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                const hasSystemCert = params.row.hasSystemCert;
                const iconType = boolToIcons({
                    value: hasSystemCert,
                    falseToDash: true,
                });
                const iconColor = hasSystemCert ? colorNames.green : colorNames.grey;
                const statusIcon = h("Icon", {
                    props: {
                        type: iconType,
                        color: iconColor,
                        size: 24,
                    },
                });

                // wildcard 域名
                const explainPoptipForWildcard = h("WildcardCSRPoptip", { props: { params: params } });
                // 一般域名
                const explainPoptip = h("DomainCertPoptip", { props: { params: params } });
                const isWildcard = params.row.servername.startsWith("_wildcard");

                const content = isWildcard
                    ? [statusIcon, explainPoptipForWildcard]
                    : hasSystemCert
                    ? [statusIcon]
                    : [statusIcon, explainPoptip];
                return h("div", content);
            },
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["DomainSetting", "ProfileDomain"],
            key: "useSystemCert",
            title: vm.$i18n.t("domainsetting.useSystemCert"),
            sectionKey: "http_https",
            description: `<div><p>Don't have your own cert? No worries, we provide sectigo positive SSL for you!</p> <img src='/static/img/positivessl_trust_seal_sm_124x32.png'></div>`,
            showInTable: false,
            withForm: true,
            formTypes: () => ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
            explainPoptip: "ExplainPoptip",
            explainPoptipWithTable: true,
            explainPoptipProps: {
                title: `${vm.$i18n.t("certsmanage.explainPoptipTitle")}`,
                tableData: [
                    {
                        object: vm.$i18n.t("certsmanage.nonWildcardDomain"),
                        explain: vm
                            .$t("certsmanage.explainPoptipContentForNonWildcard")
                            .map((item, index) => `${index + 1}.${item}`)
                            .join(", "),
                    },
                    {
                        object: vm.$i18n.t("certsmanage.wildcardDomain"),
                        explain: vm.$t("certsmanage.explainPoptipContentForWildcard"),
                    },
                ],
            },
            render: (h, params) => {
                return h(
                    "Tag",
                    {
                        props: {
                            color: params.row.useSystemCert == true ? "success" : "default",
                        },
                    },
                    params.row.useSystemCert
                );
            },
            vShowFieldVal: () => 1,
            watchField: function (value, item) {
                if (!value) item.keypairname = "self-sign";

                // 跳出 modal 提示用戶：當下尚無系統憑證，套用自簽憑證將會導致網站被判定不安全
                if (value && !item.hasSystemCert) {
                    vm.$Modal.warning({
                        title: vm.$i18n.t("global.reminderText"),
                        content: `Due to the absence of a system certificate for your domain, a self-signed certificate will be applied. Please be aware that this action may lead to your website being identified as not secure.`,
                        okText: "OK",
                    });
                }
            },
        },
        //增加sectigo和zerossl的radio button
        {
            searchViews: ["DomainSetting", "ProfileDomain"],
            key: "sslProvider",
            title: vm.$i18n.t("domainsetting.sslProvider"),
            sectionKey: "http_https",
            description:
                "<div><p>Choose one ssl provider to suit your needs.</p><img src='/static/img/Sectigo_Logo_Web.png' width='95px'></div>",
            showInTable: false,
            withForm: vm.$store.state.platformPlan.multiTenancyConfigs.sslProvider.length > 0 ? true : false,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormRadioGroup",
            //options去抓index.js的全域變數
            options: vm.$store.state.platformPlan.multiTenancyConfigs.sslProvider.map((item) => {
                return {
                    label: item.name,
                    value: item.name,
                    price: item.price,
                    wildcardPrice: item.wildcardPrice,
                    isPriceShow: item.isPriceShown,
                };
            }),
            defaultVal: vm.$store.state.platformPlan.multiTenancyConfigs.sslDefaultProvider,
            filter: {
                type: "string",
            },
            //按下選項後跳出一個modal
            watchField: async (value, item) => {
                //把上面options map出來的結果存在變數result
                let result = vm.$store.state.platformPlan.multiTenancyConfigs.sslProvider.map((item) => {
                    return {
                        label: item.name,
                        value: item.name,
                        price: item.price,
                        wildcardPrice: item.wildcardPrice,
                        isPriceShow: item.isPriceShown,
                    };
                });

                //透過find找出result[i].value=validator(value...)，結果存在變數resultFind
                let resultFind = result.find((element) => element.value === value);

                //若resultFind.isPriceShow=true，就顯示modal
                if (resultFind.isPriceShow) {
                    let singleDomainCertPricingText = `${vm.$i18n.t("domainsetting.sslProviderPrice")}${
                        resultFind.price
                    }${vm.$i18n.t(`domainsetting.sslProviderPeriod${resultFind.value}`)}`;
                    let wildcardDomainCertPricingText = `${vm.$i18n.t("domainsetting.sslProviderWildcardPrice")}${
                        resultFind.wildcardPrice
                    }${vm.$i18n.t(`domainsetting.sslProviderPeriod${resultFind.value}`)}`;
                    let content =
                        vm.$store.state.platformPlan.multiTenancyConfigs.isAllowWildcardDomain &&
                        resultFind.label == "sectigo"
                            ? singleDomainCertPricingText + wildcardDomainCertPricingText
                            : singleDomainCertPricingText;
                    vm.$Modal.info({
                        title: vm.$i18n.t("global.reminderText"),
                        content: content + `${vm.$i18n.t("domainsetting.sslProviderContactSales")}`,
                        okText: "OK",
                    });
                }
            },
            vShowFieldVal: (item) => {
                if (item.appProfileName !== "none") {
                    return item.useSystemCert ? 1 : 0;
                } else {
                    return item.useSystemCert && item.scheme.find((e) => e == "https") ? 1 : 0;
                }
            },
        },
        {
            searchViews: ["DomainSetting", "ProfileDomain", "CertsManage"],
            key: "keypairname",
            title: vm.$i18n.t("domainsetting.keypairname"),
            description:
                "<p>Choose the cert you uploaded, you can manage your uploaded cert at <router-link to='certsmanage'> here</router-link>.</p>",
            showInTable: false,
            withForm: true,
            formTypes: () => ["createForm", "editForm", "multiEditForm"],
            vShowFieldVal: () => 1,
            filter: {
                type: "string",
                defaultValue: vm.$route.query.keypairname,
            },
            defaultVal: "self-sign",
            formRender: "FormSelect",
            remoteOptions: async (queryValue, queryParams) => {
                let { isAllowWildcardDomain } = vm.$store.state.platformPlan.multiTenancyConfigs;
                queryValue = queryValue || "*";
                queryParams.keypairname = `*${queryValue}*`;
                queryParams.servername = vm.isBatchEditFormShown
                    ? vm.$refs.BatchEditForm.batchSelectedData.selectedItems
                    : vm.formData.servername;

                // 判斷serverName是否合法
                const serverList = Array.isArray(queryParams.servername)
                    ? queryParams.servername
                    : queryParams.servername.split("\n");
                const invalidServername = serverList.some(server => {
                    return !isQualifiedServername(server, vm) && !isQualifiedWildcardServername(server, isAllowWildcardDomain)
                })
                if (invalidServername) {
                    return 
                }
                queryParams.withSelfSign = true;
                queryParams.withSystem = false;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/keypairnames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [
                { fieldName: "servername", queryIf: (val) => val !== "" },
                { fieldName: "groupName", queryIf: (val) => val !== "" },
            ],
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.keypairname")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["DomainSetting", "ProfileDomain"],
            key: "isAllowOCSP",
            title: vm.$i18n.t("domainsetting.isAllowOCSP"),
            sectionKey: "http_https",
            description:
                "<p>Toggle the switch off to disable OCSP Stapling, which prevent backlogging between CDN and certificate authority server. Toggle the switch on to supplement Certificate Revocation List check and get real-time certificate revocation status.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            type: "boolean",
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.isAllowOCSP ? 1 : 0;
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "usedWafRules",
            title: vm.$i18n.t("domainsetting.usedWafRules"),
            sectionKey: "wafrules",
            description: `<p>Choose the waf rules to use.</p>
                <ul>
                  <li>click <i class="ivu-icon ivu-icon-md-add-circle"></i> to apply a rule from available rules.</li>
                  <li>click <i class="ivu-icon ivu-icon-md-trash"></i> to remove a rule from applied rules.</li>
                  <li>click <i class="ivu-icon ivu-icon-md-eye"></i> to preview the content of the rule.</li>
                  <li>Drag a rule from applied rules to manage the order.</li>
                </ul>
                `,
            showInTable: true,
            withForm: true,
            formTypes: () => ["createForm", "editForm", "multiEditForm"],
            formRender: vm.$store.getters.getWafRuleComponent,
            remoteOptions: async (queryParams) => {
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/wafrules?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            options: [],
            appliedTitle: vm.$i18n.t("domainsetting.appliedWafRules"),
            availableTitle: vm.$i18n.t("domainsetting.availableWafRules"),
            formAppliedHeaders: [
                {
                    title: vm.$i18n.t("domainsetting.appliedWafRules"),
                    align: "center",
                    minWidth: 250,
                    key: "value",
                    render: (h, params) => {
                        let imgPath = wafRuleToIcon(params.row.description.action);

                        return h(
                            "div",
                            {
                                style: {
                                    display: "flex",
                                    "flex-direction": "row",
                                    "justify-content": "flex-start",
                                    "align-items": "center",
                                },
                            },
                            [
                                h("Avatar", {
                                    props: {
                                        shape: "square",
                                        size: "large",
                                        src: "../../static/icons/" + imgPath + ".png",
                                    },
                                    style: {
                                        "margin-right": "10px",
                                    },
                                }),
                                [h("p", params.row.value)],
                            ]
                        );
                    },
                },
                {
                    title: vm.$i18n.t("domainsetting.status"),
                    key: "isEnabled",
                    minWidth: 150,
                    align: "center",
                    render: (h, params) => {
                        var formData = vm.isBatchEditFormShown ? vm.$refs.BatchEditForm.batchFormData : vm.formData;
                        let rule = formData.usedWafRules.find((e) => e.value == params.row.value);
                        return h("i-switch", {
                            props: {
                                value: rule.isEnabled,
                            },
                            on: {
                                "on-change": (value) => {
                                    rule.isEnabled = value;
                                },
                            },
                        });
                    },
                },
                {
                    title: vm.$i18n.t("domainsetting.view"),
                    key: "view",
                    minWidth: 80,
                    align: "center",
                    render: (h, params) => {
                        return h("JsonPoptip", {
                            props: {
                                jsonData: params.row.description,
                                title: params.row.value,
                                buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                                buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                            },
                        });
                        // const viewBtn = h("Icon", {
                        //     props: {
                        //         type: "md-eye",
                        //         size: 24
                        //     },
                        //     style: {
                        //         marginRight: "5px"
                        //     }
                        // });
                        // const preTag = h("pre", {
                        //     slot: "content",
                        //     domProps: {
                        //         innerHTML: JSON.stringify(params.row.description, null, 2)
                        //     },
                        //     style: {
                        //         "text-align": "left"
                        //     }
                        // });
                        // const poptip = h(
                        //     "Poptip",
                        //     {
                        //         props: {
                        //             transfer: true
                        //         }
                        //     },
                        //     [viewBtn, preTag]
                        // );
                        // return poptip;
                    },
                },
                {
                    title: vm.$i18n.t("rcmtable.deleteText"),
                    key: "",
                    minWidth: 80,
                    align: "center",
                    render: (h, params) => {
                        const trashIcon = h(
                            "Icon",
                            {
                                props: { type: "md-trash", size: 24 },
                                on: {
                                    click: () => {
                                        var formData = vm.isBatchEditFormShown
                                            ? vm.$refs.BatchEditForm.batchFormData
                                            : vm.formData;

                                        var index = formData.usedWafRules.findIndex(
                                            (waf) => waf.value == params.row.value
                                        );
                                        // 刪除列
                                        // console.log(formData)
                                        formData.usedWafRules.splice(index, 1);

                                        vm.$emit("input", formData.usedWafRules);
                                    },
                                },
                            },
                            "Delete"
                        );
                        return trashIcon;
                    },
                },
            ],
            formOptionsHeaders: [
                {
                    title: "",
                    align: "center",
                    minWidth: 80,
                    key: "",
                    render: (h, params) => {
                        return h("Icon", {
                            props: {
                                type: "md-add",
                            },
                            on: {
                                click: (value) => {
                                    var formData = vm.isBatchEditFormShown
                                        ? vm.$refs.BatchEditForm.batchFormData
                                        : vm.formData;

                                    // 避免重複推送
                                    if (!formData.usedWafRules.map((rule) => rule.value).includes(params.row.value)) {
                                        // 新增套用的
                                        formData.usedWafRules.push(params.row);

                                        vm.$emit("input", formData.usedWafRules);
                                    }
                                },
                            },
                        });
                    },
                },
                {
                    title: vm.$i18n.t("domainsetting.availableWafRules"),
                    align: "center",
                    minWidth: 250,
                    key: "value",
                    render: (h, params) => {
                        let imgPath = wafRuleToIcon(params.row.description.action);

                        return h(
                            "div",
                            {
                                style: {
                                    display: "flex",
                                    "flex-direction": "row",
                                    "justify-content": "flex-start",
                                    "align-items": "center",
                                },
                            },
                            [
                                h("Avatar", {
                                    props: {
                                        shape: "square",
                                        size: "large",
                                        src: "../../static/icons/" + imgPath + ".png",
                                    },
                                    style: {
                                        "margin-right": "10px",
                                    },
                                }),
                                [h("p", params.row.value)],
                            ]
                        );
                    },
                },
                {
                    title: vm.$i18n.t("domainsetting.view"),
                    key: "view",
                    minWidth: 80,
                    align: "center",
                    render: (h, params) => {
                        return h("JsonPoptip", {
                            props: {
                                jsonData: params.row.description,
                                title: params.row.value,
                                buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                                buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                            },
                        });
                        // const viewBtn = h("Icon", {
                        //     props: {
                        //         type: "md-eye",
                        //         size: 24
                        //     },
                        //     style: {
                        //         marginRight: "5px"
                        //     }
                        // });
                        // const preTag = h("pre", {
                        //     slot: "content",
                        //     domProps: {
                        //         innerHTML: JSON.stringify(params.row.description, null, 2)
                        //     },
                        //     style: {
                        //         "text-align": "left"
                        //     }
                        // });
                        // const poptip = h(
                        //     "Poptip",
                        //     {
                        //         props: {
                        //             transfer: true
                        //         }
                        //     },
                        //     [viewBtn, preTag]
                        // );
                        // return poptip;
                    },
                },
            ],
            defaultVal: [],
            filter: {
                type: "string",
                searchKey: "usedWafRules.value",
            },
            watchField: function (item) {},
            validate: [],
            render: (h, params) => {
                return h(
                    "div",
                    params.row.usedWafRules
                        .filter((e) => e.isEnabled == true)
                        .map(function (e) {
                            return h(
                                "p",
                                {
                                    props: {},
                                },
                                `${e.value}`
                            );
                        })
                );
            },
            vShowFieldVal: () => 1,
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    value = assignDefaultValue(value, []);
                    return value.filter((rule) => rule.isEnabled == true).map((rule) => rule.value);
                },
            },
            sortable: false,
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "isUsedWafRules",
            title: vm.$i18n.t("domainsetting.isUsedWafRules"),
            showInTable: false,
            withForm: true,
            withTable: false,
            filter: {
                type: "boolean",
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "CacheRules", "ClearCache", "ProfileDomain"],
            key: "tags",
            title: vm.$i18n.t("domainsetting.tags"),
            sectionKey: "essential",
            description: "<p>Use tags to categorize.</p>",
            withTable: true,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.tags = `*${queryValue}*`;
                queryParams.indexName = vm.esIndex;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/groupby/tags?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            options: [],
            selectAllowCreate: true,
            multiSelectWithToolbox: false,
            defaultVal: [],
            filter: {
                type: "select",
                defaultValue: [],
            },
            validate: [
                {
                    type: "array",
                    max: 10,
                    message: "Max 10 tags",
                    trigger: "change",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        value.forEach((userInput) => {
                            if (/[.,\/#!$%\^&\*;:\\{}=~()]/.test(userInput))
                                return callback(new Error(userInput + " includes one of .,/#!$%^&*;:\\{}=`~()"));
                        });
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            vShowFieldVal: () => 1,
        },
        {
            // searchViews: ["DomainSetting", "AppProfile", "ProfileDomain", "WafRules", "ServiceGroup", "ProbeSetting"],
            searchViews: ["*"],
            key: "description",
            title: vm.$i18n.t("domainsetting.description"),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    type: "string",
                    max: 500,
                    message: "Max 500 characters.",
                    trigger: "blur",
                },
            ],
        },
        //回源配置
        {
            searchViews: ["DomainSetting", "AppProfile", "OriginPolicy", "ProfileDomain"],
            key: "originscheme",
            title: vm.$i18n.t("domainsetting.originscheme"),
            sectionKey: "origins",
            description:
                "<p>The scheme of your origin web server using. <br>Note: If the origin web server has setup 302 redirect to https, the originscheme must set to <strong>https</strong>.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSelect",
            options: ["http", "https", "static"],
            defaultVal: "http",
            filter: {
                type: "string",
            },
            watchField: function (value, item) {
                if (value == "http") {
                    item.originPort = 80;
                } else {
                    item.originPort = 443;
                }
            },
            validate: [
                {
                    required: true,
                    message: "回源協議 不能為空",
                    trigger: "blur",
                },
            ],
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "OriginPolicy", "ProfileDomain"],
            key: "portMappingType",
            title: vm.$i18n.t("domainsetting.portMappingType"),
            sectionKey: "origins",
            description: "<p>Static port mapping means origins have same port listened as frontend</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormRadioGroup",
            options: ["default", "static"],
            defaultVal: "default",
            filter: {
                type: "string",
            },
            validate: [
                {
                    required: true,
                    message: "portMappingType 不能為空",
                    trigger: "blur",
                },
            ],
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "OriginPolicy", "ProfileDomain"],
            key: "originPort",
            title: vm.$i18n.t("domainsetting.originPort"),
            sectionKey: "origins",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formWidth: "100px",
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 80,
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        let useablePorts = formOptions.originUseablePorts();
                        let errors = !useablePorts.includes(value) ? "超出可用範圍" : [];
                        callback(errors);
                    },
                    trigger: "change",
                },
            ],
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "useOriginPolicy",
            title: vm.$i18n.t("domainsetting.useOriginPolicy"),
            sectionKey: "origins",
            description: "<div>Use Origin Policy or not</div>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            render: (h, params) => {
                return h(
                    "Tag",
                    {
                        props: {
                            color: params.row.useOriginPolicy == true ? "success" : "default",
                        },
                    },
                    params.row.useOriginPolicy
                );
            },
            vShowFieldVal: () => 1,
            watchField: function (item) {},
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "usedOriginPolicies",
            title: vm.$i18n.t("domainsetting.usedOriginPolicies"),
            sectionKey: "origins",
            description:
                "<p>The applies origin policies. We <strong>Strongly</strong> recommand you put at least one default region.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: () => import("@/components/FormItems/OriginPolicySelector"),
            defaultVal: [],
            placeholder: "",
            validate: [
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "回源策略至少填寫一個",
                    trigger: "change",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
                searchKey: "usedOriginPolicies.originPolicyName",
            },
            minWidth: 250,
            vShowFieldVal: (item) => {
                return item.useOriginPolicy ? 1 : 0;
            },
            render: (h, params) => {
                var fontStyle = params.row.useOriginPolicy ? "normal" : "italic";
                var fontColor = params.row.useOriginPolicy ? "" : "rgb(204, 204, 204)";
                var usedOriginPolicies = assignDefaultValue(params.row.usedOriginPolicies, []);
                var { groupName } = params.row;
                var toPageName = "OriginPolicies";
                const usedOriginPolicy = usedOriginPolicies.map((originPolicy) =>
                    h(
                        "li",
                        {
                            style: {
                                "list-style": "none",
                                fontStyle: fontStyle,
                                color: fontColor,
                            },
                        },
                        [
                            h("span", {}, originPolicy.regionName.join(", ")),
                            h(
                                "a",
                                {
                                    style: {
                                        float: "right",
                                    },
                                    on: {
                                        click: () => {
                                            vm.$router.push({
                                                name: toPageName,
                                                query: { groupName, originPolicyName: originPolicy.originPolicyName },
                                            });
                                        },
                                    },
                                },
                                originPolicy.originPolicyName
                            ),
                        ]
                    )
                );
                return usedOriginPolicy;
            },
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    value = Array.isArray(value) ? value : [];
                    return value.map((originPolicy) => `${originPolicy.regionName}:${originPolicy.originPolicyName}`);
                },
            },
            sortable: false,
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "isEnabledCompressionToOrigin",
            title: "Upstream Compression",
            sectionKey: "advanced",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "isEnabledUpstreamHttp11",
            title: "Upstream HTTP/1.1",
            sectionKey: "advanced",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "isInsertRidHeaderToOrigin",
            title: "Insert RID",
            sectionKey: "advanced",
            description: "<p>Insert CDN request id within header, the key of header is <strong>rid</strong></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "OriginPolicy", "ProfileDomain"],
            key: "origins",
            title: vm.$i18n.t("domainsetting.origins"),
            sectionKey: "origins",
            description: "<p>The origin server behind the CDN, allow ip address or domain.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.origins = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/groupby/origins?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            selectAllowCreate: true,
            multiSelectWithToolbox: false,
            defaultVal: [],
            filter: {
                type: "string",
            },
            validate: [
                // { required: false, message: '回源 不能為空', trigger: 'blur' },
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "回源至少填寫一個",
                    trigger: "change",
                },
                {
                    required: true,
                    type: "array",
                    max: currentGroupResourceLimits.maxDomainOriginCount,
                    message: `回源最多 ${currentGroupResourceLimits.maxDomainOriginCount} 個`,
                    trigger: "change",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        ipValidate("origin", value).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            const groupName = vm.isBatchEditFormShown
                                // 批次異動抓取BatchEditForm
                                ? vm.$refs.BatchEditForm.batchSelectedData.selectedGroupName
                                // 單一異動抓取formData
                                : vm.formData.groupName;

                            const groupCdnRootDomains =
                                vm.getEsResult.cnameRootDomain[groupName] ||
                                vm.getEsResult.cnameRootDomain["all"] ||
                                [];

                            value.forEach((userInput) => {
                                const findCdnRootDomains = groupCdnRootDomains.find((rootDomain) =>
                                    userInput.endsWith(`.${rootDomain}`)
                                );
                                if (findCdnRootDomains) {
                                    return callback(new Error("CDN CNAME Not Allowed"));
                                }
                            });
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "a.b.c.d",
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["OriginPolicy"],
            key: "originsOptions",
            title: vm.$i18n.t("domainsetting.origins"),
            sectionKey: "essential",
            description: `<p>The origin server behind the CDN, weight will only effect when choosing <strong>LoadBalancer Methods</strong> to <strong>weight</strong>. Backuped origins will only be used when all origins unavailabled</p>`,
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: () => import("@/components/FormItems/AdvanceOriginsForm"),
            filter: {
                type: "string",
                searchKey: "originsOptions.host",
            },
            width: 300,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        style: {
                            display: "flex",
                            flexDirection: "column",
                        },
                    },
                    [
                        params.row.originsOptions.map(function (originData) {
                            let { host, weight, isBackupHost, isHostEnabled, isCustomSettingEnabled } = originData;
                            let oStat = vm.getEsResult.originStat.find(
                                (oStat) => oStat.originHost == host && oStat.groupName == params.row.groupName
                            );
                            let latency = oStat ? oStat.latency : 0;
                            let statusColor = latencyToColorName(latency);
                            let originText = `${host}, w: ${weight}`;

                            if(isBackupHost) {
                                originText = `${originText}, backup`;
                            }
                            if (isShowCustomSettingEnabledGroups.includes(params.row.groupName) && isCustomSettingEnabled) {
                                originText = `${originText}, custom`;
                            }

                            let fontStyle = !isHostEnabled ? "italic" : "";
                            let color = !isHostEnabled ? "rgb(204, 204, 204)" : "";
                            return [
                                h(
                                    "div",
                                    {
                                        style: {
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                        },
                                    },
                                    [
                                        h("Icon", {
                                            props: {
                                                type: "ios-radio-button-on",
                                                color: statusColor,
                                                size: 16,
                                            },
                                            style: {
                                                margin: "5px",
                                            },
                                        }),
                                        h(
                                            "p",
                                            {
                                                style: {
                                                    fontStyle,
                                                    color,
                                                },
                                            },
                                            originText
                                        ),
                                    ]
                                ),
                            ];
                        }),
                    ]
                );
            },
            validate: [
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "回源至少填寫一個",
                    trigger: "change",
                },
                {
                    required: true,
                    type: "array",
                    max: currentGroupResourceLimits.maxDomainOriginCount,
                    message: `回源最多 ${currentGroupResourceLimits.maxDomainOriginCount} 個`,
                    trigger: "change",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        let hasHostEnabled = value.some((e) => e.isHostEnabled);
                        if (!hasHostEnabled) return callback(new Error("At least enable 1 origin"));
                        let originsHosts = value.map((e) => e.host);
                        ipValidate("origin", originsHosts).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            const groupCdnRootDomains =
                                vm.getEsResult.cnameRootDomain[vm.formData.groupName] ||
                                vm.getEsResult.cnameRootDomain["all"];
                            originsHosts.forEach((userInput) => {
                                const findCdnRootDomains = groupCdnRootDomains.find((rootDomain) =>
                                    userInput.endsWith(`.${rootDomain}`)
                                );
                                if (findCdnRootDomains) {
                                    return callback(new Error("CDN CNAME Not Allowed"));
                                }
                            });
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            vShowFieldVal: () => 1,
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "cnameto",
            title: vm.$i18n.t("domainsetting.cnameto"),
            showInTable: false,
            withForm: false,
            minWidth: 300,
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "currentCname",
            title: vm.$i18n.t("domainsetting.currentCname"),
            showInTable: false,
            withForm: false,
            minWidth: 300,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "ProfileDomain"],
            key: "cnameInfo",
            title: vm.$i18n.t("domainsetting.cnameInfo"),
            showInTable: true,
            withForm: false,
            minWidth: 450,
            render: (h, params) => {
                const isRootDomain = params.row.servername.split(".").length == 2;
                const currentARecord = assignDefaultValue(params.row.currentARecord, []);
                const currentCname = assignDefaultValue(params.row.currentCname.toLowerCase(), "N/A");
                let serviceGroup =
                    vm.getEsResult.serviceGroups.find(
                        (serviceGroup) => serviceGroup.serviceGroupName == params.row.serviceGroupName
                    ) || {};
                let nodeServiceIps = serviceGroup.nodeServiceIps || [];
                // 判斷是否有A紀錄for rootDomain用
                const isARecordCorrect = h("Icon", {
                    props: {
                        type: boolToIcons({
                            value: nodeServiceIps.some((slbServiceIp) => currentARecord.includes(slbServiceIp)),
                            falseToDash: false,
                        }),
                        color: boolToColors({
                            value: nodeServiceIps.some((slbServiceIp) => currentARecord.includes(slbServiceIp)),
                            falseToGrey: false,
                        }),
                        size: 24,
                    },
                });
                // 判斷是否有CNAME
                const isCnameCorrect = h("Icon", {
                    props: {
                        type: boolToIcons({
                            value: params.row.cnameto.some((cnametoItem) => cnametoItem.toLowerCase() == currentCname),
                            falseToDash: false,
                        }),
                        color: boolToColors({
                            value: params.row.cnameto.some((cnametoItem) => cnametoItem.toLowerCase() == currentCname),
                            falseToGrey: false,
                        }),
                        size: 24,
                    },
                });

                let elements;

                // rootDomain的
                if (isRootDomain) {
                    // rootDomain 要判斷是否有A紀錄跟CNAME
                    const aRecordCorrect = h("p", ["A Record Correct: ", isARecordCorrect]);
                    elements = [aRecordCorrect];

                    // 已經有CNAME的domain
                    const findCname = params.row.cnameto.find(
                        (cnametoItem) => cnametoItem.toLowerCase() == currentCname
                    );
                    if (findCname) {
                        const current = h("p", `Current: ${currentCname}`);
                        elements.push(current);
                    }

                    const filterNotCname = params.row.cnameto.filter(
                        (cnametoItem) => cnametoItem.toLowerCase() != currentCname
                    );
                    if (filterNotCname.length > 0) {
                        elements.push(h("p", `Should CNAME :`));
                        filterNotCname.forEach((cnametoItem) => {
                            const shouldAndCopyText = h("CopyText", {
                                props: { text: cnametoItem, copyText: `- ${cnametoItem}` },
                            });
                            elements.push(shouldAndCopyText);
                        });
                    }
                } else {
                    // 不是rootDomain的不用去判斷有沒有A紀錄,只要判斷有沒有CNAME
                    const cnameCorrect = h("p", ["CNAME Correct: ", isCnameCorrect]);
                    elements = [cnameCorrect];
                    // cnameto由後台配置去設定,可能會有多個dns解析廠商
                    const findCname = params.row.cnameto.find(
                        (cnametoItem) => cnametoItem.toLowerCase() == currentCname
                    );
                    if (findCname) {
                        const current = h("p", `Current: ${currentCname}`);
                        elements.push(current);
                    }

                    const filterNotCname = params.row.cnameto.filter(
                        (cnametoItem) => cnametoItem.toLowerCase() != currentCname
                    );
                    if (filterNotCname.length > 0) {
                        elements.push(h("p", `Should CNAME :`));
                        filterNotCname.forEach((cnametoItem) => {
                            const shouldAndCopyText = h("CopyText", {
                                props: { text: cnametoItem, copyText: `- ${cnametoItem}` },
                            });
                            elements.push(shouldAndCopyText);
                        });
                    }
                }
                // 因為後台配置可能會配置多個dnsRootDomain,所以要判斷都有符合者,check的按鈕才會隱藏
                const checkCname = h("checkCname", {
                    props: { servername: params.row.servername },
                });
                if (!params.row.cnameto.every((cnametoItem) => cnametoItem == currentCname)) {
                    elements.push(checkCname);
                }
                return h("span", {}, elements);
            },
            sortable: false,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "cnameHint",
            title: vm.$i18n.t("domainsetting.cnameHint"),
            sectionKey: "essential",
            description: "<p>You will have to setup a <strong>CNAME</strong> record with the value.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "RawHTML",
            isFormSlot: true,
            withTable: false,
            defaultVal: `<p></p>`,
            disabled: false,
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "isAcceptHttpV1",
            title: vm.$i18n.t("domainsetting.isAcceptHttpV1"),
            sectionKey: "advanced",
            description:
                "Enable this to accept HTTP/1.0 requests, the default behavior is to reject HTTP/1.0 requests.",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "websocketPath",
            title: vm.$i18n.t("domainsetting.websocketPath"),
            sectionKey: "advanced",
            description:
                "<p>e.g. enter 'socket.io' in the input field, websocket will listen to 'www.example.com<strong>/socket.io/</strong>'.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            placeholder: "socket.io\nsignalr\nbar/mypath",
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "proxyBufferSize",
            title: vm.$i18n.t("domainsetting.proxyBufferSize"),
            sectionKey: "advanced",
            description: "<p>Response header size limit. default is 4KB</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSelect",
            options: [
                { label: "2KB", value: 2 },
                { label: "4KB", value: 4 },
                { label: "8KB", value: 8 },
                { label: "16KB", value: 16 },
                { label: "32KB", value: 32 },
                { label: "64KB", value: 64 },
            ],
            defaultVal: 4,
            filter: {
                type: "range",
            },
            watchField: function (item) {},
            validate: [],
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "customVariables",
            title: vm.$i18n.t("domainsetting.customVariables"),
            sectionKey: "advanced",
            description:
                "<p>A list of custom key-value pairs that are currently allowed to be sent from the EDGE to the origin server.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: () => import("@/components/FormItems/CustomVariables"),
            defaultVal: [],
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "proxySetHeader",
            title: vm.$i18n.t("domainsetting.proxySetHeader"),
            sectionKey: "advanced",
            description: "<p>Set headers when forwarding the request to the origin server.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 160,
            formRender: () => import("@/components/FormItems/ProxySetHeader"),
            defaultVal: [],
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.proxySetHeader,
                        maxShow: "5",
                        title: vm.$i18n.t("domainsetting.proxySetHeader"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        const customValue = value.filter((v) => !enumList.PROXY_SET_HEADER_SYSTEM_OPTIONS.includes(v));
                        const keysInCustomVariables = (vm.formData.customVariables || []).map((c) => c.key);
                        const hasInvalidCustomValue = customValue.some((v) => !keysInCustomVariables.includes(v));
                        if (hasInvalidCustomValue)
                            return callback(new Error(vm.$i18n.t("rcmtable.hasInvalidCustomVariables")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "isUpgradeInsecureRequest",
            title: vm.$i18n.t("domainsetting.isUpgradeInsecureRequest"),
            sectionKey: "advanced",
            description: "<p>Toggle Upgrade Insecure Request.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: () => 1,
            render: (h, params) => {
                var iconType = boolToIcons({
                    value: params.row.isUpgradeInsecureRequest,
                    falseToDash: true,
                });
                var iconColor = boolToColors({
                    value: params.row.isUpgradeInsecureRequest,
                    falseToGrey: true,
                });
                return h("Icon", {
                    props: {
                        type: iconType,
                        color: iconColor,
                        size: 24,
                    },
                });
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "maxUploadSize",
            title: vm.$i18n.t("domainsetting.maxUploadSize"),
            sectionKey: "advanced",
            description: "<p>Max upload size (MB)</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            inputMaxNum: 100,
            inputMinNum: 1,
            defaultVal: 1,
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (!value) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["DomainSetting", "AppProfile", "ProfileDomain"],
            key: "platformVersion",
            title: vm.$i18n.t("domainsetting.platformVersion"),
            showInTable: false,
            withForm: false,
            render: (h, params) => {
                return h("span", params.row.platformVersion || "-");
            },
            exportOptions: { canExport: false, callback: (value) => value },
            filter: {
                type: "string",
            }
        },
        {
            searchViews: ["DomainSetting", "ProfileDomain", "ServiceGroup"],
            key: "isEnforceProtect",
            title: vm.$i18n.t("servicegroup.isEnforceProtect"),
            description: "<p>Enforce to the protect mode.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["editForm"],
            minWidth: 140,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["*"],
            key: "cusNoncachepath",
            title: vm.$i18n.t("cacherules.cusNoncachepath"),
            sectionKey: "dontcache",
            description: "<p>The <strong>Path</strong> you <strong>don't</strong> want to be cached.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                var cusNoncachepath = assignDefaultValue(params.row.cusNoncachepath, "");
                return h(
                    "div",
                    cusNoncachepath.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ndontcache\nbar/mypath",
        },
        {
            searchViews: ["*"],
            key: "cusNonachefile",
            title: vm.$i18n.t("cacherules.cusNoncachefile"),
            sectionKey: "dontcache",
            description: "<p>The <strong>File</strong> you <strong>don't</strong> want to be cached.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h(
                    "div",
                    params.row.cusNonachefile.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ndontcache.html\nmyfile.js\n\\.css",
        },
        {
            searchViews: ["*"],
            key: "proxyCacheIgnoreArgs",
            title: vm.$i18n.t("cacherules.proxyCacheIgnoreArgs"),
            sectionKey: "cache",
            description: "<p></p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["*"],
            key: "cusCachepath",
            title: vm.$i18n.t("cacherules.cusCachepath"),
            sectionKey: "cache",
            description:
                "<p>e.g. enter 'foo' in the input field, data under the directory 'www.example.com<strong>/foo/</strong>' will be cached.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            autosize: inputAutoSize,
            fieldType: "textarea",
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h(
                    "div",
                    params.row.cusCachepath.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ncache\nbar/mypath",
        },
        {
            searchViews: ["*"],
            key: "cusCachefile",
            title: vm.$i18n.t("cacherules.cusCachefile"),
            sectionKey: "cache",
            description:
                "<p>e.g. enter 'foo' in the input field, file 'www.example.com<strong>/foo$</strong>' will be cached.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h(
                    "div",
                    params.row.cusCachefile.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ncache.html\nmyfile.js\n\\.css",
        },
        {
            searchViews: ["*"],
            key: "forceCusCachePath",
            title: vm.$i18n.t("cacherules.forceCusCachePath"),
            sectionKey: "cache",
            description:
                "<p>The <strong>Path</strong> you want to be cached, <strong>omit</strong> the headers such as <strong>Cache-Control</strong>, <strong>Set-Cookie</strong> and <strong>Expires</strong>. e.g. enter 'foo' in the input field, data under the directory 'www.example.com<strong>/foo/</strong>' will be cached.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 200,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            autosize: inputAutoSize,
            fieldType: "textarea",
            vShowFieldVal: () => 1,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h(
                    "div",
                    params.row.forceCusCachePath.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ncache\nbar/mypath",
        },
        {
            searchViews: ["*"],
            key: "forceCusCacheFile",
            title: vm.$i18n.t("cacherules.forceCusCacheFile"),
            sectionKey: "cache",
            description:
                "<p>The <strong>File</strong> you want to be cached, <strong>omit</strong> the headers such as <strong>Cache-Control</strong>, <strong>Set-Cookie</strong> and <strong>Expires</strong>. e.g. enter 'foo' in the input field, file 'www.example.com<strong>/foo$</strong>' will be cached.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 200,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            vShowFieldVal: () => 1,
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h(
                    "div",
                    params.row.forceCusCacheFile.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        // if (valueArray.length > 10) return callback(new Error("最多10筆"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ncache.html\nmyfile.js\n\\.css",
        },
        {
            searchViews: ["*"],
            key: "cusFollowCachePath",
            title: vm.$i18n.t("cacherules.cusFollowCachePath"),
            sectionKey: "cache",
            description:
                "<p>The <strong>Path</strong> to disable adding or modifying the <strong>Expires</strong> and <strong>Cache-Control</strong> response header .</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                var cusFollowCachePath = assignDefaultValue(params.row.cusFollowCachePath, "");

                return h(
                    "div",
                    cusFollowCachePath.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ndontcache\nbar/mypath",
        },
        {
            searchViews: ["*"],
            key: "cusFollowCacheFile",
            title: vm.$i18n.t("cacherules.cusFollowCacheFile"),
            sectionKey: "cache",
            description:
                "<p>The <strong>File</strong> to disable adding or modifying the <strong>Expires</strong> and <strong>Cache-Control</strong> response header .</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            fieldType: "textarea",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                var cusFollowCacheFile = assignDefaultValue(params.row.cusFollowCacheFile, "");

                return h(
                    "div",
                    cusFollowCacheFile.split("\n").map(function (o) {
                        return h(
                            "P",
                            {
                                props: {},
                            },
                            o
                        );
                    })
                );
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        if (valueArray.includes("")) return callback(new Error("含有空行"));
                        if (new Blob([value]).size > 3000) return callback(new Error("最多 3000 byte"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "foo\ndontcache.html\nmyfile.js\n\\.css",
        },
        {
            searchViews: ["*"],
            key: "cusCachepathExp",
            title: vm.$i18n.t("cacherules.cusCachepathExp"),
            sectionKey: "cache",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 5,
            needTransferUnit: true,
            vShowFieldVal: () => 1,
            filter: {
                type: "range",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == 0) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["*"],
            key: "cusCachepathSrvexp",
            title: vm.$i18n.t("cacherules.cusCachepathSrvexp"),
            sectionKey: "cache",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 5,
            needTransferUnit: true,
            vShowFieldVal: () => 1,
            filter: {
                type: "range",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == 0) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["*"],
            key: "cusCachefileExp",
            title: vm.$i18n.t("cacherules.cusCachefileExp"),
            sectionKey: "cache",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 5,
            needTransferUnit: true,
            vShowFieldVal: () => 1,
            filter: {
                type: "range",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == 0) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["*"],
            key: "cusCachefileSrvexp",
            title: vm.$i18n.t("cacherules.cusCachefileSrvexp"),
            sectionKey: "cache",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 5,
            needTransferUnit: true,
            vShowFieldVal: () => 1,
            filter: {
                type: "range",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == 0) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["*"],
            key: "defCachefileExp",
            title: vm.$i18n.t("cacherules.defCachefileExp"),
            sectionKey: "cache",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 5,
            needTransferUnit: true,
            vShowFieldVal: () => 1,
            filter: {
                type: "range",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == 0) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["*"],
            key: "defCachefileSrvexp",
            title: vm.$i18n.t("cacherules.defCachefileSrvexp"),
            sectionKey: "cache",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormInputNumber",
            defaultVal: 5,
            needTransferUnit: true,
            vShowFieldVal: () => 1,
            filter: {
                type: "range",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == 0) return callback(new Error(vm.$i18n.t("rcmtable.validNumberError")));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["*"],
            key: "defCachefile",
            title: vm.$i18n.t("cacherules.defCachefile"),
            sectionKey: "cache",
            showInTable: false,
            withForm: false,
            minWidth: 100,
            tooltip: true,
            formRender: "FormInput",
            defaultVal:
                "bmp|class|css|csv|doc|docx|ejs|eot|eps|gif|ico|jar|jpeg|jpg|js|mid|midi|otf|pdf|pict|pls|png|ppt|pptx|ps|svg|svgz|swf|tif|tiff|ttf|webp|woff|woff2|xls|xlsx",
            autosize: inputAutoSize,
            fieldType: "textarea",
            vShowFieldVal: () => 1,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["*"],
            key: "defNoncachepath",
            title: vm.$i18n.t("cacherules.defNoncachepath"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "/",
            autosize: inputAutoSize,
            fieldType: "textarea",
            vShowFieldVal: () => 1,
            filter: {
                type: "string",
            },
        },
        /* 緩存結束 */

        /* WAF開始 */
        {
            searchViews: ["*"],
            key: "action",
            fixed: "left",
            minWidth: 140,
            title: vm.$i18n.t("usersmanage.action"),
            showInTable: true,
            withForm: false,
            exportOptions: { canExport: false, callback: (value) => value },
            render: (h, params) => {
                const actionDivArray = [];
                const editBtn = h("Button", {
                    props: {
                        shape: "circle",
                        icon: vm.isEditBtnDisabled ? "md-eye" : "md-create",
                    },
                    on: {
                        click: () => {
                            vm.editItem(params.row);
                        },
                    },
                });
                const copyBtn = h("Button", {
                    props: {
                        shape: "circle",
                        icon: "md-copy",
                        disabled: vm.isEditBtnDisabled,
                    },
                    on: {
                        click: () => {
                            vm.copyItem(params.row);
                        },
                    },
                });
                var dropdownItems = [
                    {
                        value: "delete",
                        title: vm.$t("rcmtable.deleteText"),
                        modalTitle: vm.$t("rcmtable.deleteText"),
                        modalContent: vm.$t("rcmtable.confirmDeleteText"),
                        disabled: vm.isDeleteBtnDisabled,
                    },
                ];

                const moreBtn = h(
                    "Dropdown",
                    {
                        props: {
                            trigger: "click",
                            placement: "right",
                            transfer: true,
                        },
                        on: {
                            "on-click": (value) => {
                                var currentItem = dropdownItems.find((e) => e.value == value);
                                vm.$Modal.confirm({
                                    title: currentItem.modalTitle,
                                    content: currentItem.modalContent,
                                    okText: vm.$i18n.t("rcmtable.confirmText"),
                                    cancelText: vm.$i18n.t("rcmtable.cancelText"),
                                    onOk: () => {
                                        var deleteItem = Object.assign({}, params.row);
                                        delete deleteItem._index;
                                        delete deleteItem._rowKey;

                                        vm.actionType = "delete";
                                        vm.updateData(vm.esIndex, "delete", deleteItem);
                                    },
                                });
                            },
                        },
                    },
                    [
                        h("a", [
                            h("Button", {
                                props: {
                                    shape: "circle",
                                    icon: "md-more",
                                    key: params.index,
                                },
                            }),
                        ]),
                        h(
                            "DropdownMenu",
                            {
                                slot: "list",
                            },
                            [
                                dropdownItems.map(function (item) {
                                    return h(
                                        "DropdownItem",
                                        {
                                            props: {
                                                name: item.value,
                                                disabled: item.disabled,
                                            },
                                        },
                                        item.title
                                    );
                                }),
                            ]
                        ),
                    ]
                );

                // const deleteBtn = h("Button", {
                //     props: {
                //         shape: "circle",
                //         icon: "md-trash",
                //         disabled: vm.isDeleteBtnDisabled,
                //         type: "dashed"
                //     },
                //     style: {
                //         color: "#e1e1e1"
                //     },
                //     on: {
                //         click: () => {
                //             var deleteItem = Object.assign({}, params.row);
                //             delete deleteItem._index;
                //             delete deleteItem._rowKey;

                //             vm.$Modal.confirm({
                //                 title: vm.$t("rcmtable.deleteText"),
                //                 content: vm.$t("rcmtable.confirmRemoveText"),
                //                 okText: vm.$i18n.t("rcmtable.confirmText"),
                //                 cancelText: vm.$i18n.t("rcmtable.cancelText"),
                //                 onOk: () => {
                //                     vm.actionType = "delete";
                //                     vm.updateData(vm.esIndex, "delete", deleteItem);
                //                 }
                //             });
                //         }
                //     }
                // });
                if (vm.userCanIndex) {
                    actionDivArray.push(editBtn);
                }
                actionDivArray.push(copyBtn);
                actionDivArray.push(moreBtn);

                return h("div", actionDivArray);
            },
            sortable: false,
        },
        {
            searchViews: ["*"],
            key: "objectLog",
            minWidth: 200,
            title: vm.$i18n.t("global.showLog"),
            showInTable: true,
            withForm: false,
            exportOptions: { canExport: false, callback: (value) => value },
            render: (h, params) => {
                return h(
                    "ObjectLogBtn",
                    {
                        props: { objId: params.row._id, title: "Logs", groupName: params.row.groupName },
                    },
                    ""
                );
            },
            vShowFieldVal: () => 1,
            sortable: false,
        },
        {
            searchViews: ["*"],
            key: "_id",
            minWidth: 200,
            title: vm.$i18n.t("global._id"),
            showInTable: false,
            withForm: false,
            exportOptions: { canExport: true, callback: (value) => value },
            render: (h, params) => {
                return h("p", params.row._id);
            },
            vShowFieldVal: () => 1,
            sortable: false,
        },
        {
            searchViews: ["EdgeSetting", "SlbSetting", "NinjaSetting"],
            key: "nodeConnect",
            title: "connect",
            showInTable: false,
            withForm: false,
            minWidth: 120,
            render: (h, params) => {
                return h(
                    "NodeConnectBtn",
                    {
                        props: {},
                    },
                    ""
                );
            },
            vShowFieldVal: () => 1,
            sortable: false,
        },
        {
            // searchViews: ["WafRules", "AlertRules"],
            searchViews: ["SSHKeysManage"],
            key: "sshKeyName",
            title: "KeyName",
            sectionKey: "essential",
            description: "<p>The <strong>Name</strong> of your key.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
                defaultValue: vm.$route.query.sshKeyName,
            },
        },
        {
            searchViews: ["SSHKeysManage"],
            key: "sshKeyRaw",
            title: "SSH PRIVATE KEY",
            sectionKey: "essential",
            description: "<p>You are <strong>NOT</strong> able to view the content after you upload.</p>",
            showInTable: false,
            withForm: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            autosize: { minRows: 8, maxRows: 12 },
            // formRender: "CodeEditor",
            defaultVal: "",
        },
        {
            // searchViews: ["WafRules", "AlertRules"],
            searchViews: ["*"],
            key: "ruleName",
            title: vm.$i18n.t("wafrules.ruleName"),
            sectionKey: "essential",
            description: "<p>The <strong>Name</strong> of your rule.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
                defaultValue: vm.$route.query.ruleName,
            },
            render: (h, params) => {
                // backendSetting 裡 bypass 的 waf action
                let bypassWafActions = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.action
                    .filter((action) => {
                        return action.isEnable == false;
                    })
                    .map((action) => {
                        return { value: action.value, label: action.label };
                    });
                // backendSetting 裡 bypass 的 waf detect
                let bypassWafDetectKeys = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    .filter((detect) => {
                        return detect.isEnable == false;
                    })
                    .flatMap((detect) => {
                        return detect.rmFornt;
                    });
                // 如果 bypass 的 waf action == params.row.wafAction，將 action push 到 explainAction 中
                let explainAction = "";
                let checkWafAction = bypassWafActions
                    .map((action) => {
                        return action.value;
                    })
                    .includes(params.row.wafAction);
                if (checkWafAction) {
                    explainAction = `Action: ${wafActionToString(params.row.wafAction)} `;
                }
                // 如果 bypass 的 waf detect 有值，將 detectWithValue 轉為 true，並將 detect push 到 explainDetectArray 中
                let detectWithValue = false;
                let explainDetectArray = [];
                bypassWafDetectKeys.forEach((item) => {
                    if (params.row[item] && params.row[item] !== "null" && params.row[item] !== undefined) {
                        switch (typeof params.row[item]) {
                            case "object":
                                if (params.row[item].length > 0) {
                                    detectWithValue = true;
                                    explainDetectArray.push(item);
                                }
                                break;
                            case "string":
                                if (params.row[item]) {
                                    detectWithValue = true;
                                    explainDetectArray.push(item);
                                }
                                break;
                            default:
                        }
                    }
                });
                let explainDetect = explainDetectArray.length > 0 ? `Detect: ${explainDetectArray.join(", ")} ` : "";
                let checkWafDetect = detectWithValue;

                let addAmpersand = explainAction && explainDetect ? "& " : "";
                // 警告的 icon
                let extendBtns = [];
                const byPassWafRuleBtn = h("div", [
                    h(
                        "Tooltip",
                        {
                            props: {
                                placement: "top",
                                transfer: true,
                                content: `${vm.$i18n.t(
                                    "wafrules.explainWafIsNotFunctionAnymore"
                                )}${explainAction}${addAmpersand}${explainDetect}`,
                                maxWidth: "200px",
                            },
                        },
                        [
                            h("Icon", {
                                props: {
                                    type: "md-warning",
                                    size: 24,
                                    color: colorNames.orange,
                                },
                            }),
                        ]
                    ),
                ]);
                // 判斷是否增加警告的icon
                if (checkWafAction || checkWafDetect) {
                    extendBtns.push(byPassWafRuleBtn);
                }

                return h("div", [
                    h(
                        "div",
                        {
                            attrs: {},
                            style: {
                                width: "120px",
                            },
                        },
                        params.row.ruleName
                    ),
                    extendBtns,
                ]);
            },
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafAction",
            title: vm.$i18n.t("wafrules.wafAction"),
            sectionKey: "essential",
            description: "<p>The action to perform when meet the conditions.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            explainPoptip: "ExplainPoptip",
            explainPoptipProps: {
                title: `WAF ${vm.$t("global.object")} ${vm.$t("global.explanation")}`,
                tableData: [
                    {
                        object: "Allow",
                        explain: vm.$t("wafrules.explainObject.allow"),
                    },
                    {
                        object: "Deny",
                        explain: vm.$t("wafrules.explainObject.deny"),
                    },
                    {
                        object: "Speed Limit",
                        explain: vm.$t("wafrules.explainObject.speed_limit"),
                    },
                    {
                        object: "Redirect",
                        explain: vm.$t("wafrules.explainObject.redirect"),
                    },
                    {
                        object: "Cookie / JS Challenge",
                        explain: vm.$t("wafrules.explainObject.cookie_JS_challenge"),
                    },
                    {
                        object: "Gesture Challenge",
                        explain: vm.$t("wafrules.explainObject.gesture_challenge"),
                    },
                    {
                        object: "Log Only",
                        explain: vm.$t("wafrules.explainObject.log_only"),
                    },
                    {
                        object: "Block Period",
                        explain: vm.$t("wafrules.explainObject.block_period"),
                    },
                    {
                        object: "Set Request Headers",
                        explain: vm.$t("wafrules.explainObject.set_request_headers"),
                    },
                    {
                        object: "Set Response Headers",
                        explain: vm.$t("wafrules.explainObject.set_response_headers"),
                    },
                    {
                        object: "Custom Page",
                        explain: vm.$t("wafrules.explainObject.custom_page"),
                    },
                    {
                        object: "Origin Policy",
                        explain: vm.$t("wafrules.explainObject.origin_policy"),
                    },
                    {
                        object: "Smart Captcha",
                        explain: vm.$t("wafrules.explainObject.smart_captcha"),
                    },
                    {
                        object: "Scoll",
                        explain: "Scroll",
                    },
                    {
                        object: "Hotlink",
                        explain: vm.$t("wafrules.explainObject.hotlink"),
                    },
                ],
            },
            remoteOptions: async (queryValue, queryParams) => {
                // queryValue = queryValue || "*";
                // queryParams.actionName = `*${queryValue}*`;
                // let queryString = new URLSearchParams(queryParams).toString();
                // let result = await getFormOptions(`/wafactions?${queryString}`);
                // return result.data.options;
                await getPlatformPlan({
                    groupName: queryParams.groupName,
                })
                    .then((res) => {
                        vm.$store.commit("SET_PLATFORM_PLAN", res.data);
                    })
                    .catch((err) => {
                        console.log(err);
                    });
                let objData = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.action;
                objData = objData
                    .map((e) => {
                        e.disabled = !e.isEnable;
                        return e;
                    })
                    .filter((e) => e.isShow);
                return objData;
            },
            defaultVal: "",
            filter: {
                type: "select",
            },
            validate: [
                {
                    required: true,
                    message: "Missing Action",
                    trigger: "blur",
                },
            ],
            watchField: async (value, item) => {
                if (value !== "08") {
                    item.wafBucketTime = 300;
                }
                if (value !== "05") {
                    item.wafSpeedLimit = 10240;
                }
                if (value !== "06") {
                    item.wafRedirectUrl = "";
                }
                if (value !== "09") {
                    item.wafSetReqHeaders = "";
                }
                if (value !== "10") {
                    item.wafSetResHeaders = "";
                }
                // gesture chanllenge
                let text = vm
                    .$t("wafrules.gestureChallengeReminderText")
                    .replace("pageLink", `<a href='/group-api-keys'>${vm.$t("routesMenu.ApiKeys")}</a>`);
                if (vm.$store.state.userData.privilege != "admin") {
                    text = vm
                        .$t("wafrules.nuserChallengeReminderText")
                        .replace("pageLink", `<a href='/group-api-keys'>${vm.$t("routesMenu.ApiKeys")}</a>`);
                }
                const content = `<p>${text}</p>`;
                if (value == "03") {
                    let checkVaptchaKey = await getGroupApiKeys({
                        groupName: vm.formData.groupName,
                    });
                    if (checkVaptchaKey.data.groupVaptcha.key == "") {
                        vm.$Modal.info({
                            title: vm.$i18n.t("global.reminderText"),
                            content: content,
                            okText: "OK",
                        });
                    }
                }
            },
            render: (h, params) => {
                // let svg = params.row.wafAction == 0 ? "check.svg" : "cross.svg";
                let explain = wafActionToString(params.row.wafAction);
                let wafValue = wafCodeToValue(params.row.wafAction);

                const clearWafBtn = h("ClearWafBtn", {
                    props: {
                        shape: "circle",
                        bucketData: {
                            groupName: params.row.groupName,
                            section: "bucket",
                            servername: "",
                            wafrule: params.row.ruleName,
                            ip: "",
                        },
                    },
                });
                let extendBtns = [];
                if (["bucket"].includes(wafValue) || (["deny"].includes(wafValue) && params.row.wafIsDenyThenBucket)) {
                    extendBtns.push(clearWafBtn);
                }
                return h("div", [
                    h(
                        "Tooltip",
                        {
                            props: {
                                placement: "top",
                                transfer: true,
                                content: params.row.wafAction,
                            },
                        },
                        [
                            h(
                                "div",
                                {
                                    attrs: {},
                                    style: {
                                        width: "120px",
                                        height: "24px",
                                    },
                                },
                                explain
                            ),
                        ]
                    ),
                    extendBtns,
                ]);
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafDenyHtmlPageName",
            title: vm.$i18n.t("wafrules.wafDenyHtmlPageName"),
            sectionKey: "essential",
            description: "<p>The Html Page you want to present</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            defaultVal: "default",
            vShowFieldVal: (item) =>
                item.wafAction == "01" || item.wafAction == "08" || item.wafAction == "16" ? 1 : 0,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.htmlPageName = `*${queryValue}*`;
                queryParams.withDefault = true;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`htmlpagenames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [
                { fieldName: "wafAction", queryIf: (val) => ["01", "08", "16"].includes(val) },
                { fieldName: "groupName", queryIf: (val) => val !== "" },
            ],
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafCustomHtmlPageName",
            title: vm.$i18n.t("wafrules.wafCustomHtmlPageName"),
            sectionKey: "essential",
            description: "<p>The Html Page you want to present</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            defaultVal: "",
            vShowFieldVal: (item) => (item.wafAction == "12" ? 1 : 0),
            validate: [
                {
                    required: true,
                    message: "Missing Page Name",
                    trigger: "blur",
                },
            ],
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.htmlPageName = `*${queryValue}*`;
                queryParams.withDefault = false;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`htmlpagenames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [
                { fieldName: "wafAction", queryIf: (val) => val == "12" },
                { fieldName: "groupName", queryIf: (val) => val !== "" },
            ],
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafOriginPolicyName",
            title: vm.$i18n.t("wafrules.wafOriginPolicyName"),
            sectionKey: "essential",
            description: "<p>The Origin Policy to apply.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            defaultVal: "",
            vShowFieldVal: (item) => (item.wafAction == "13" ? 1 : 0),
            validate: [
                {
                    required: true,
                    message: "Missing Origin Policy Name",
                    trigger: "blur",
                },
            ],
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.originPolicyName = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/originpolicynames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [
                { fieldName: "wafAction", queryIf: (val) => val == "13" },
                { fieldName: "groupName", queryIf: (val) => val !== "" },
            ],
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafBucketTime",
            title: vm.$i18n.t("wafrules.wafBucketTime"),
            sectionKey: "essential",
            description: "<p>The period of time to block. (seconds)</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            inputMaxNum: 43200,
            inputMinNum: 5,
            defaultVal: 300,
            vShowFieldVal: (item) => {
                if (item.wafAction == "08") {
                    return 1;
                }
                if (item.wafAction == "01" && item.wafIsDenyThenBucket == true) {
                    return 1;
                }
                return 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafCustomScrollName",
            title: vm.$i18n.t("wafrules.wafCustomScrollName"),
            sectionKey: "essential",
            description: "<p>The Scroll Page you want to present</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            defaultVal: "",
            vShowFieldVal: (item) => (item.wafAction == "15" ? 1 : 0),
            validate: [
                {
                    required: true,
                    message: "Missing Scroll Name",
                    trigger: "blur",
                },
            ],
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.scrollName = `*${queryValue}*`;
                queryParams.withDefault = false;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`scrollnames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [
                // { fieldName: "wafAction", queryIf: (val) => val == "15" },
                { fieldName: "groupName", queryIf: (val) => val !== "" },
            ],
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafIsGlobalBucketRecord",
            title: vm.$i18n.t("wafrules.wafIsGlobalBucketRecord"),
            sectionKey: "essential",
            description:
                "<p>During the block period, all domains within the same group as the domain which applied this WAF rule will block the requester's IP address from further access.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: (item) => {
                if (item.wafAction == "08") {
                    return 1;
                }
                if (item.wafAction == "01" && item.wafIsDenyThenBucket == true) {
                    return 1;
                }
                return 0;
            },
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafSpeedLimit",
            title: vm.$i18n.t("wafrules.wafSpeedLimit"),
            sectionKey: "essential",
            description: "<p>The speed to limit. (KB/s)</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            defaultVal: 10240,
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        let errors = vm.formData.wafAction == "05" && value < 1 ? "Speed must greater than 0" : [];
                        callback(errors);
                    },
                    trigger: "change",
                },
            ],
            vShowFieldVal: (item) => (item.wafAction == "05" ? 1 : 0),
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafRedirectUrl",
            title: vm.$i18n.t("wafrules.wafRedirectUrl"),
            sectionKey: "essential",
            description: "<p>The url to  redirect to. (http://example.com)</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        let errors =
                            vm.formData.wafAction == "06" &&
                            !(value.indexOf("http://") == 0 || value.indexOf("https://") == 0)
                                ? "Missing redirect url"
                                : [];
                        callback(errors);
                    },
                    trigger: "change",
                },
                {
                    validator: async (rule, value, callback, source, options) => {
                        if (/\\+/.test(value)) return callback(new Error(`The use of backslashes (\\) is prohibited.`));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            vShowFieldVal: (item) => (item.wafAction == "06" ? 1 : 0),
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafRedirectHttpCode",
            title: vm.$i18n.t("domainsetting.redirectHttpCode"),
            sectionKey: "essential",
            description: "<p>The http code for redirecting.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            width: 80,
            formRender: "FormSelect",
            multiple: false,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.codeName = `*${queryValue}*`;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/redirect-http-code?${queryString}`);
                return result.data.options;
            },
            // queryParamsFields: [{ fieldName: "groupName", queryIf: val => val !== "" }],
            options: [],
            defaultVal: "302",
            filter: {
                type: "string",
            },
            vShowFieldVal: (item) => (item.wafAction == "06" ? 1 : 0),
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafSetReqHeaders",
            title: vm.$i18n.t("wafrules.wafSetReqHeaders"),
            sectionKey: "essential",
            description:
                "<p>Set request header from CDN edges to origin.Use <b>$rip</b> to obtain client ip. ex. my-ip-header:$rip</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            vShowFieldVal: (item) => (item.wafAction == "09" ? 1 : 0),
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafSetReqHeaders.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafSetReqHeaders"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.wafAction == "09" && value.length == 0)
                            return callback(new Error("Missing request headers"));
                        let prohibitedKeys = [
                            "x-forwarded-for",
                            "x-forwarded-proto",
                            "true-client-ip",
                            "rid",
                            "c-type",
                            "host",
                            "connection",
                            "if-modified-since",
                            "if-unmodified-since",
                            "if-match",
                            "if-none-match",
                            "user-agent",
                            "referer",
                            "content-length",
                            "content-range",
                            "content-type",
                            "range",
                            "if-range",
                            "transfer-encoding",
                            "te",
                            "expect",
                            "upgrade",
                            "accept-encoding",
                            "via",
                            "authorization",
                            "keep-alive",
                            "x-forwarded-for",
                            "x-real-ip",
                            "accept",
                            "accept-language",
                            "depth",
                            "destination",
                            "overwrite",
                            "date",
                            "cookie",
                            // "cdn-loop"
                        ];

                        let valueArray = value.split("\n");
                        valueArray.forEach((e) => {
                            let headerKey = e.split(":")[0];
                            let headerVal = e.split(":")[1];

                            if (headerKey == undefined || headerVal == undefined)
                                return callback(new Error("invalid: " + e));

                            if (headerKey.length == 0 || headerVal.length == 0)
                                return callback(new Error("invalid: " + e));

                            if (prohibitedKeys.includes(headerKey.toLowerCase()))
                                return callback(new Error("invalid key: " + headerKey));

                            if (/[\"]/.test(e)) return callback(new Error("invalid: " + e));
                        });

                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "my-ip-header:$rip",
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafSetResHeaders",
            title: vm.$i18n.t("wafrules.wafSetResHeaders"),
            sectionKey: "essential",
            description:
                "<p>Set response header from CDN to client, useful in <b>CORS</b> required scenario. ex. access-control-allow-origin:mydomain.com</p>",
            explainPoptip: "ExplainPoptip",
            explainPoptipProps: {
                title: `${vm.$i18n.t("wafrules.wafSetResHeaders")} ${vm.$t("global.object")} ${vm.$t(
                    "global.explanation"
                )}`,
                tableData: [
                    {
                        object: vm.$t("wafrules.explainObject.corsHeaders"),
                        explain: vm.$t("wafrules.explainObject.corsHeadersExplain"),
                    },
                    {
                        object: vm.$t("wafrules.explainObject.nonCorsHeaders"),
                        explain: vm.$t("wafrules.explainObject.nonCorsHeadersExplain"),
                    },
                ],
            },    
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            vShowFieldVal: (item) => (item.wafAction == "10" ? 1 : 0),
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafSetResHeaders.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafSetResHeaders"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.wafAction == "10" && value.length == 0)
                            return callback(new Error("Missing response headers"));
                        let prohibitedKeys = [
                            "status",
                            "content-type",
                            "content-length",
                            "date",
                            "last-modified",
                            "etag",
                            "server",
                            "www-authenticate",
                            "location",
                            "refresh",
                            "set-cookie",
                            "content-disposition",
                            "cache-control",
                            "expires",
                            "accept-ranges",
                            "content-range",
                            "connection",
                            "keep-alive",
                            "vary",
                            "link",
                            "x-accel-expires",
                            "x-accel-redirect",
                            "x-accel-limit-rate",
                            "x-accel-buffering",
                            "x-accel-charset",
                            "transfer-encoding",
                            "content-encoding",
                        ];

                        let valueArray = value.split("\n");
                        valueArray.forEach((e) => {
                            let headerKey = e.split(":")[0];
                            let headerVal = e.split(":")[1];

                            if (headerKey == undefined || headerVal == undefined)
                                return callback(new Error("invalid: " + e));

                            if (headerKey.length == 0 || headerVal.length == 0)
                                return callback(new Error("invalid: " + e));

                            if (prohibitedKeys.includes(headerKey.toLowerCase()))
                                return callback(new Error("invalid key: " + headerKey));

                            if (/[\"]/.test(e)) return callback(new Error("invalid: " + e));
                        });

                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "access-control-allow-origin:mydomain.com",
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafHotlinkSecretKey",
            title: vm.$i18n.t("wafrules.wafHotlinkSecretKey"),
            sectionKey: "essential",
            description: "<p>The cryptographic key used to generate and validate signed URL.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "",
            placeholder: "",
            filter: {
                type: "string",
            },
            validate: [{ required: true, message: "不能為空", trigger: "blur" }],
            vShowFieldVal: (item) => (item.wafAction == "16" ? 1 : 0),
            explainPoptip: "ExplainHotlink",
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafHotlinkTimeToLive",
            title: vm.$i18n.t("wafrules.wafHotlinkTimeToLive"),
            sectionKey: "essential",
            description:
                "<p>The duration in seconds for which the signed URL remains valid. If the hotlink's time to live expires, you'll need to generate a new signed URL.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            inputMinNum: 60,
            inputMaxNum: 60 * 60 * 24,
            defaultVal: 60,
            filter: {
                type: "range",
            },
            vShowFieldVal: (item) => (item.wafAction == "16" ? 1 : 0),
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafHotlinkQueryArgument",
            title: vm.$i18n.t("wafrules.wafHotlinkQueryArgument"),
            sectionKey: "essential",
            description:
                "<p>The query parameter located in the query string of the signed URL. e.g. 'www.example.com?<strong>key</strong>=hotlinkSignedURL'</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "",
            placeholder: "",
            filter: {
                type: "string",
            },
            validate: [{ required: true, message: "不能為空", trigger: "blur" }],
            vShowFieldVal: (item) => (item.wafAction == "16" ? 1 : 0),
        },
        {
            searchViews: ["WafRules"],
            key: "wafIp",
            title: vm.$i18n.t("wafrules.wafIp"),
            sectionKey: "conditionIdentity",
            description: "<p>The IPs to match the rule, you can setup multiple IPs with <strong>newline</strong></p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafIp.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafIp"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let networks = value.split("\n");
                        networks.forEach((e, i) => {
                            let index = networks.findIndex((x) => x == e);
                            if (index !== i) return callback(new Error(`Duplicate: ${e}`));
                            if (/[\s]/.test(e)) return callback(new Error("invalid: " + e));
                        });

                        ipValidate("network", networks).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "A.B.C.D/netmask\nA.B.C.D/netmask",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "ip")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "ip"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafUri",
            title: vm.$i18n.t("wafrules.wafUri"),
            sectionKey: "conditionIdentity",
            description:
                "<p>The URI need to match the rules, you can setup multiple URIs with <strong>newline</strong>. </br> Remember to use <strong>Regex</strong></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafUri.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafUri"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.wafAction == "16" && !value)
                            return callback(
                                new Error(vm.$i18n.t("wafrules.wafUri") + vm.$i18n.t("rcmtable.validateEmptyText"))
                            );
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "\\/api\\/secret$\n\\/assets$",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "url")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "url"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafCountry",
            title: vm.$i18n.t("wafrules.wafCountry"),
            sectionKey: "conditionIdentity",
            description: "<p>The countries to perform country detection.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                ? vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "geo").isEnable
                : false,
            options: countryCode,
            defaultVal: [],
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.wafCountry,
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafCountry"),
                        width: "350",
                    },
                });
            },
            filter: {
                type: "string",
            },
            validate: [],
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "geo")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "geo"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafIsp",
            title: vm.$i18n.t("wafrules.wafIsp"),
            sectionKey: "conditionIdentity",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafIsp.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafIsp"),
                        width: "350",
                    },
                });
            },
            placeholder: "Target Telecom Co. Ltd.\nMy Other ISP",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "isp")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "isp"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafProxyType",
            title: vm.$i18n.t("wafrules.wafProxyType"),
            sectionKey: "conditionIdentity",
            description:
                "<p>We use <strong>ip2proxy</strong> database, for more information, please look at <a href='https://www.ip2proxy.com/'>ip2proxy</a></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: false,
            options: [
                { label: "VPN", value: "vpn" },
                { label: "TOR", value: "tor" },
                { label: "DCH", value: "dch" },
                { label: "PUB", value: "pub" },
                { label: "WEB", value: "web" },
                { label: "SES", value: "ses" },
            ],
            defaultVal: [],
            filter: {
                type: "string",
            },
            validate: [],
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                          (e) => e.id == "ip2Proxy"
                      ).isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "ip2Proxy"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafHeaders",
            title: vm.$i18n.t("wafrules.wafHeaders"),
            sectionKey: "conditionIdentity",
            description: "<p>The header need to perform headers detection.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            quickAddProps: [
                {
                    title: "user-agent",
                    description: vm.$i18n.t("wafrules.wafHeadersQuickAddProps.description"),
                    templateData: [
                        {
                            label: "Google Chrome",
                            value: `user-agent:.*Chrome\/(1[0-3][0-5]|[1-9][0-9])\.`,
                        },
                        {
                            label: "Mozilla Firefox",
                            value: `user-agent:.*Firefox\/([0-9]|[1-9][0-9]|1[0-3][0-5])\.`,
                        },
                        {
                            label: "Apple Safari",
                            value: `user-agent:.*Version\/(1[2-8])\. Safari\/`,
                        },
                        {
                            label: "Internet Explorer",
                            value: `user-agent:.*Trident\/(10|11|[4-9])\.`,
                        },
                        {
                            label: "小红书",
                            value: `user-agent:.*Red\/XiaoHongShu.*`,
                        },
                        {
                            label: "抖音",
                            value: `user-agent:.*Aweme\/.*`,
                        },
                        {
                            label: "百度",
                            value: `user-agent:.*Baiduspider\/.*`,
                        },
                    ],
                },
            ],
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafHeaders.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafHeaders"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        valueArray.forEach((e) => {
                            let headerKey = e.split(":")[0];
                            let headerVal = e.split(":")[1];

                            if (headerKey == undefined || headerVal == undefined)
                                return callback(new Error("invalid: " + e));

                            if (headerKey.length == 0 || headerVal.length == 0)
                                return callback(new Error("invalid: " + e));
                        });

                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "user-agent:Bad Agent$\nhost:mysite\\.com$\nreferer:^(?!^mysite.com$)",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "header")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "header"
                );
                const wafHeaderType = vm.formData.wafHeaderType;
                return filterDetect && wafHeaderType === "wafHeaders" ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafHeaderType",
            title: vm.$i18n.t("wafrules.wafHeadersType"),
            sectionKey: "conditionIdentity",
            description: "<p>Request Header Matching Logic Version</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 80,
            formRender: "FormRadioGroup",
            explainPoptip: "ExplainWafHeaderType",
            defaultVal: "wafHeaders",
            options: [
                {
                    label: vm.$i18n.t("wafrules.wafHeaders"),
                    value: "wafHeaders",
                },
                {
                    label: vm.$i18n.t("wafrules.wafHeadersSet"),
                    value: "wafHeadersSet",
                },
            ],
            defaultVal: "wafHeaders",
            validate: [
                {
                    required: true,
                    message: "wafHeaderType 不能為空",
                    trigger: "blur",
                },
            ],
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["WafRules"],
            key: "wafHeadersSet",
            title: vm.$i18n.t("wafrules.wafHeadersSet"),
            sectionKey: "conditionIdentity",
            description: "<p>The header need to perform headers detection.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "WafHeaderListInput",
            fieldType: "textarea",
            defaultVal: [""],
            autosize: inputAutoSize,
            quickAddProps: [
                {
                    title: "user-agent",
                    description: vm.$i18n.t("wafrules.wafHeadersQuickAddProps.description"),
                    templateData: [
                        {
                            label: "Google Chrome",
                            value: `user-agent:.*Chrome\/(1[0-3][0-5]|[1-9][0-9])\.`,
                        },
                        {
                            label: "Mozilla Firefox",
                            value: `user-agent:.*Firefox\/([0-9]|[1-9][0-9]|1[0-3][0-5])\.`,
                        },
                        {
                            label: "Apple Safari",
                            value: `user-agent:.*Version\/(1[2-8])\. Safari\/`,
                        },
                        {
                            label: "Internet Explorer",
                            value: `user-agent:.*Trident\/(10|11|[4-9])\.`,
                        },
                        {
                            label: "小红书",
                            value: `user-agent:.*Red\/XiaoHongShu.*`,
                        },
                        {
                            label: "抖音",
                            value: `user-agent:.*Aweme\/.*`,
                        },
                        {
                            label: "百度",
                            value: `user-agent:.*Baiduspider\/.*`,
                        },
                    ],
                },
            ],
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: (params.row.wafHeadersSet || []).filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafHeadersSet"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        // 檢查值是否填寫
                        const findVal = value.find((item) => item.length !== 0);
                        if (!findVal) {
                            return callback();
                        }

                        const newValue = value.join("\n");
                        const valueArray = newValue.split("\n").filter((item) => item);
                        valueArray.forEach((e) => {
                            const headerKey = e.split(":")[0];
                            const headerVal = e.split(":")[1];

                            if (headerKey == undefined || headerVal == undefined)
                                return callback(new Error("invalid: " + e));

                            if (headerKey.length == 0 || headerVal.length == 0)
                                return callback(new Error("invalid: " + e));
                        });

                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "user-agent:Bad Agent$\nhost:mysite\\.com$\nreferer:^(?!^mysite.com$)",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "header")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                const filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "header"
                );
                const wafHeaderType = vm.formData.wafHeaderType;
                return filterDetect && wafHeaderType === "wafHeadersSet" ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafJa3Hashes",
            title: vm.$i18n.t("wafrules.wafJa3Hashes"),
            sectionKey: "conditionIdentity",
            description: "<p>The JA3 Hash values to match the rule, you can setup multiple JA3 Hashes with <strong>newline</strong></p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "wafJa3HashesInput",
            fieldType: "textarea",
            defaultVal: [],
            autosize: inputAutoSize,
            minWidth: 300,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                if (!params.row.wafJa3Hashes || params.row.wafJa3Hashes.length == 0 ) return;
                return h("StringList", {
                    props: {
                        listData: params.row.wafJa3Hashes.filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafJa3Hashes"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();

                        // 驗證輸入筆數(10筆)
                        if (value.length > 10) return callback(new Error("Up to 10 JA3 Hashes allowed"));

                        // md5 規則(32個 a-f0-9)
                        const md5Re = /^[a-f0-9]{32}$/;
                        const checkedValueSet = new Set();

                        for (const e of value) {
                            // 驗證重複
                            if (checkedValueSet.has(e))
                                return callback(new Error(`Duplicate: ${e}`));

                            checkedValueSet.add(e);

                            // 驗證 md5 格式
                            if (!md5Re.test(e))
                                return callback(new Error("invalid JA3 Hash: " + e));
                        }

                        callback();

                    },
                    trigger: "blur",
                },
            ],
            placeholder: "73159efb27fe349d9e62c5d365f6d972\n329b565abdf75223103131e11ead4844",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "ja3Hashes")
                        .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "ja3Hashes"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafRate",
            title: vm.$i18n.t("wafrules.wafRate"),
            sectionKey: "conditionBehavior",
            description:
                "<p>The <strong>Request Rate</strong> detection. The formula is <strong>hits / seconds</strong> </br> For Example: 200/1 means <strong>200 request per 1 second.</strong></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "",
            // disabled: false,
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let hit = value.split("/")[0];
                        let second = value.split("/")[1];

                        if (hit == undefined || second == undefined) return callback(new Error("invalid: " + value));

                        if (hit.length == 0 || second.length == 0) return callback(new Error("invalid: " + value));

                        if (!/^[0-9]*$/.test(hit) || !/^[0-9]*$/.test(second))
                            return callback(new Error("invalid: " + value));

                        callback();
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
            placeholder: "",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "cc")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "cc"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafRateLimitToken",
            title: vm.$i18n.t("wafrules.wafRateLimitToken"),
            sectionKey: "conditionBehavior",
            description:
                "<p>EDGE determines whether a request hits the WAF Rules based on token composition. For example, If the request rate is set to 200/1, it means that within 1 second, <strong>requests with identical token characteristics</strong> that access the domain more than 200 times will trigger the WAF action.</p>",
            explainPoptip: "ExplainWafRateLimitToken",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: () => import("@/components/FormItems/WafRateLimitTokenForm"),
            defaultVal: {
                group: true,
                ruleName: true,
                ip: true,
                host: true,
                uri: false,
                method: false,
                country: false,
                ua: false,
                // customSign: false,
                // customSignValue: "",
            },
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["WafRules"],
            key: "wafHttpMethod",
            title: vm.$i18n.t("wafrules.wafHttpMethod"),
            sectionKey: "conditionBehavior",
            description: "<p>The method of an http request.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                ? vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "method")
                      .isEnable
                : false,
            options: [
                { label: "GET", value: "get" },
                { label: "POST", value: "post" },
                { label: "PURGE", value: "purge" },
                { label: "PUT", value: "put" },
                { label: "HEAD", value: "head" },
                { label: "OPTIONS", value: "options" },
                { label: "DELETE", value: "delete" },
                { label: "PATCH", value: "patch" },
            ],
            defaultVal: [],
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.wafHttpMethod,
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafHttpMethod"),
                        width: "350",
                    },
                });
            },
            validate: [],
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "method")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "method"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafOwasp",
            title: vm.$i18n.t("wafrules.wafOwasp"),
            sectionKey: "conditionOwasp",
            description: "<p>The OWASP base generic vulnerability detection.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: true,
            options: [
                { label: "http_violation", value: "20000_http_violation" },
                { label: "http_anomaly", value: "21000_http_anomaly" },
                { label: "user_agent", value: "35000_user_agent" },
                { label: "generic_attack", value: "40000_generic_attack" },
                { label: "sqli", value: "41000_sqli" },
                { label: "xss", value: "42000_xss" },
            ],
            defaultVal: [],
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.wafOwasp,
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafOwasp"),
                        width: "350",
                    },
                });
            },
            validate: [],
            //     disabled: () =>
            //         vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
            //             ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "owasp")
            //                   .isEnable
            //             : true,
            //     vShowFieldVal: (item) => {
            //         let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
            //             (detect) => detect.isShow && detect.id == "owasp"
            //         );
            //         return filterDetect ? 1 : 0;
            //     },
            // },
            // {
            //     searchViews: ["WafRules"],
            //     key: "wafRewaf",
            //     title: vm.$i18n.t("wafrules.wafRewaf"),
            //     sectionKey: "conditionRewaf",
            //     description: "<p>The wafRewaf base lower standards vulnerability detection.</p>",
            //     showInTable: false,
            //     withForm: true,
            //     formTypes: ["createForm", "editForm", "multiEditForm"],
            //     // formRender: "MultiSelect",
            //     formRender: "FormSelect",
            //     multiple: true,
            //     selectAllowCreate: false,
            //     multiSelectWithToolbox: true,
            //     options: [
            //         { label: "args", value: "args" },
            //         { label: "cookie", value: "cookie" },
            //         { label: "url", value: "url" },
            //         { label: "user-agent", value: "user-agent" },
            //     ],
            //     defaultVal: [],
            //     filter: {
            //         type: "string",
            //     },
            //     render: (h, params) => {
            //         return h("TagList", {
            //             props: {
            //                 listData: params.row.wafOwasp,
            //                 maxShow: "5",
            //                 title: vm.$i18n.t("wafrules.wafOwasp"),
            //                 width: "350",
            //             },
            //         });
            //     },
            //     validate: [],
            //     disabled: () =>
            //         vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
            //             ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "rewaf")
            //                   .isEnable
            //             : true,
            //     vShowFieldVal: (item) => {
            //         let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
            //             (detect) => detect.isShow && detect.id == "rewaf"
            //         );
            //         return filterDetect ? 1 : 0;
            //     },
        },
        {
            searchViews: ["WafRules"],
            key: "wafIpExclude",
            title: vm.$i18n.t("wafrules.wafIpExclude"),
            sectionKey: "conditionExclusion",
            description: "<p>The IPs to match the rule, you can setup multiple IPs with <strong>newline</strong></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafIpExclude.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafIpExclude"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let networks = value.split("\n");
                        networks.forEach((e, i) => {
                            let index = networks.findIndex((x) => x == e);
                            if (index !== i) return callback(new Error(`Duplicate: ${e}`));
                            if (/[\s]/.test(e)) return callback(new Error("invalid: " + e));
                        });

                        ipValidate("network", networks).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "A.B.C.D/netmask\nA.B.C.D/netmask",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "ip")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "ip"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafUriExclude",
            title: vm.$i18n.t("wafrules.wafUriExclude"),
            sectionKey: "conditionExclusion",
            description:
                "<p>The URI need to match the rules, you can setup multiple URIs with <strong>newline</strong>. </br> Remember to use <strong>Regex</strong></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafUriExclude.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafUriExclude"),
                        width: "350",
                    },
                });
            },
            validate: [],
            placeholder: "\\/api/secret$\n\\/assets$",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "url")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "url"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafHeadersExclude",
            title: vm.$i18n.t("wafrules.wafHeadersExclude"),
            sectionKey: "conditionExclusion",
            description: "<p>The header need to perform headers detection.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafHeadersExclude.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafHeadersExclude"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let valueArray = value.split("\n");
                        valueArray.forEach((e) => {
                            let headerKey = e.split(":")[0];
                            let headerVal = e.split(":")[1];

                            if (headerKey == undefined || headerVal == undefined)
                                return callback(new Error("invalid: " + e));

                            if (headerKey.length == 0 || headerVal.length == 0)
                                return callback(new Error("invalid: " + e));
                        });

                        callback();
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "user-agent:Good Agent$\nhost:mysite\\.com$\nreferer:^(?!^mysite.com$)",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "header")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "header"
                );
                return filterDetect ? 1 : 0;
            },
        },
        {
            searchViews: ["WafRules"],
            key: "wafHeadersNotExist",
            title: vm.$i18n.t("wafrules.wafHeadersNotExist"),
            sectionKey: "conditionIdentity",
            description: "<p>The header need to perform headers detection.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.wafHeadersNotExist.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("wafrules.wafHeadersNotExist"),
                        width: "350",
                    },
                });
            },
            validate: [],
            placeholder: "user-agent\nhost",
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find((e) => e.id == "header")
                          .isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "header"
                );
                return filterDetect ? 1 : 0;
            },
        },

        {
            searchViews: ["WafRules"],
            key: "usedByFQDNConfigLength",
            title: vm.$i18n.t("global.usage"),
            showInTable: false,
            withForm: false,
            minWidth: 200,
            render: (h, params) => {
                return h("span", params.row.usedByFQDNConfigLength);
            },
            exportOptions: { canExport: true, callback: (value) => value },
            sortable: false,
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafWithL3BucketList",
            title: vm.$i18n.t("wafrules.wafWithL3BucketList"),
            sectionKey: "essential",
            description: "<p>The block period with Layer 3.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: (item) => {
                if (item.wafAction == "08") {
                    return 1;
                }
                if (item.wafAction == "01" && item.wafIsDenyThenBucket == true) {
                    return 1;
                }
                return 0;
            },
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafIsDenyThenBucket",
            title: vm.$i18n.t("wafrules.wafIsDenyThenBucket"),
            sectionKey: "essential",
            description: "<p>Block user for a period of time after denying multiple times.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: (item) => (item.wafAction == "01" ? 1 : 0),
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "isAllowTcpReset",
            title: vm.$i18n.t("wafrules.isAllowTcpReset"),
            sectionKey: "essential",
            description:
                "<p>Toggle on the swith, edge can terminate a TCP connection by sending a TCP reset packet.</p>",
            showInTable: false,
            withForm: false,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            vShowFieldVal: (item) =>
                (item.wafAction == "01" || item.wafAction == "08") && item.wafDenyHtmlPageName != "emptyResp" ? 1 : 0,
            queryParamsFields: [{ fieldName: "wafDenyHtmlPageName", queryIf: (val) => val != "" }],
            remoteOptions: async (queryValue) => {
                return queryValue == "emptyResp";
            },
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafDenyThenBucketRate",
            title: vm.$i18n.t("wafrules.wafDenyThenBucketRate"),
            sectionKey: "essential",
            description:
                "<p>Set the frequency threshold of triggering <b>Deny then Block Period</b>.<br>The formula is <b>hits / seconds</b><br>For Example: 200/1 means 200 hits per 1 second.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            defaultVal: "1/1",
            filter: {
                type: "",
            },
            vShowFieldVal: (item) => (item.wafIsDenyThenBucket == true && item.wafAction == "01" ? 1 : 0),
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (!/^\d+\/\d+$/.test(value)) return callback(new Error("Invalid Format"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafBucketPreSslDeny",
            title: vm.$i18n.t("wafrules.wafBucketPreSslDeny"),
            sectionKey: "essential",
            description:
                "<p>When the HTTPS request counts exceed the pre SSL deny threshold, EDGE will no longer deliver the Deny Page. Instead, it will terminate the connection without sending any response to the client. This has the same effect as an empty response in the Deny Page. Toggle the switch on to reduce server load when your domain is under attack.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                          (e) => e.id == "preSslDeny"
                      ).isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "preSslDeny"
                );
                if (filterDetect) {
                    if (item.wafAction == "01" && item.wafIsDenyThenBucket) {
                        return 1;
                    }
                    if (item.wafAction == "08") {
                        return 1;
                    }
                }
                return 0;
            },
        },
        {
            searchViews: ["WafRules", "owasp"],
            key: "wafBucketPreSslDenyThreshold",
            title: vm.$i18n.t("wafrules.wafBucketPreSslDenyThreshold"),
            sectionKey: "essential",
            description: "<p>During the block period, the HTTPS request counts to trigger pre SSL deny.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            // inputMaxNum: 3600,
            inputMinNum: 0,
            defaultVal: 0,
            filter: {
                type: "range",
            },
            disabled: () =>
                vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect
                    ? !vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                          (e) => e.id == "preSslDeny"
                      ).isEnable
                    : true,
            vShowFieldVal: (item) => {
                let filterDetect = vm.$store.state.platformPlan.multiTenancyConfigs.wafSetting.detect.find(
                    (detect) => detect.isShow && detect.id == "preSslDeny"
                );
                if (filterDetect) {
                    if (item.wafAction == "01" && item.wafIsDenyThenBucket) {
                        return 1;
                    }
                    if (item.wafAction == "08") {
                        return 1;
                    }
                }
                return 0;
            },
        },
        /* WAF結束 */
        {
            searchViews: ["GroupResource", "ServiceGroup"],
            key: "slbs",
            title: vm.$i18n.t("groupresource.slbs"),
            description: "<p>The slbs you want to assign to them.</p>",
            showInTable: true,
            minWidth: 400,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormTransfer",
            maxItemsCount: 3000,
            options: [],
            defaultVal: [],
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }, "recordType"],
            noOptionsText: "沒有可用的SLB節點",
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "maxAssignSlbCount",
            title: vm.$i18n.t("groupresource.maxAssignSlbCount"),
            description: "<p>How many slb they can register.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxAssignSlbCount,
            defaultVal: resourceLimits.groupMaxAssignSlbCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxAssignSlbCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxAssignEdgeCount",
            title: vm.$i18n.t("groupresource.maxAssignEdgeCount"),
            description: "<p>How many edge they can register.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxAssignEdgeCount,
            defaultVal: resourceLimits.groupMaxAssignEdgeCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxAssignEdgeCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "createdServiceGroupCount",
            title: vm.$i18n.t("groupresource.createdServiceGroupCount"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxServiceGroupCount",
            title: vm.$i18n.t("groupresource.maxServiceGroupCount"),
            description: "<p>How many servicegroups they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxServiceGroupCount,
            defaultVal: resourceLimits.groupMaxServiceGroupCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxServiceGroupCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "serviceGroupUsage",
            title: vm.$i18n.t("groupresource.serviceGroupUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdServiceGroupCount} / ${params.row.maxServiceGroupCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "createdServerNameCount",
            title: vm.$i18n.t("groupresource.createdServerNameCount"),
            // showInTable: true,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxServerNameCount",
            title: vm.$i18n.t("groupresource.maxServerNameCount"),
            description: "<p>How many fqdn they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxServerNameCount,
            defaultVal: resourceLimits.groupMaxServerNameCount,
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "serverNameUsage",
            title: vm.$i18n.t("groupresource.serverNameUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdServerNameCount} / ${params.row.maxServerNameCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "createdAlertRuleCount",
            title: vm.$i18n.t("groupresource.createdAlertRuleCount"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxAlertRuleCount",
            title: vm.$i18n.t("groupresource.maxAlertRuleCount"),
            description: "<p>How many alert rules they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxAlertRuleCount,
            defaultVal: resourceLimits.groupMaxAlertRuleCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxAlertRuleCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "alertRuleUsage",
            title: vm.$i18n.t("groupresource.alertRuleUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdAlertRuleCount} / ${params.row.maxAlertRuleCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "createdHtmlPageCount",
            title: vm.$i18n.t("groupresource.createdHtmlPageCount"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxHtmlPageCount",
            title: vm.$i18n.t("groupresource.maxHtmlPageCount"),
            description: "<p>How many html pages they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxHtmlPageCount,
            defaultVal: resourceLimits.groupMaxHtmlPageCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxHtmlPageCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "htmlPageUsage",
            title: vm.$i18n.t("groupresource.htmlPageUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdHtmlPageCount} / ${params.row.maxHtmlPageCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "createdOriginPolicyCount",
            title: vm.$i18n.t("groupresource.createdOriginPolicyCount"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxOriginPolicyCount",
            title: vm.$i18n.t("groupresource.maxOriginPolicyCount"),
            description: "<p>How many origin policies they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxOriginPolicyCount,
            defaultVal: resourceLimits.groupMaxOriginPolicyCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxOriginPolicyCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "originPolicyUsage",
            title: vm.$i18n.t("groupresource.originPolicyUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdOriginPolicyCount} / ${params.row.maxOriginPolicyCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxDomainListenPortCount",
            title: vm.$i18n.t("groupresource.maxDomainListenPortCount"),
            description: "<p>How many port can a <strong>domain</strong> listen.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxDomainListenPortCount,
            defaultVal: resourceLimits.groupMaxDomainListenPortCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxDomainListenPortCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value > vm.formData.maxGroupListenPortCount)
                            return callback(
                                new Error("The value cannot be greater than the Max Listen Port Count by Group.")
                            );
                        callback();
                    },
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxGroupListenPortCount",
            title: vm.$i18n.t("groupresource.maxGroupListenPortCount"),
            description: "<p>How many port can a <strong>group</strong> listen.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxListenPortCount,
            defaultVal: resourceLimits.groupMaxListenPortCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxGroupListenPortCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value < vm.formData.maxDomainListenPortCount)
                            return callback(
                                new Error("The value cannot be less than the Max Listen Port Count by Domain.")
                            );
                        callback();
                    },
                    trigger: "change",
                },
            ],
            watchField: function (value, item) {
                if (item.actionType == "create") return;
                let mapPortCount = vm.getEsResult.mapPortCount
                    ? vm.getEsResult.mapPortCount.find((e) => e.groupName == item.groupName).groupUniqListenPorts
                    : 0;
                if (value < mapPortCount) {
                    vm.$Modal.warning({
                        title: vm.$i18n.t("global.reminderText"),
                        content: `${vm.$i18n.t(
                            "groupresource.maxGroupListenPortCountModalTextForCurrentlyUsedPortCount"
                        )} ${mapPortCount} ${vm.$i18n.t(
                            "groupresource.maxGroupListenPortCountModalTextForLimitation"
                        )} ${value}`,
                        okText: "OK",
                    });
                }
            },
            render: (h, params) => {
                let mapPortCount = vm.getEsResult.mapPortCount
                    ? vm.getEsResult.mapPortCount.find((e) => e.groupName == params.row.groupName).groupUniqListenPorts
                    : 0;
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${mapPortCount} / ${params.row.maxGroupListenPortCount}`
                );
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "maxDomainOriginCount",
            title: vm.$i18n.t("groupresource.maxDomainOriginCount"),
            description: "<p>How many origin for domain.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxDomainOriginCount,
            defaultVal: resourceLimits.groupMaxDomainOriginCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxDomainOriginCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "createdWafRuleCount",
            title: vm.$i18n.t("groupresource.createdWafRuleCount"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxWafRuleCount",
            title: vm.$i18n.t("groupresource.maxWafRuleCount"),
            description: "<p>How many waf rules they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxWafRuleCount,
            defaultVal: resourceLimits.groupMaxWafRuleCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxWafRuleCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "groupMaxFQDNUsedWafCount",
            title: vm.$i18n.t("groupresource.groupMaxFQDNUsedWafCount"),
            description: "<p>How many WAF Rule can be added to each FQDN Config.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxFQDNUsedWafCount,
            defaultVal: resourceLimits.groupMaxFQDNUsedWafCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.groupMaxFQDNUsedWafCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxOwaspRuleCount",
            title: vm.$i18n.t("groupresource.maxOwaspRuleCount"),
            description: "<p>How many OWASP Rule can be created.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxOwaspRuleCount,
            defaultVal: resourceLimits.groupMaxOwaspRuleCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxOwaspRuleCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "groupMaxRulesInWafHeadersSet",
            title: vm.$i18n.t("groupresource.groupMaxRulesInWafHeadersSet"),
            description: "<p>How many rules can be created in request headers set.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxRulesInWafHeadersSet,
            defaultVal: 5,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.groupMaxRulesInWafHeadersSet")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxOwaspRuleUsedFQDNCount",
            title: vm.$i18n.t("groupresource.maxOwaspRuleUsedFQDNCount"),
            description: "<p>How many FQDN Config can be added to each OWASP Rule.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxOwaspRuleUsedFQDNCount,
            defaultVal: resourceLimits.groupMaxOwaspRuleUsedFQDNCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxOwaspRuleUsedFQDNCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxRuleCountOfRewriteUrlRules",
            title: vm.$i18n.t("groupresource.maxRuleCountOfRewriteUrlRules"),
            description: "<p>How many rule can be added to each rewriteUrlRules.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 1,
            inputMaxNum: resourceLimits.groupMaxRuleCountOfRewriteUrlRules,
            defaultVal: 3,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxRuleCountOfRewriteUrlRules")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxRuleCountOfCacheRules",
            title: vm.$i18n.t("groupresource.maxRuleCountOfCacheRules"),
            description: "<p>How many rule can be added to each cacheRules.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 1,
            inputMaxNum: resourceLimits.groupMaxRuleCountOfCacheRules,
            defaultVal: 5,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxRuleCountOfCacheRules")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "wafRuleUsage",
            title: vm.$i18n.t("groupresource.wafRuleUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdWafRuleCount} / ${params.row.maxWafRuleCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["AppProfile", "GroupResource"],
            key: "maxServerNameInAppProfileCount",
            title: vm.$i18n.t("groupresource.maxServerNameInAppProfileCount"),
            align: "center",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            inputMinNum: 0,
            inputMaxNum: resourceLimits.profileMaxCapacity,
            defaultVal: resourceLimits.profileMaxCapacity,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxServerNameInAppProfileCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "createdFQDNConfigCount",
            title: vm.$i18n.t("groupresource.createdFQDNConfigCount"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxFQDNConfigCount",
            title: vm.$i18n.t("groupresource.maxFQDNConfigCount"),
            description: "<p>How many FQDN Config they can create.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxFQDNConfigCount,
            defaultVal: resourceLimits.groupMaxFQDNConfigCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxFQDNConfigCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "paymentStatus",
            align: "center",
            title: vm.$i18n.t("groupresource.paymentStatus"),
            showInTable: vm.$store.state.platformPlan.multiTenancyConfigs.isAllowPaymentShow,
            withForm: false,
            minWidth: 160,
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                // type: "string"
                type: "boolean",
                searchKey: "billing.paymentStatus",
            },
            render: (h, params) => {
                try {
                    let paymentStatus = params.row.billing.paymentStatus;
                    var iconType = boolToIcons({
                        value: paymentStatus,
                        falseToDash: false,
                    });
                    var iconColor = boolToColors({
                        value: paymentStatus,
                        falseToGrey: true,
                    });
                    return h("Icon", {
                        props: {
                            type: iconType,
                            color: iconColor,
                            size: 24,
                        },
                    });
                } catch (error) {
                    // 無付款狀態不給值
                    // console.log(`group:${params.row.groupName} paymentStatus error:, ${error.message}`);
                    return h();
                }
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "FQDNConfigUsage",
            align: "center",
            title: vm.$i18n.t("groupresource.FQDNConfigUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdFQDNConfigCount} / ${params.row.maxFQDNConfigCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxAppProfileCount",
            title: vm.$i18n.t("groupresource.maxAppProfileCount"),
            description: "<p>How many AppProfile they can create.</p>",
            align: "center",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxAppProfileCount,
            defaultVal: resourceLimits.groupMaxAppProfileCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxAppProfileCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "createdAppProfileCount",
            title: vm.$i18n.t("groupresource.createdAppProfileCount"),
            // showInTable: true,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "appProfileUsage",
            align: "center",
            title: vm.$i18n.t("groupresource.appProfileUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                let maxCount =
                    params.row.maxAppProfileCount == undefined
                        ? resourceLimits.groupMaxAppProfileCount
                        : params.row.maxAppProfileCount;

                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdAppProfileCount} / ${maxCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "maxUserCount",
            title: vm.$i18n.t("groupresource.maxUserCount"),
            description: "<p>How many user they can create.</p>",
            align: "center",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxUserCount,
            defaultVal: resourceLimits.groupMaxUserCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxUserCount")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "createdUserCount",
            title: vm.$i18n.t("groupresource.createdUserCount"),
            // showInTable: true,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "userUsage",
            align: "center",
            title: vm.$i18n.t("groupresource.userUsage"),
            showInTable: true,
            withForm: false,
            minWidth: 140,
            render: (h, params) => {
                let maxCount =
                    params.row.maxUserCount == undefined ? resourceLimits.groupMaxUserCount : params.row.maxUserCount;
                return h(
                    "div",
                    {
                        props: {},
                    },
                    `${params.row.createdUserCount} / ${maxCount}`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "createdNonProfileServerNameCount",
            title: vm.$i18n.t("groupresource.createdNonProfileServerNameCount"),
            // showInTable: true,
            withForm: false,
            minWidth: 100,
            // filter: {
            //     type: "range"
            // }
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "kafkaLogRate",
            title: vm.$i18n.t("groupresource.kafkaLogRate"),
            description: "<p>The Log speed to limit. (byte/s)</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            minWidth: 100,
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupkafkaLogRate,
            defaultVal: resourceLimits.groupkafkaLogRate,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.kafkaLogRate")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "isSubscribeRawLogs",
            title: vm.$i18n.t("groupresource.isSubscribeRawLogs"),
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "netflowIpListV2",
            title: vm.$i18n.t("groupresource.netflowIpListV2"),
            sectionKey: "netflow",
            description: "<p>Set IP list groups to view charts in netflow page.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: () => import("@/components/FormItems/NetflowIpListGroup"),
            filter: {
                type: "string",
            },
            render: (h, params) => {
                if (params.row.netflowIpListV2 && params.row.netflowIpListV2.length > 0) {
                    const ipListGroups = params.row.netflowIpListV2.map((item) => ({
                        ...item,
                        ips: item.ips.split("\n"),
                    }));

                    return h("JsonPoptip", {
                        props: {
                            jsonData: ipListGroups,
                            title: vm.$i18n.t("groupresource.netflowIpListV2"),
                            buttonText: "",
                            buttonIcon: "md-eye",
                        },
                    });
                }
            },
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    value = Array.isArray(value) ? value : [];
                    return value.map((ipList) => `${ipList.name}:${ipList.ips}`);
                },
            },
            sortable: false,
        },
        {
            searchViews: ["GroupResource"],
            key: "http2UpgradeList",
            title: vm.$i18n.t("groupresource.http2UpgradeList"),
            description: `<p>
            You can configure ports or port ranges <b>(e.g. 2000 or 1001–1100)</b> that may be upgraded to HTTP/2.
            Available ports: all ports from <b>1001</b> to <b>4999</b>.<br>
            Ports in the range <b>5000–6000</b> are generally unavailable unless explicitly allowed in backend configuration.<br>
            Ports in the range <b>6001–32767</b> may also be restricted based on unsafe port settings.<br>
            Additionally, port <b>9600</b> is reserved for Logstash and cannot be used.<br>
            If a service is using a port listed here, it will be automatically upgraded to HTTP/2.
            </p>`,
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: () => import("@/components/FormItems/Http2UpgradeListByGroup"),
            render: (h, params) => {
                const ranges = params.row.http2UpgradeList;
                if (Array.isArray(ranges) && ranges.length > 0) {
                    return ranges.map((item) => h("Tag", {}, item)); // item 為字串
                } else {
                    return h("span", {}, "-");
                }
            },
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    return Array.isArray(value) ? value : [];
                },
            },
            sortable: false,
            validate: [
                {
                    validator: async (rule, value, callback) => {
                        if (!value || value.length === 0) return callback();

                        // 驗證格式是否只包含數字與連字號(-)
                        const invalidFormat = value.some((item) => {
                            return !/^\d+(-\d+)?$/.test(item);
                        });

                        if (invalidFormat) {
                            return callback(new Error(vm.$i18n.t("groupresource.http2UpgradeListErrorFormat")));
                        }

                        try {
                            const res = await formOptions.httpsUseablePorts();
                            const allowedPorts = res || [];

                            const expandPorts = (value || []).flatMap((item) => {
                                if (typeof item === "number") return [item];
                                if (typeof item === "string") {
                                    if (/^\d+$/.test(item)) return [parseInt(item)];
                                    if (/^\d+-\d+$/.test(item)) {
                                        const [start, end] = item.split("-").map(Number);
                                        if (start > end) return []; // 避免非法範圍
                                        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                                    }
                                }
                                return [];
                            });

                            // 檢查重複 port
                            const portCountMap = {};
                            const duplicatePorts = [];
                            for (const port of expandPorts) {
                                portCountMap[port] = (portCountMap[port] || 0) + 1;
                                if (portCountMap[port] === 2) {
                                    duplicatePorts.push(port);
                                }
                            }

                            if (duplicatePorts.length > 0) {
                                return callback(
                                    new Error(
                                        `${vm.$i18n.t(
                                            "groupresource.http2UpgradeListErrorDuplicate"
                                        )} ${duplicatePorts.join(", ")}`
                                    )
                                );
                            }

                            const invalidPorts = expandPorts.filter((port) => !allowedPorts.includes(port));
                            if (invalidPorts.length > 0) {
                                return callback(
                                    new Error(
                                        `${vm.$i18n.t(
                                            "groupresource.http2UpgradeListErrorInvalid"
                                        )} ${invalidPorts.join(", ")}`
                                    )
                                );
                            }

                            callback();
                        } catch (err) {
                            console.log("驗證可用 port 發生錯誤", err);
                            callback(new Error(vm.$i18n.t("groupresource.http2UpgradeListErrorFailed")));
                        }
                    },
                    trigger: "blur",
                },
            ],
            // vShowFieldVal: () => {
            //     return vm.$store.state.platformPlan.multiTenancyConfigs.sethttp2UpgradeList ? 1 : 0;
            // },
        },
        {
            searchViews: ["GroupResource"],
            key: "netflowEmail",
            title: vm.$i18n.t("groupresource.netflowEmail"),
            sectionKey: "netflow",
            description: "<p>Set email to receive netflow report.</p>",
            showInTable: false,
            withForm: false,
            formTypes: ["createForm", "editForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
            placeholder: "",
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value !== "" && value.indexOf("@") == -1) return callback(new Error("Invalid Email"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.setNetflowIpList ? 1 : 0;
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "netflowSchedule",
            title: vm.$i18n.t("groupresource.netflowSchedule"),
            sectionKey: "netflow",
            description: "<p>Select date to send netflow report from our platform to the email given above.</p>",
            showInTable: false,
            withForm: false,
            disabled: () => false,
            formRender: "FormRadioGroup",
            options: [
                {
                    label: "5",
                    value: 5,
                },
                {
                    label: "15",
                    value: 15,
                },
                {
                    label: "25",
                    value: 25,
                },
            ],
            defaultVal: 5,
            formTypes: ["createForm", "editForm"],
            filter: {
                type: "string",
            },
            exportOptions: { canExport: false, callback: (value) => value },
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.setNetflowIpList ? 1 : 0;
            },
        },
        {
            searchViews: ["GroupResource"],
            key: "netflowPercentile",
            title: vm.$i18n.t("groupresource.netflowPercentile"),
            sectionKey: "netflow",
            description:
                "<p>This is used to show the value at a specific percentile in the report. For example, 95 means the 95th percentile.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: 95,
            inputMaxNum: 100,
            inputMinNum: 0,
            filter: {
                type: "range",
            },
            disabled: () => false,
        },
        {
            searchViews: ["GroupResource"],
            key: "netflowTimestampAggregationInterval",
            title: vm.$i18n.t("groupresource.netflowTimestampAggregationInterval"),
            sectionKey: "netflow",
            description:
                "<p>It defines the time bucket size for grouping or aggregating records based on timestamp. For example, setting it to <strong>1</strong> means the data will be grouped into <strong>1-minute</strong> intervals.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: 1,
            inputMaxNum: 10,
            inputMinNum: 1,
            filter: {
                type: "range",
            },
            disabled: () => false,
        },
        {
            searchViews: ["OriginPolicy"],
            key: "originPolicyName",
            title: vm.$i18n.t("originPolicy.originPolicyName"),
            sectionKey: "essential",
            description: "<p>The name of the origin policy.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            placeholder: "",
            validate: [
                {
                    required: true,
                    message: "Origin policy name can't be empty",
                    trigger: "blur",
                },
                {
                    validator: async (rule, value, callback, source, options) => {
                        if (!/^[a-z0-9\_]*$/.test(value))
                            return callback(
                                new Error(`${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a - z 0 - 9 _`)
                            );
                        if (value.length > 35)
                            return callback(new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 35`));
                        let checkDocumentExistResult = await checkDocumentExist({
                            topic: "originPolicyId",
                            fieldVal: value,
                            groupName: vm.formData.groupName,
                        });
                        if (!checkDocumentExistResult.data.success) {
                            return callback(new Error("OriginPolicy Name Exist"));
                        }
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
                defaultValue: vm.$route.query.originPolicyName,
            },
        },
        // {
        //     searchViews: ["OriginPolicy"],
        //     key: "originRegion",
        //     title: "Country",
        //     description: "<p></p>",
        //     showInTable: true,
        //     withForm: true,
        //     formTypes: ["createForm", "editForm", "multiEditForm"],
        //     formRender: "MultiSelect",
        //     options: countryCode,
        //     defaultVal: [],
        //     render: (h, params) => {
        //         return h("p", params.row.originRegion.join(", "));
        //     },
        //     filter: {
        //         type: "string"
        //     },
        //     validate: []
        // },
        {
            searchViews: ["OriginPolicy"],
            key: "loadbalancerMethod",
            title: "loadbalancerMethod",
            sectionKey: "essential",
            showInTable: true,
            withForm: true,
            disabled: false,
            formRender: "FormSelect",
            explainPoptip: "ExplainPoptip",
            explainPoptipProps: {
                title: `Loadbalancer Method ${vm.$t("global.object")} ${vm.$t("global.explanation")}`,
                tableData: [
                    {
                        object: "IP Hash",
                        explain: vm.$t("originPolicy.explainObject.ip_hash"),
                    },
                    {
                        object: "Round Robin",
                        explain: vm.$t("originPolicy.explainObject.round_robbin"),
                    },
                    {
                        object: "Weight",
                        explain: vm.$t("originPolicy.explainObject.weight"),
                    },
                ],
            },
            options: [
                { label: "IP Hash", value: "ipHash" },
                { label: "Round Robin", value: "roundRobin" },
                // { label: "Least Connect", value: "leastConnect" },
                { label: "Weight", value: "weight" },
            ],
            defaultVal: "ipHash",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "loadbalancerSticky",
            title: "loadbalancerSticky",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckMode",
            title: "healthCheckMode",
            sectionKey: "essential",
            showInTable: true,
            withForm: true,
            disabled: false,
            formRender: "FormSelect",
            options: [
                { label: "TCP", value: "tcp" },
                { label: "HTTP", value: "http" },
            ],
            defaultVal: "tcp",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckUri",
            title: "healthCheckUri",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            formRender: "FormInput",
            defaultVal: "",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0 || value.trim() == "")
                            return callback(new Error("Missing HealthCheck Url"));

                        callback();
                    },
                    trigger: "change",
                },
            ],
            placeholder: "",
            vShowFieldVal: (item) => {
                return vm.formData.healthCheckMode !== "tcp" ? 1 : 0;
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckHttpCode",
            title: "healthCheckHttpCode",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: false,
            defaultVal: ["200-399"],
            options: ["200-399"],
            disabled: () => true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback(new Error("Missing HealthCheck Url"));

                        callback();
                    },
                    trigger: "change",
                },
            ],
            placeholder: "",
            vShowFieldVal: (item) => {
                return vm.formData.healthCheckMode !== "tcp" ? 1 : 0;
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckInterval",
            title: "healthCheckInterval",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            disabled: () => true,
            formRender: "FormSelect",
            options: [{ label: "10s", value: 10 }],
            defaultVal: 10,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckTimeout",
            title: "healthCheckTimeout",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            disabled: () => true,
            formRender: "FormSelect",
            options: [{ label: "500ms", value: 500 }],
            defaultVal: 500,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckSuccess",
            title: "healthCheckSuccess",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            disabled: () => true,
            formRender: "FormSelect",
            options: [{ label: "2", value: 2 }],
            defaultVal: 2,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["OriginPolicy"],
            key: "healthCheckFail",
            title: "healthCheckFail",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            disabled: () => true,
            formRender: "FormSelect",
            options: [{ label: "3", value: "3" }],
            defaultVal: "3",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
        },
        //htmlPages
        {
            searchViews: ["HtmlPages"],
            key: "htmlPageName",
            title: "HtmlPage Name",
            sectionKey: "essential",
            description: "<p>The name of the html page.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            placeholder: "",
            validate: [
                {
                    required: true,
                    message: "Html page name can't be empty",
                    trigger: "blur",
                },
                {
                    validator: async (rule, value, callback, source, options) => {
                        if (!/^[a-z0-9\_]*$/.test(value))
                            return callback(
                                new Error(`${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a - z 0 - 9 _`)
                            );
                        if (value.length > 35)
                            return callback(new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 35`));
                        if (value == "default" || value == "simple" || value == "emptyResp")
                            return callback(new Error("System Reserved Name"));
                        let checkDocumentExistResult = await checkDocumentExist({
                            topic: "htmlPageId",
                            fieldVal: value,
                            groupName: vm.formData.groupName,
                        });
                        if (!checkDocumentExistResult.data.success) {
                            return callback(new Error("HtmlPage Name Exist"));
                        }
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
                defaultValue: vm.$route.query.htmlPageName,
            },
        },
        {
            searchViews: ["HtmlPages"],
            key: "htmlPageUpload",
            title: "HtmlPage Upload",
            sectionKey: "essential",
            description: "<p>Upload your html here.</p>",
            showInTable: false,
            withForm: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "FormUpload",
            uploadEndPoint: "htmlPage",
            fieldType: "textarea",
            defaultVal: "",
            handleUpload: {
                format: ["html"],
                // 上傳成功後更新的欄位
                successUpdateFields: ["htmlPageFileName", "htmlPageRaw", "htmlPageUpload"],
                // successAction() {
                //     vm.formData.htmlPreview = generateHtmlPreview({
                //         htmlPageRaw: vm.formData.htmlPageRaw,
                //         ridPosition: vm.formData.ridPosition,
                //         ridColor: vm.formData.ridColor
                //     });
                // }
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["HtmlPages"],
            key: "ridPosition",
            title: "Rid Position",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            withTable: false,
            disabled: () => false,
            formRender: "FormRadioGroup",
            options: [
                {
                    label: "Top Left",
                    value: "z-index:9999;font-size:18px;position:absolute;top:0;left:0;",
                },
                {
                    label: "Top Right",
                    value: "z-index:9999;font-size:18px;position:absolute;top:0;right:0;",
                },
                {
                    label: "Center Center",
                    value: "z-index:9999;font-size:18px;position:absolute;top:50%;left:50%;margin-right: -50%;transform: translate(-50%, -50%);",
                },
                {
                    label: "Bottom Left",
                    value: "z-index:9999;font-size:18px;position:absolute;bottom:0;left:0;",
                },
                {
                    label: "Bottom Right",
                    value: "z-index:9999;font-size:18px;position:absolute;bottom:0;right:0;",
                },
            ],
            defaultVal: "z-index:9999;font-size:18px;position:absolute;top:0;left:0;",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["HtmlPages"],
            key: "ridColor",
            title: "Rid Color",
            sectionKey: "essential",
            showInTable: false,
            withForm: true,
            withTable: false,
            disabled: () => false,
            formRender: "FormRadioGroup",
            options: [
                { label: "Black", value: "color:black" },
                { label: "White", value: "color:white" },
            ],
            defaultVal: "color:black",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            exportOptions: { canExport: false, callback: (value) => value },
        },

        {
            searchViews: ["HtmlPages"],
            key: "htmlPageFileName",
            title: "htmlPageFileName",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: false,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "FormInput",
            defaultVal: "",
            exportOptions: { canExport: false, callback: (value) => value },
        },

        {
            searchViews: ["HtmlPages"],
            key: "htmlPageRaw",
            title: "HtmlPage Content",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            // formRender: "FormInput",
            // fieldType: "textarea",
            // autosize: { minRows: 8, maxRows: 12 },
            formRender: "CodeEditor",
            defaultVal: "",
        },
        {
            searchViews: ["HtmlPages"],
            key: "htmlPreview",
            title: "HtmlPage Preview",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            isFormSlot: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "HtmlPreview",
            defaultVal: "",
            queryParamsFields: ["htmlPageRaw", "ridPosition", "ridColor"],
        },
        //增加isPresentHtmlPageFromLocal的按鈕
        {
            searchViews: ["HtmlPages"],
            key: "isPresentHtmlPageFromLocal",
            title: vm.$i18n.t("htmlPage.isPresentHtmlPageFromLocal"),
            sectionKey: "essential",
            description:
                "<p>Toggle the switch on to get your HTML Page from EDGE directly, rather than using Iframe.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSwitch",
            defaultVal: vm.$store.state.platformPlan.multiTenancyConfigs.isPresentHtmlPageFromLocalDefaultValue,
            type: "boolean",
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.isPresentHtmlPageFromLocal ? 1 : 0;
            },
        },

        // scrolls
        {
            searchViews: ["Scrolls"],
            key: "scrollName",
            title: "Scroll Name",
            sectionKey: "essential",
            description: "<p>The name of the scroll page.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            placeholder: "",
            validate: [
                {
                    required: true,
                    message: "Scroll name can't be empty",
                    trigger: "blur",
                },
                {
                    validator: async (rule, value, callback, source, options) => {
                        if (!/^[a-z0-9\_]*$/.test(value))
                            return callback(
                                new Error(`${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a - z 0 - 9 _`)
                            );
                        if (value.length > 35)
                            return callback(new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 35`));
                        if (value == "default") return callback(new Error("System Reserved Name"));
                        let checkDocumentExistResult = await checkDocumentExist({
                            topic: "scrollId",
                            fieldVal: value,
                            groupName: vm.formData.groupName,
                        });
                        if (!checkDocumentExistResult.data.success) {
                            return callback(new Error("Scroll Name Exist"));
                        }
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
                defaultValue: vm.$route.query.scrollName,
            },
        },
        {
            searchViews: ["Scrolls"],
            key: "scrollUpload",
            title: "Scroll Upload",
            sectionKey: "essential",
            description: "<p>Upload your html here.</p>",
            showInTable: false,
            withForm: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "FormUpload",
            uploadEndPoint: "scroll",
            fieldType: "textarea",
            defaultVal: "",
            handleUpload: {
                format: ["js"],
                // 上傳成功後更新的欄位
                successUpdateFields: ["scrollFileName", "scrollRaw", "scrollUpload"],
                // successAction() {
                //     vm.formData.htmlPreview = generateHtmlPreview({
                //         htmlPageRaw: vm.formData.htmlPageRaw,
                //         ridPosition: vm.formData.ridPosition,
                //         ridColor: vm.formData.ridColor
                //     });
                // }
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },

        {
            searchViews: ["Scrolls"],
            key: "scrollFileName",
            title: "scrollFileName",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: false,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "FormInput",
            defaultVal: "",
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["Scrolls"],
            key: "scrollRaw",
            title: "Scroll Content",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "CodeEditorV2",
            defaultVal: "module.exports = (req, res, next) => {\n\n}",
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        try {
                            let prettifyResult = prettier.format(value, { tabWidth: 4 });
                        } catch (err) {
                            console.log(err.message);
                            return callback(new Error(err.message));
                        }
                        scrollValidate(value).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            mode: "text/javascript",
        },

        {
            searchViews: ["ServiceGroup", "SlbSetting", "VirtualSlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "runningStat",
            title: vm.$i18n.t("servicegroup.health"),
            description: "",
            showInTable: true,
            withForm: false,
            options: runningStatFilterOptions,
            minWidth: 100,
            defaultVal: "",
            filter: {
                type: "select",
            },
            render: (h, params) => {
                var iconColor = statusToColor(params.row.runningStat);

                return h("Icon", {
                    props: {
                        type: "ios-radio-button-on",
                        color: iconColor,
                        size: 16,
                    },
                });
            },
        },
        {
            searchViews: ["ServiceGroup"],
            key: "protectSlbs",
            title: vm.$i18n.t("servicegroup.protectSlbs"),
            description: "<p>The Protect SLBs to put into the servicegroup.</p>",
            showInTable: true,
            minWidth: 400,
            withForm: true,
            formTypes: () => ["createForm", "editForm", "multiEditForm"],
            vShowFieldVal: () => 1,
            formRender: "FormTransfer",
            watchField: (item) => {},
            options: [],
            remoteOptions: async (queryParams) => {
                queryParams.serviceProvideType = "protect";
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/slbnames?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }, "recordType"],
            defaultVal: [],
            maxItemsCount: 0,
            noOptionsText: "沒有可用的防禦SLB節點",
            filter: {
                type: "string",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.recordType == "A" && value.length > vm.$store.state.maxRecordLb.a)
                            return callback(new Error("Limit Exceeded"));
                        if (vm.recordType == "CNAME" && value.length > vm.$store.state.maxRecordLb.cname)
                            return callback(new Error("Limit Exceeded"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            render: (h, params) => {
                var protectSlbs = vm.getEsResult
                    ? params.row.protectSlbs.map((e) => vm.getEsResult.protectSlbs.find((s) => e == s.nodeId))
                    : [];
                return h(
                    "div",
                    protectSlbs
                        .slice()
                        .sort()
                        .map(function (s) {
                            let serviceProvideTypeTag = s.serviceProvideType == "normal" ? "N" : "P";
                            return h(
                                "Tag",
                                {
                                    props: {
                                        color: s.runningStat,
                                    },
                                },
                                `${s.hostname}(${serviceProvideTypeTag}): ${s.serviceIP} ${s.line}`
                            );
                        })
                );
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting", "EdgeSetting", "ProbeSetting", "NinjaSetting"],
            key: "hostname",
            title: vm.$i18n.t("slbsetting.hostname"),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            minWidth: 180,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
                defaultValue: vm.$route.query.hostname,
            },
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "internalIP",
            title: vm.$i18n.t("slbsetting.internalIP"),
            showInTable: false,
            withForm: false,
            minWidth: 140,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
            render: (h, params) => {
                console.log(params.row.internalIP);
                return h("TagList", {
                    props: {
                        listData: params.row.internalIP,
                        maxShow: "5",
                        title: vm.$i18n.t("slbsetting.internalIP"),
                        width: "350",
                    },
                });
            },
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "externalIP",
            title: vm.$i18n.t("slbsetting.externalIP"),
            showInTable: false,
            withForm: false,
            minWidth: 140,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["SlbSetting"],
            key: "isEnableProxyProtocol",
            title: vm.$i18n.t("slbsetting.isEnableProxyProtocol"),
            sectionKey: "essential",
            description: "<p>Toggle the switch on, SLB will send Proxy Protocol header to EDGE.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["SlbSetting"],
            key: "isEnableProxyProtocolFromClient",
            title: vm.$i18n.t("slbsetting.isEnableProxyProtocolFromClient"),
            sectionKey: "essential",
            description:
                "<p>Toggle the switch on, SLB will expect to receive and then parse the Proxy Protocol header.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "serviceIP",
            title: vm.$i18n.t("slbsetting.serviceIP"),
            description: "<p>The <strong>ServiceIP</strong> is what the client will resolve in the DNS.</p>",
            showInTable: true,
            withForm: true,
            minWidth: tableColumnWidth.serviceIP,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "serviceVersion",
            title: vm.$i18n.t("edgesetting.serviceVersion"),
            description: "<p>edge version</p>",
            showInTable: true,
            withForm: true,
            minWidth: tableColumnWidth.serviceVersion,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting", "ServiceGroup"],
            key: "recordType",
            title: vm.$i18n.t("slbsetting.recordType"),
            description: "<p>A or CNAME, this value <strong>can't</strong> be changed after created.</p>",
            showInTable: false,
            withForm: true,
            disabled: () => vm.formType !== "createForm",
            formRender: "FormSelect",
            options: [
                { label: "A Record", value: "A" },
                { label: "CNAME Record", value: "CNAME" },
            ],
            defaultVal: "A",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    message: "Missing Record Type",
                    trigger: "blur",
                },
            ],
            filter: {
                type: "select",
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "isDummySlb",
            title: vm.$i18n.t("slbsetting.isDummySlb"),
            sectionKey: "essential",
            withTable: false,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "TextOnly",
            defaultVal: true,
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "lineOptimize",
            title: vm.$i18n.t("slbsetting.lineOptimize"),
            showInTable: false,
            withForm: false,
            minWidth: 120,
            render: (h, params) => {
                return h(
                    "LineOptimize",
                    {
                        props: { slb: params.row },
                    },
                    ""
                );
            },
            vShowFieldVal: () => 1,
            sortable: false,
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting", "RecordLineGroups"],
            key: "line",
            title: vm.$i18n.t("slbsetting.line"),
            sectionKey: "essential",
            description: "<p>The <strong>Line</strong> means which particular of clients will resolve to this Slb.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 140,
            tooltip: true,
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: true,
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.line,
                        maxShow: "5",
                        title: vm.$i18n.t("slbsetting.line"),
                        width: "350",
                    },
                });
            },
            options: [],
            defaultVal: ["默认"],
            // 普通用戶無法調整防禦節點線路
            disabled: () => vm.formData.serviceProvideType == "protect" && !isAdmin,
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback(new Error("Missing Line"));
                        callback();
                    },
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "serviceProvideType",
            title: vm.$i18n.t("slbsetting.serviceProvideType"),
            sectionKey: "essential",
            description:
                "<p>The <strong>serviceProvideType</strong> determine this node is protection node or service node.</p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formRender: "FormSelect",
            formTypes: ["createForm", "editForm", "multiEditForm"],
            options: [
                { label: "Normal", value: "normal" },
                { label: "Protect", value: "protect" },
            ],
            defaultVal: "normal",
            vShowFieldVal: () => 1,
            minWidth: 200,
            tooltip: true,
            filter: {
                type: "select",
            },
            // 普通用戶無法調整
            disabled: () => !isAdmin,
        },
        {
            searchViews: [
                "SlbSetting",
                "VirtualSlbSetting",
                "EdgeSetting",
                "UsersManage",
                "AlertRules",
                "NotificationChannels",
                "ServiceGroup",
                "NinjaSetting",
            ],
            key: "isenabled",
            title: vm.$i18n.t("slbsetting.isenabled"),
            description:
                "<p>Toggle the status of slbs, when <strong>disabled</strong>, the DNS <strong>won't</strong> be resolved.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 140,
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
            render: (h, params) => {
                var status = params.row.isenabled;

                return h("i-switch", {
                    props: {
                        value: status,
                        beforeChange() {
                            return new Promise((resolve) => {
                                vm.$Modal.confirm({
                                    title: vm.$i18n.t("rcmtable.changeStatusText"),
                                    content: vm.$i18n.t("rcmtable.confirmChangeStatusText"),
                                    okText: vm.$i18n.t("rcmtable.confirmText"),
                                    cancelText: vm.$i18n.t("rcmtable.cancelText"),
                                    onOk: () => {
                                        resolve();
                                    },
                                });
                            });
                        },
                        disabled: vm.isEditBtnDisabled,
                    },
                    on: {
                        "on-change": (value) => {
                            params.row.isenabled = value;
                            delete params.row._index;
                            delete params.row._rowKey;
                            delete params.row._checked;
                            _updateData("rcm-slbs", "index", params.row)
                                .then((result) => {
                                    vm.$Notice.success({
                                        title: vm.$i18n.t("global.success"),
                                        desc: `${result.data.message}`,
                                    });
                                })
                                .catch(function (error) {
                                    vm.$Notice.warning({
                                        title: vm.$i18n.t("global.error"),
                                        desc: `${error}`,
                                    });
                                    vm.getTableData();
                                });
                        },
                    },
                });
            },
            sortable: false,
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "externalCheckInfo",
            title: vm.$i18n.t("slbsetting.externalCheckInfo"),
            showInTable: true,
            withForm: false,
            minWidth: 360,
            render: (h, params) => {
                const isEnabledExternalCheck = assignDefaultValue(params.row.isEnabledExternalCheck, false);
                const isPassExternalHealthCheck = assignDefaultValue(params.row.isPassExternalHealthCheck, "N/A");
                const checkTimesAsHealthy = assignDefaultValue(params.row.checkTimesAsHealthy, 1);
                const checkTimesAsUnhealthy = assignDefaultValue(params.row.checkTimesAsUnhealthy, 1);
                const externalCheckUrl = assignDefaultValue(params.row.externalCheckUrl, "N/A");

                const isEnabledExternalCheckIcon = h("Icon", {
                    props: {
                        type: boolToIcons({
                            value: params.row.isEnabledExternalCheck,
                            falseToDash: true,
                        }),
                        color: boolToColors({
                            value: params.row.isEnabledExternalCheck,
                            falseToGrey: true,
                        }),
                        size: 24,
                    },
                });
                const isPassExternalHealthCheckSpan = h(
                    "span",
                    {
                        style: {
                            color: boolToColors({
                                value: params.row.isPassExternalHealthCheck,
                                falseToGrey: false,
                            }),
                        },
                    },
                    isPassExternalHealthCheck
                );

                const p1 = h("p", ["Enabled: ", isEnabledExternalCheckIcon, ", Pass: ", isPassExternalHealthCheckSpan]);
                const p2 = h("p", `Good: ${checkTimesAsHealthy}, Bad: ${checkTimesAsUnhealthy}`);
                const p3 = h("p", `CheckUrl: ${externalCheckUrl}`);
                const statsBtn = h(
                    "Button",
                    {
                        props: {
                            size: "small",
                        },
                        on: {
                            click: () => {
                                getSlbExternCheckStats(params.row.hostname)
                                    .then((result) => {
                                        let { avgRates, probesRates } = result.data;
                                        // {
                                        //     avgRates: [
                                        //         [
                                        //             { timestamp: "", rate: "" },
                                        //             { timestamp: "", rate: "" }
                                        //         ]
                                        //     ],
                                        //     probesRates: [
                                        //         {
                                        //             probeName: "",
                                        //             rates: [
                                        //                 { timestamp: "", rate: "" },
                                        //                 { timestamp: "", rate: "" }
                                        //             ]
                                        //         },
                                        //         {
                                        //             probeName: "",
                                        //             rates: [
                                        //                 { timestamp: "", rate: "" },
                                        //                 { timestamp: "", rate: "" }
                                        //             ]
                                        //         }
                                        //     ]
                                        // };

                                        // 創建敘述資訊
                                        let startTime = _dateFormat(avgRates[0].timestamp, "HH:mm");
                                        let dataLength = avgRates.length;
                                        let endTime = _dateFormat(avgRates[dataLength - 1].timestamp, "HH:mm");

                                        // 創建表頭
                                        let tableColumns = avgRates.map((e) => ({
                                            key: e.timestamp,
                                            title: _dateFormat(e.timestamp, "mm"),
                                            width: 60,
                                        }));
                                        tableColumns.unshift({
                                            key: "hostname",
                                            width: 200,
                                            fixed: "left",
                                        });

                                        // 創建資料
                                        let tableData = [];

                                        // 須將多筆資料轉為平鋪Object
                                        let avgRateData = arrayToObject(avgRates, "timestamp", "rate");
                                        avgRateData.hostname = "Average";
                                        tableData.push(avgRateData);

                                        probesRates.forEach((probeData) => {
                                            let probeRateData = arrayToObject(probeData.rates, "timestamp", "rate");
                                            probeRateData.hostname = probeData.probeName;
                                            tableData.push(probeRateData);
                                        });
                                        vm.$Modal.info({
                                            title: `${params.row.hostname} last 30 min stats(${startTime} - ${endTime})`,
                                            width: 70,
                                            render: (h) => {
                                                let statsTextEl = h("h3", `Health Check Statistic`);
                                                let statsTableEl = h(
                                                    "Table",
                                                    {
                                                        props: {
                                                            data: tableData,
                                                            columns: tableColumns,
                                                        },
                                                    },
                                                    ""
                                                );
                                                let elements = [statsTextEl, statsTableEl];
                                                return elements;
                                            },
                                        });
                                    })
                                    .catch((err) => {
                                        vm.$Modal.info({
                                            title: `${params.row.hostname} last 30 min stats`,
                                            width: 70,
                                            render: (h) => {
                                                let statsTextEl = h("h3", `Health Check Statistic`);
                                                let noDataTextEl = h(
                                                    "p",
                                                    {
                                                        props: {},
                                                    },
                                                    "No Data Yet"
                                                );
                                                let elements = [statsTextEl, noDataTextEl];
                                                return elements;
                                            },
                                        });
                                    });
                            },
                        },
                    },
                    `stats`
                );
                const elements = [p1, p2, p3, statsBtn];
                return h("span", {}, elements);
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "isEnabledExternalCheck",
            title: vm.$i18n.t("slbsetting.isEnabledExternalCheck"),
            sectionKey: "healthCheck",
            description:
                "<p>Testing http response from external probes, response with 200 will treat as healthy. Test runs every 10 seconds, while half of the probes fails, the faulty slb will be taken down from dns resolving. You have to <strong>allow</strong> our probes ip bellow: <ul><li>47.52.251.56</li><li>52.221.227.251</li><li>52.246.164.57</li></ul></p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 140,
            formRender: "FormSwitch",
            defaultVal: false,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "checkTimesAsHealthy",
            title: vm.$i18n.t("slbsetting.checkTimesAsHealthy"),
            sectionKey: "healthCheck",
            description: "<p></p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            inputMaxNum: 29,
            inputMinNum: 1,
            defaultVal: 3,
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "checkTimesAsUnhealthy",
            title: vm.$i18n.t("slbsetting.checkTimesAsUnhealthy"),
            sectionKey: "healthCheck",
            description: "<p></p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInputNumber",
            inputMaxNum: 29,
            inputMinNum: 1,
            defaultVal: 3,
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "usedHealthCheckProbes",
            title: vm.$i18n.t("slbsetting.usedHealthCheckProbes"),
            sectionKey: "healthCheck",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormTransfer",
            maxItemsCount: 30,
            options: [],
            defaultVal: [],
            noOptionsText: "No Available Health Check Probes",
            filter: {
                type: "string",
            },
            remoteOptions: async (queryParams) => {
                let result = await getFormOptions(`/healthcheckprobenames`);
                return result.data.options;
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "externalCheckUrl",
            title: vm.$i18n.t("slbsetting.externalCheckUrl"),
            sectionKey: "healthCheck",
            description:
                "<p>The url to check with. If this is a physical slb instead of virtual one, we provide <strong>http://your-slb-service-ip:5403/100k.bin</strong> for health checking.</p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            minWidth: 140,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.isEnabledExternalCheck && [undefined, ""].includes(value))
                            return callback(new Error("Missing HealthCheck Url"));

                        callback();
                    },
                    trigger: "change",
                },
            ],
            placeholder: "http://your-slb-service-ip:5403/100k.bin",
        },
        {
            searchViews: ["SlbSetting"],
            key: "sourceNat",
            title: vm.$i18n.t("slbsetting.sourceNat"),
            sectionKey: "advanced",
            description:
                "<p>Toggle the switch off, the source IP will not be changed to SLB's IP address when the packet is sent to EDGE.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
            watchField: async (value, item) => {
                vm.$emit("passSourceNatBoolean", value);
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "slbSourceIpPool",
            title: vm.$i18n.t("slbsetting.slbSourceIpPool"),
            sectionKey: "advanced",
            description:
                "<p>Source Ip Pool for connection between slb and edges, make sure you have those IPs on your slbs; <strong>leave it blank if you're not sure</strong></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            vShowFieldVal: () => (vm.formData.isDummySlb == true ? 0 : 1),
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.slbSourceIpPool.split("\n").filter((e) => e.length > 0),
                        maxShow: "5",
                        title: vm.$i18n.t("slbsetting.slbSourceIpPool"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value == undefined || value.length == 0) return callback();
                        let ips = value.split("\n");
                        ips.forEach((e, i) => {
                            let index = ips.findIndex((x) => x == e);
                            if (index !== i) return callback(new Error(`Duplicate: ${e}`));
                        });

                        ipValidate("ips", ips).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "a.b.c.d\nb.b.c.d",
        },
        {
            searchViews: ["GroupResource", "SlbSetting"],
            key: "edges",
            title: vm.$i18n.t("groupresource.edges"),
            showInTable: true,
            minWidth: 400,
            formTypes: ["createForm", "editForm"],
            formRender: "FormTransfer",
            maxItemsCount: 1024,
            options: [],
            defaultVal: [],
            noOptionsText: "沒有可用的EDGE節點",
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "connectState",
            title: vm.$i18n.t("slbsetting.connectState"),
            showInTable: true,
            withForm: false,
            minWidth: tableColumnWidth.connectState,
            formRender: "FormSwitch",
            defaultVal: true,
            sortable: false,
        },
        {
            searchViews: ["SlbSetting"],
            key: "gitSlbSuccess",
            title: vm.$i18n.t("slbsetting.gitSlbSuccess"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                // type: "string"
                type: "boolean",
            },
            render: (h, params) => {
                var iconType = boolToIcons({
                    value: params.row.gitSlbSuccess,
                    falseToDash: false,
                });
                var iconColor = params.row.gitSlbSuccess ? colorNames.green : colorNames.red;
                return !params.row.isDummySlb
                    ? h("Icon", {
                          props: {
                              type: iconType,
                              color: iconColor,
                              size: 24,
                          },
                      })
                    : h("div", "");
            },
        },
        {
            searchViews: ["SlbSetting"],
            key: "outgoingRate",
            title: vm.$i18n.t("slbsetting.outgoingRate"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            defaultVal: true,
            filter: {
                // type: "string"
                // type: "number"
                type: "range",
            },
            render: (h, params) => {
                let outgoingRate = params.row.outgoingRate || 0;
                let outgoingStat = outgoingRateToStatusName(outgoingRate);
                return !params.row.isDummySlb
                    ? h(
                          "Tag",
                          {
                              props: {
                                  color: outgoingStat,
                              },
                          },
                          outgoingRate.toFixed()
                      )
                    : h("div", "");
            },
        },
        {
            searchViews: ["SlbSetting"],
            key: "slbMasterIsRunning",
            title: vm.$i18n.t("slbsetting.slbMasterIsRunning"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            filter: {
                type: "boolean",
            },
            render: (h, params) => {
                const iconType = boolToIcons({
                    value: params.row.slbMasterIsRunning,
                    falseToDash: false,
                });
                const iconColor = params.row.slbMasterIsRunning ? colorNames.green : colorNames.red;
                return h("icon",{
                    props:{
                        type: iconType,
                        color: iconColor,
                        size: 24
                    }
                })
            },
        },
        {
            searchViews: ["SlbSetting"],
            key: "speedLimit",
            title: "Speed Limit",
            sectionKey: "advanced",
            description: "<p>The speed to limit. (KB/s)</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            defaultVal: 0,
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        let errors = value < 1 ? "Speed must greater than 0" : [];
                        callback(errors);
                    },
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["SlbSetting"],
            key: "ipWhiteList",
            title: "IP White List",
            sectionKey: "advanced",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            autosize: inputAutoSize,
            fieldType: "textarea",
            filter: {
                type: "string",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let networks = value.split("\n");
                        networks.forEach((e, i) => {
                            let index = networks.findIndex((x) => x == e);
                            if (index !== i) return callback(new Error(`Duplicate: ${e}`));
                        });

                        ipValidate("network", networks).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "A.B.C.D/netmask\nA.B.C.D/netmask",
        },
        {
            searchViews: ["SlbSetting"],
            key: "ipBlackList",
            title: "IP Black List",
            sectionKey: "advanced",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            autosize: inputAutoSize,
            fieldType: "textarea",
            filter: {
                type: "string",
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let networks = value.split("\n");
                        networks.forEach((e, i) => {
                            let index = networks.findIndex((x) => x == e);
                            if (index !== i) return callback(new Error(`Duplicate: ${e}`));
                        });

                        ipValidate("network", networks).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "A.B.C.D/netmask\nA.B.C.D/netmask",
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting"],
            key: "errMsg",
            title: vm.$i18n.t("slbsetting.errMsg"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "serviceProvideType",
            title: vm.$i18n.t("edgesetting.serviceProvideType"),
            description:
                "<p>The <strong>serviceProvideType</strong> determine this node is protection node or service node.</p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formRender: "FormSelect",
            formTypes: ["editForm", "multiEditForm"],
            options: [
                { label: "Normal", value: "normal" },
                { label: "Protect", value: "protect" },
            ],
            minWidth: 100,
            tooltip: true,
            filter: {
                type: "select",
            },
        },
        {
            searchViews: ["NinjaSetting"],
            key: "serviceProvideType",
            title: vm.$i18n.t("edgesetting.serviceProvideType"),
            description:
                "<p>The <strong>serviceProvideType</strong> determine this node is protection node or service node.</p>",
            withTable: false,
            showInTable: false,
            withForm: true,
            formRender: "FormSelect",
            formTypes: ["editForm", "multiEditForm"],
            options: [
                { label: "Normal", value: "normal" },
                { label: "Protect", value: "protect" },
            ],
            minWidth: 100,
            tooltip: true,
            filter: {
                type: "select",
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "gitSitesSuccess",
            title: vm.$i18n.t("edgesetting.gitSitesSuccess"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                // type: "string"
                type: "boolean",
            },
            render: (h, params) => {
                var iconType = boolToIcons({
                    value: params.row.gitSitesSuccess,
                    falseToDash: false,
                });
                var iconColor = params.row.gitSitesSuccess ? colorNames.green : colorNames.red;
                return h("Icon", {
                    props: {
                        type: iconType,
                        color: iconColor,
                        size: 24,
                    },
                });
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "nginxIsRunning",
            title: vm.$i18n.t("edgesetting.nginxIsRunning"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                // type: "string"
                type: "boolean",
            },
            render: (h, params) => {
                var iconType = boolToIcons({
                    value: params.row.nginxIsRunning,
                    falseToDash: false,
                });
                var iconColor = params.row.nginxIsRunning ? colorNames.green : colorNames.red;
                return h("Icon", {
                    props: {
                        type: iconType,
                        color: iconColor,
                        size: 24,
                    },
                });
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "nginxMasterIsRunning",
            title: vm.$i18n.t("edgesetting.nginxMasterIsRunning"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            filter: {
                type: "boolean",
            },
            render: (h, params) => {
                let iconType = boolToIcons({
                    value: params.row.nginxMasterIsRunning,
                    falseToDash: false,
                });
                let iconColor = params.row.nginxMasterIsRunning ? colorNames.green : colorNames.red;
                return h("Icon", {
                    props: {
                        type: iconType,
                        color: iconColor,
                        size: 24,
                    },
                });
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "lagLogBytes",
            title: vm.$i18n.t("edgesetting.lagLogBytes"),
            showInTable: true,
            withForm: false,
            sortable: true,
            render: (h, params) => {
                return h("p", `${params.row.lagLogBytes} bytes`);
            },
        },
        {
            searchViews: ["EdgeSetting", "NinjaSetting"],
            key: "errMsg",
            title: vm.$i18n.t("edgesetting.errMsg"),
            showInTable: false,
            withForm: false,
            minWidth: 100,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "isEnableProxyProtocol",
            title: vm.$i18n.t("edgesetting.isEnableProxyProtocol"),
            sectionKey: "essential",
            description: "<p>Toggle the switch on, EDGE will expect Proxy Protocol header from SLB.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                type: "boolean",
            },
        },
        {
            searchViews: ["EdgeSetting"],
            key: "usedBySlbs",
            title: vm.$i18n.t("groupresource.slbs"),
            showInTable: false,
            withForm: false,
            minWidth: 400,
            render: (h, params) => {
                return h(
                    "div",
                    params.row.usedBySlbs
                        .slice()
                        .sort()
                        .map(function (s) {
                            let serviceProvideTypeTag = s.serviceProvideType == "normal" ? "N" : "P";
                            return h(
                                "Tag",
                                {
                                    props: {
                                        color: s.runningStat,
                                    },
                                },
                                `${s.hostname}(${serviceProvideTypeTag}): ${s.serviceIP} ${s.line}`
                            );
                        })
                );
            },
            sortable: false,
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["NinjaSetting"],
            key: "gitNinjaSuccess",
            title: vm.$i18n.t("edgesetting.gitSitesSuccess"),
            showInTable: true,
            withForm: false,
            minWidth: 160,
            formRender: "FormSwitch",
            defaultVal: true,
            filter: {
                // type: "string"
                type: "boolean",
            },
            render: (h, params) => {
                var iconType = boolToIcons({
                    value: params.row.gitNinjaSuccess,
                    falseToDash: false,
                });
                var iconColor = params.row.gitNinjaSuccess ? colorNames.green : colorNames.red;
                return h("Icon", {
                    props: {
                        type: iconType,
                        color: iconColor,
                        size: 24,
                    },
                });
            },
        },
        // {
        //     searchViews: ["NinjaSetting"],
        //     key: "ninjaIsRunning",
        //     title: vm.$i18n.t("ninjasetting.ninjaIsRunning"),
        //     showInTable: true,
        //     withForm: false,
        //     minWidth: 100,
        //     formRender: "FormSwitch",
        //     defaultVal: true,
        //     filter: {
        //         // type: "string"
        //         type: "boolean"
        //     },
        //     render: (h, params) => {
        //         var iconType = boolToIcons({
        //             value: params.row.ninjaIsRunning,
        //             falseToDash: false
        //         });
        //         var iconColor = params.row.ninjaIsRunning ? colorNames.green : colorNames.red;
        //         return h("Icon", {
        //             props: {
        //                 type: iconType,
        //                 color: iconColor,
        //                 size: 24
        //             }
        //         });
        //     }
        // },
        {
            searchViews: ["ProbeSetting"],
            key: "serviceIP",
            title: vm.$i18n.t("probesetting.serviceIP"),
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            minWidth: tableColumnWidth.serviceIP,
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["UsersManage", "SystemLog", "Tasks"],
            key: "username",
            title: vm.$i18n.t("usersmanage.userName"),
            showInTable: true,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["UsersManage"],
            key: "password",
            title: vm.$i18n.t("usersmanage.passWord"),
            description: "<p>The password of the user</p>",
            withTable: false,
            withForm: true,
            formTypes: ["createForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    required: true,
                    message: "password 不能為空",
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["UsersManage"],
            key: "email",
            title: vm.$i18n.t("usersmanage.emailAddress"),
            description: "<p>The email of the user.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            tooltip: true,
            formRender: "FormInput",
            defaultVal: "",
            filter: {
                type: "string",
            },
            placeholder: "",
            validate: [
                { required: true, message: "email 不能為空", trigger: "blur" },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.indexOf("@") == -1) return callback(new Error("Invalid Email"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["UsersManage"],
            key: "lastModifiedPasswordDate",
            title: vm.$i18n.t("usersmanage.lastModifiedPasswordDate"),
            description: "",
            showInTable: true,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                if (params.row.lastModifiedPasswordDate !== undefined) {
                    return h("div", _dateFormat(params.row.lastModifiedPasswordDate, "YYYY-MM-DD HH:mm:ss"));
                }
            },
        },
        {
            searchViews: ["UsersManage"],
            key: "lastModifiedOtpTokenDate",
            title: vm.$i18n.t("usersmanage.lastModifiedOtpTokenDate"),
            showInTable: true,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                if (params.row.lastModifiedOtpTokenDate !== undefined) {
                    return h("div", _dateFormat(params.row.lastModifiedOtpTokenDate, "YYYY-MM-DD HH:mm:ss"));
                }
            },
        },
        {
            searchViews: ["UsersManage"],
            key: "trustedIpAndDevice",
            title: vm.$i18n.t("usersmanage.trustedIpAndDevice"),
            showInTable: true,
            minWidth: 400,
            render: (h, params) => {
                if (params.row.userAllowEquipList !== undefined) {
                    return h(
                        "div",
                        params.row.userAllowEquipList
                            .slice()
                            .sort()
                            .map(function (e) {
                                return h("p", `IP: ${e.ip} - Device: ${e["user-agent"]}`);
                            })
                    );
                }
            },
        },
        {
            searchViews: ["UsersManage"],
            key: "loginInfos",
            title: vm.$i18n.t("usersmanage.userLoginAndLogoutRecords"),
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                if (!params.row.loginInfos) return h("div", "");
                return h("loginAndLogoutRecordsInfosBtn", {
                    props: { loginData: params.row.loginInfos, logoutData: params.row.logoutInfos },
                });
            },
            exportOptions: {
                canExport: false,
                callback: (value) => value,
            },
            sortable: false,
        },
        {
            searchViews: ["UsersManage"],
            key: "privilege",
            title: vm.$i18n.t("usersmanage.privilege"),
            description: "<p>The privilege of the user.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            minWidth: 100,
            tooltip: true,
            formRender: "FormSelect",
            options: ["normaluser", "admin"],
            defaultVal: "normaluser",
            filter: {
                type: "string",
            },
        },
        // {
        //     searchViews: ["UsersManage"],
        //     key: "groupName",
        //     width: tableColumnWidth.groupName,
        //     title: vm.$i18n.t("usersmanage.groupName"),
        //     description: "<p>The group of this user to assign.</p>",
        //     showInTable: true,
        //     withForm: true,
        //     formTypes: ["createForm"],
        //     formRender: "FormSelect",
        //     options: [],
        //     defaultVal: "",
        //     filter: {
        //         type: "string"
        //     },
        //     vShowFieldVal: () => 1,
        //     validate: [
        //         {
        //             required: true,
        //             message: "group 不能為空",
        //             trigger: "blur"
        //         }
        //     ]
        // },
        {
            searchViews: ["UsersManage"],
            key: "inGroupNames",
            minWidth: tableColumnWidth.inGroupNames,
            title: vm.$i18n.t("usersmanage.inGroupNames"),
            description: "<p>The groups of this user to assign.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: false,
            options: [],
            defaultVal: "",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "At least one group should be assigned",
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["UsersManage"],
            key: "inGroupsRole",
            minWidth: 200,
            title: vm.$i18n.t("usersmanage.inGroupNames"),
            description: "<p>The groups of this user to assign.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: () => import("@/components/FormItems/GroupsRoleSelector"),
            options: [],
            defaultVal: [],
            filter: {
                type: "string",
                searchKey: "inGroupsRole.groupName",
                defaultValue: vm.$store.state.queryGroupName == "all" ? "*" : vm.$store.state.queryGroupName,
            },
            filterDisabled: vm.$store.state.queryGroupName !== "all",
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "At least one group should be assigned",
                    trigger: "change",
                },
            ],
            render: (h, params) => {
                const inGroupsRole = sortGroupRoles(params.row.inGroupsRole);
                const lis = inGroupsRole.map((groupRole) =>
                    h(
                        "li",
                        {
                            style: {
                                "list-style": "none",
                            },
                        },
                        [
                            h("span", {}, groupRole.groupName),
                            h(
                                "span",
                                {
                                    style: {
                                        float: "right",
                                        color: "rgb(204, 204, 204)",
                                    },
                                },
                                groupRole.roleName
                            ),
                        ]
                    )
                );

                return lis;
            },
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    value = assignDefaultValue(value, []);
                    return value.map((groupRole) => `${groupRole.groupName}: ${groupRole.roleName}`);
                },
            },
            sortable: false,
        },
        {
            searchViews: ["UsersManage"],
            key: "userRoleName",
            minWidth: 180,
            title: vm.$i18n.t("rolesmanage.roleName"),
            // description: "<p>The groups of this user to assign.</p>",
            showInTable: true,
            withForm: false,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            options: [],
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.roleName = `*${queryValue}*`;
                queryParams.groupName = vm.$store.state.queryGroupName;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/rolenames?${queryString}`);
                return result.data.options;
            },
            defaultVal: "",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                },
            ],
        },
        //增加isUseUserAllowIp的switch
        {
            searchViews: ["UsersManage"],
            key: "isUseUserAllowIp",
            title: vm.$i18n.t("usersmanage.isUseUserAllowIp"),
            sectionKey: "conditionIdentity",
            description:
                "<p>Toggle the switch on to allow a list of IP addresses in the firewall to authenticate users.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSwitch",
            defaultVal: false,
            type: "boolean",
        },
        //增加userAllowIp的input
        {
            searchViews: ["UsersManage"],
            key: "userAllowIp",
            title: vm.$i18n.t("usersmanage.userAllowIp"),
            sectionKey: "conditionIdentity",
            description:
                "<p>Setup multiple IPs that are allowed access to your service (by valid network format, e.g. a.b.c.d/netmask). Multiple IPs can separate with <strong>newline</strong>.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "0.0.0.0/0",
            autosize: inputAutoSize,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("StringList", {
                    props: {
                        listData: params.row.userAllowIp
                            ? params.row.userAllowIp.split("\n").filter((e) => e.length > 0)
                            : [],
                        maxShow: "5",
                        title: vm.$i18n.t("usersmanage.userAllowIp"),
                        width: "350",
                    },
                });
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) return callback();
                        let ipsValidation = value.split("\n");
                        ipsValidation.forEach((e, i) => {
                            let index = ipsValidation.findIndex((x) => x == e);
                            if (index !== i) return callback(new Error(`Duplicate: ${e}`));
                            if (/[\s]/.test(e)) return callback(new Error("invalid: " + e));
                        });
                        ipValidate("ipsValidation", ipsValidation).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            placeholder: "A.B.C.D/netmask\nA.B.C.D/netmask",
            vShowFieldVal: (item) => (item.isUseUserAllowIp ? 1 : 0),
        },
        {
            searchViews: ["Tasks"],
            key: "submitTime",
            title: vm.$i18n.t("tasks.submitTime"),
            showInTable: true,
            minWidth: 250,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.submitTime, "YYYY-MM-DD HH:mm:ss.SSS"));
            },
        },
        {
            searchViews: ["UsersManage"],
            key: "reset",
            title: vm.$i18n.t("usersmanage.reset"),
            minWidth: 100,
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                var dropdownItems = [
                    {
                        value: "otpReset",
                        title: vm.$i18n.t("usersmanage.otpReset"),
                        apiAction: resendOtpMail,
                        modalTitle: vm.$i18n.t("usersmanage.otpReset"),
                        modalContent: vm.$i18n.t("usersmanage.otpResetText"),
                    },
                    {
                        value: "pwdReset",
                        title: vm.$i18n.t("usersmanage.pwdReset"),
                        apiAction: resetPwdMail,
                        modalTitle: vm.$i18n.t("usersmanage.pwdReset"),
                        modalContent: vm.$i18n.t("usersmanage.pwdResetText"),
                    },
                ];
                if (!isAdmin) {
                    dropdownItems.push({
                        value: "removeFromGroup",
                        title: vm.$i18n.t("usersmanage.removeFromGroup"),
                        apiAction: removeFromGroup,
                        modalTitle: vm.$i18n.t("usersmanage.removeFromGroup"),
                        modalContent: vm.$i18n.t("usersmanage.removeFromGroupText"),
                    });
                }
                return h(
                    "Dropdown",
                    {
                        props: {
                            trigger: "click",
                            placement: "left",
                            transfer: true,
                        },
                        on: {
                            "on-click": (value) => {
                                var currentItem = dropdownItems.find((e) => e.value == value);
                                vm.$Modal.confirm({
                                    title: currentItem.modalTitle,
                                    content: currentItem.modalContent,
                                    okText: vm.$i18n.t("rcmtable.confirmText"),
                                    cancelText: vm.$i18n.t("rcmtable.cancelText"),
                                    onOk: () => {
                                        currentItem
                                            .apiAction(params.row)
                                            .then((result) => {
                                                vm.$Notice.success({
                                                    title: vm.$i18n.t("global.success"),
                                                    desc: `${result.data.message}`,
                                                });
                                            })
                                            .catch(function (error) {
                                                vm.$Notice.warning({
                                                    title: vm.$i18n.t("global.error"),
                                                    desc: `${error}`,
                                                });
                                                // vm.getAllData();
                                            });
                                    },
                                });
                            },
                        },
                    },
                    [
                        h("a", [
                            h("Button", {
                                props: {
                                    shape: "circle",
                                    icon: "md-more",
                                    loading: vm.tableBtnClass.optBtnIsLoading[params.index],
                                    key: params.index,
                                    disabled: vm.isEditBtnDisabled,
                                },
                            }),
                        ]),
                        h(
                            "DropdownMenu",
                            {
                                slot: "list",
                            },
                            [
                                dropdownItems.map(function (item) {
                                    return h(
                                        "DropdownItem",
                                        {
                                            props: {
                                                name: item.value,
                                            },
                                        },
                                        item.title
                                    );
                                }),
                            ]
                        ),
                    ]
                );
            },
            sortable: false,
        },
        {
            searchViews: ["Tasks"],
            key: "actionName",
            title: vm.$t("tasks.actionName"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "objId",
            title: vm.$t("tasks.objId"),
            minWidth: 160,
            showInTable: true,
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.objId,
                        maxShow: "5",
                        title: vm.$t("tasks.objId"),
                        width: "350",
                    },
                });
            },
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "decodeObjId",
            title: vm.$t("tasks.decodeObjId"),
            minWidth: 160,
            showInTable: true,
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.decodeObjId,
                        maxShow: "5",
                        title: vm.$t("tasks.decodeObjId"),
                        width: "350",
                    },
                });
            },
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "request_source",
            title: vm.$t("tasks.request_source"),
            minWidth: 100,
            showInTable: true,
            render: (h, params) => {
                return h("p", {}, params.row.request_source);
            },
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "diffData",
            title: vm.$t("tasks.diffData"),
            showInTable: true,
            tooltip: true,
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: JSON.stringify(
                            diff(JSON.parse(params.row.originData), JSON.parse(params.row.requestData))
                        ),
                        title: vm.$t("tasks.diffData"),
                        buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                        buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                    },
                });
            },
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["Tasks"],
            key: "originData",
            title: vm.$t("tasks.originData"),
            showInTable: true,
            tooltip: true,
            render: (h, params) => {
                return JSON.parse(params.row.originData).length > 0
                    ? h("JsonPoptip", {
                          props: {
                              jsonData: params.row.originData,
                              title: vm.$t("tasks.originData"),
                              buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                              buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                          },
                      })
                    : [];
            },
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["Tasks"],
            key: "requestData",
            title: vm.$t("tasks.requestData"),
            showInTable: true,
            tooltip: true,
            render: (h, params) => {
                return JSON.parse(params.row.requestData).length > 0
                    ? h("JsonPoptip", {
                          props: {
                              jsonData: params.row.requestData,
                              title: vm.$t("tasks.requestData"),
                              buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                              buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                          },
                      })
                    : [];
            },
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["Tasks"],
            key: "scope",
            title: vm.$t("tasks.scope"),
            showInTable: true,
            tooltip: true,
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: params.row.scope,
                        title: vm.$t("tasks.scope"),
                        buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                        buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                    },
                });
            },
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["Tasks"],
            key: "scopeResult",
            title: vm.$t("tasks.scopeResult"),
            showInTable: true,
            tooltip: true,
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: params.row.scopeResult,
                        title: vm.$t("tasks.scopeResult"),
                        buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                        buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                    },
                });
            },
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["Tasks"],
            key: "runCounter",
            title: vm.$t("tasks.runCounter"),
            showInTable: true,
            minWidth: 80,
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "taskNo",
            title: vm.$t("tasks.taskNo"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["Tasks"],
            key: "trainNo",
            title: vm.$t("tasks.trainNo"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["Tasks"],
            key: "state",
            title: vm.$t("tasks.state"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "errorMsg",
            title: vm.$t("tasks.errorMsg"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["Tasks"],
            key: "finishedTime",
            title: vm.$t("tasks.finishedTime"),
            showInTable: true,
            minWidth: 250,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.finishedTime, "YYYY-MM-DD HH:mm:ss.SSS"));
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "queueState",
            title: vm.$t("certsApply.queueState"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["CertsApply"],
            key: "serverName",
            title: vm.$t("certsApply.serverName"),
            sectionKey: "essential",
            description: "<p>Add Domains.</p>",
            formTypes: ["createForm"],
            withTable: true,
            withForm: true,
            showInTable: true,
            selectAllowCreate: true,
            sortable: false,
            minWidth: 180,
            filter: {
                type: "string",
            },
            formRender: "FormSelect",
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.domain = `*${queryValue}*`;
                let groupName = vm.formData.groupName;
                queryParams.groupName = groupName;
                queryParams.indexName = "rcm-nginxconfigs";
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/groupby/domains?${queryString}`);
                return result.data.options;
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    message: `domain ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (/[*_]/.test(value)) return callback(new Error(value + " includes one of *_"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsApply"],
            key: "fastEntry",
            align: "center",
            title: vm.$i18n.t("certsApply.fastEntry"),
            showInTable: true,
            withForm: false,
            minWidth: 120,
            defaultVal: false,
            render: (h, params) => {
                try {
                    let fastEntry = params.row.fastEntry;
                    var iconType = boolToIcons({
                        value: fastEntry,
                        falseToDash: false,
                    });
                    var iconColor = boolToColors({
                        value: fastEntry,
                        falseToGrey: true,
                    });
                    return h("Icon", {
                        props: {
                            type: iconType,
                            color: iconColor,
                            size: 24,
                        },
                    });
                } catch (error) {
                    // 無快速通關狀態不給值
                    // console.log(`group:${params.row.groupName} paymentStatus error:, ${error.message}`);
                    return h();
                }
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "sslProvider",
            title: vm.$t("certsApply.sslProvider"),
            showInTable: true,
            minWidth: 160,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "requestSSLOrderNumber",
            title: vm.$t("certsApply.requestSSLOrderNumber"),
            showInTable: true,
            minWidth: 110,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "requestSSLState",
            title: vm.$t("certsApply.requestSSLState"),
            showInTable: true,
            minWidth: 100,
            sortable: false,
        },
        {
            searchViews: ["CertsApply"],
            key: "collectSSLState",
            title: vm.$t("certsApply.collectSSLState"),
            showInTable: true,
            minWidth: 100,
            sortable: false,
        },
        {
            searchViews: ["CertsApply"],
            key: "requestAttemptCount",
            title: vm.$t("certsApply.requestAttemptCount"),
            showInTable: true,
            minWidth: 110,
            filter: {
                type: "range",
            },
            render: (h, params) => {
                return h(
                    "p",
                    {
                        style: {
                            color:
                                params.row.requestAttemptCount < 10
                                    ? colorNames.green
                                    : params.row.requestAttemptCount < 20 && params.row.requestAttemptCount >= 10
                                    ? colorNames.orange
                                    : colorNames.red,
                        },
                    },
                    params.row.requestAttemptCount
                );
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "collectAttemptCount",
            title: vm.$t("certsApply.collectAttemptCount"),
            showInTable: true,
            minWidth: 110,
            filter: {
                type: "range",
            },
            render: (h, params) => {
                return h(
                    "p",
                    {
                        style: {
                            color:
                                params.row.collectAttemptCount < 10
                                    ? colorNames.green
                                    : params.row.collectAttemptCount < 20 && params.row.collectAttemptCount >= 10
                                    ? colorNames.orange
                                    : colorNames.red,
                        },
                    },
                    params.row.collectAttemptCount
                );
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "requestSSLErrMsg",
            title: vm.$t("certsApply.requestSSLErrMsg"),
            showInTable: true,
            minWidth: 140,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: params.row.requestSSLErrMsg,
                        title: vm.$t("certsApply.requestSSLErrMsg"),
                        buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                        buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                    },
                });
            },
            sortable: false,
        },
        {
            searchViews: ["CertsApply"],
            key: "collectSSLErrMsg",
            title: vm.$t("certsApply.collectSSLErrMsg"),
            showInTable: true,
            minWidth: 140,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: params.row.collectSSLErrMsg,
                        title: vm.$t("certsApply.collectSSLErrMsg"),
                        buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                        buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                    },
                });
            },
            sortable: false,
        },
        {
            searchViews: ["CertsApply"],
            key: "taskAction",
            title: vm.$t("certsApply.taskAction"),
            showInTable: true,
            minWidth: 110,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "addToQueueDate",
            title: vm.$t("certsApply.addToQueueDate"),
            showInTable: true,
            minWidth: 210,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.addToQueueDate, "YYYY-MM-DD HH:mm:ss.SSS"));
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "requestSSLDate",
            title: vm.$t("certsApply.requestSSLDate"),
            showInTable: true,
            minWidth: 210,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.requestSSLDate, "YYYY-MM-DD HH:mm:ss.SSS"));
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "collectSSLDate",
            title: vm.$t("certsApply.collectSSLDate"),
            showInTable: true,
            minWidth: 210,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.collectSSLDate, "YYYY-MM-DD HH:mm:ss.SSS"));
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "requestSSLlValidateFileName",
            title: vm.$t("certsApply.requestSSLlValidateFileName"),
            showInTable: true,
            minWidth: 150,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["CertsApply"],
            key: "domains",
            title: vm.$t("certsApply.serverName"),
            sectionKey: "essential",
            description: "<p>Add Domain.</p>",
            formTypes: ["editForm"],
            minWidth: 140,
            formRender: "FormSelect",
            withTable: true,
            withForm: true,
            showInTable: true,
            multiple: true,
            selectAllowCreate: true,
            multiSelectWithToolbox: true,
            options: [],
            defaultVal: [],
            filter: {
                type: "select",
                defaultValue: [],
            },
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.domain = `*${queryValue}*`;
                queryParams.groupName = vm.$store.state.queryGroupName;
                queryParams.indexName = "rcm-nginxconfigs";
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/groupby/domains?${queryString}`);
                return result.data.options;
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["CertsApply"],
            key: "domainFilter",
            title: "domain filter",
            showInTable: false,
            filterDisabled: false,
            filter: {
                searchKey: "id",
                type: "string",
                defaultValue: vm.esIndexFields ? vm.esIndexFields.domainFilter : "",
            },
        },
        {
            searchViews: ["WafEvent"],
            key: "req_id",
            fixed: "left",
            title: vm.$t("wafevent.req_id"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "server_name",
            title: vm.$t("wafevent.server_name"),
            showInTable: true,
            minWidth: tableColumnWidth.serverName,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "group",
            minWidth: tableColumnWidth.groupName,
            title: vm.$t("wafevent.group"),
            showInTable: false,
            filter: {
                type: "string",
                defaultValue: vm.$store.state.queryGroupName == "all" ? "*" : vm.$store.state.queryGroupName,
            },
            filterDisabled: vm.$store.state.queryGroupName !== "all",
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "servicegroup",
            minWidth: tableColumnWidth.serviceGroupName,
            title: vm.$t("wafevent.servicegroup"),
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "host",
            title: "host",
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "tb_action",
            title: vm.$t("wafevent.tb_action"),
            showInTable: true,
            minWidth: 180,
            options: [
                { label: "Allow", value: "00" },
                { label: "Deny", value: "01" },
                { label: "Gesture", value: "03" },
                { label: "Speed", value: "05" },
                { label: "Redirect", value: "06" },
                { label: "Cookie", value: "07" },
                { label: "Period", value: "08" },
                { label: "Set request headers", value: "09" },
                {
                    label: "Set response headers",
                    value: "10",
                },
                {
                    label: "Log only",
                    value: "11",
                },
                {
                    label: "Custom Page",
                    value: "12",
                },
                {
                    label: "Origin Policy",
                    value: "13",
                },
                {
                    label: "Smart Captcha",
                    value: "14",
                },
                {
                    label: "Hotlink",
                    value: "16",
                },
            ],
            filter: {
                type: "select",
            },
            render: (h, params) => {
                let explain = wafActionToString(params.row.tb_action);
                let wafValue = wafCodeToValue(params.row.tb_action);
                const clearWafBtn = h("ClearWafBtn", {
                    props: {
                        shape: "circle",
                        bucketData: {
                            groupName: params.row.group,
                            section: wafValue,
                            servername: params.row.server_name,
                            wafrule: params.row.tb_rule,
                            ip: params.row.remote_addr,
                        },
                    },
                });
                let extendBtns = [];
                if (["vaptcha", "bucket", "captcha"].includes(wafValue)) {
                    extendBtns.push(clearWafBtn);
                }
                return h("div", [
                    h(
                        "Tooltip",
                        {
                            props: {
                                placement: "top",
                                transfer: true,
                                content: params.row.tb_action,
                            },
                        },
                        [
                            h(
                                "div",
                                {
                                    attrs: {},
                                    style: {
                                        width: "120px",
                                        height: "24px",
                                    },
                                },
                                explain
                            ),
                        ]
                    ),
                    extendBtns,
                ]);
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "tb_type",
            title: vm.$t("wafevent.tb_type"),
            showInTable: false,
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "tb_rule",
            title: vm.$t("wafevent.tb_rule"),
            showInTable: false,
            minWidth: 100,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "tb_reason",
            title: vm.$t("wafevent.tb_reason"),
            showInTable: true,
            filter: {
                type: "string",
            },
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    // if (value.includes("[" && "]")) {
                    //     value = JSON.parse(value);
                    // }
                    return value;
                },
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "cus_realy_addr",
            title: vm.$t("wafevent.cus_realy_addr"),
            showInTable: true,
            filter: {
                type: "string",
                defaultValue: vm.$route.query.cus_realy_addr,
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "country",
            title: vm.$t("wafevent.country"),
            showInTable: true,
            minWidth: 100,
            filter: {
                type: "string",
            },
            render: (h, params) => {
                let svg = `${params.row.country.toLowerCase()}.svg`;
                return h("div", [
                    h(
                        "Tooltip",
                        {
                            props: {
                                placement: "top",
                                transfer: true,
                                content: params.row.country,
                            },
                        },
                        [
                            params.row.country !== "-"
                                ? h(
                                      "img",
                                      {
                                          attrs: {
                                              src: `/static/icons/flags/${svg}`,
                                          },
                                          style: {
                                              width: "24px",
                                              height: "16px",
                                          },
                                      },
                                      params.row.country
                                  )
                                : h(
                                      "div",
                                      {
                                          style: {
                                              width: "24px",
                                              height: "16px",
                                          },
                                      },
                                      "N/A"
                                  ),
                        ]
                    ),
                ]);
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "request_method",
            title: vm.$t("wafevent.request_method"),
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "request_time",
            title: vm.$t("wafevent.request_time"),
            showInTable: false,
            filter: {
                type: "range",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "upstream_response_time",
            title: vm.$t("wafevent.upstream_response_time"),
            minWidth: 220,
            showInTable: true,
            filter: {
                type: "range",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "server_protocol",
            title: "Server Protocol",
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "upstream_status",
            title: "Upstream Status",
            showInTable: true,
            minWidth: 220,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "server_port",
            title: vm.$t("wafevent.server_port"),
            showInTable: false,
            minWidth: 80,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "uri",
            title: vm.$t("wafevent.uri"),
            showInTable: false,
            minWidth: 200,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "args",
            title: vm.$t("wafevent.args"),
            showInTable: false,
            minWidth: 200,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "request_url",
            title: vm.$t("wafevent.request_url"),
            showInTable: true,
            minWidth: 200,
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "req_headers",
            title: vm.$t("wafevent.req_headers"),
            showInTable: true,
            minWidth: 100,
            fieldType: "textarea",
            defaultVal: "",
            autosize: inputAutoSize,
            filter: {
                type: "object",
            },
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: params.row.req_headers,
                        title: vm.$t("wafevent.req_headers"),
                        buttonText: vm.$store.getters.getJsonPopTipConfig.withIcon ? "" : "顯示",
                        buttonIcon: vm.$store.getters.getJsonPopTipConfig.withIcon ? "md-eye" : "",
                    },
                });
            },
            exportOptions: {
                canExport: true,
                callback: (value) => {
                    try {
                        const result = [];
                        for (var prop in value) {
                            result.push(prop + ":" + value[prop]);
                        }
                        return result.join(",");
                    } catch (err) {
                        console.log(err);
                        return value;
                    }
                },
            },
            sortable: false,
            placeholder: "user-agent:Bad Agent$\nhost:mysite\\.com$\nreferer:^(?!^mysite.com$)",
        },
        {
            searchViews: ["WafEvent", "SystemLog"],
            key: "timestamp",
            title: vm.$t("wafevent.timestamp"),
            showInTable: true,
            minWidth: 220,
            filter: {
                type: "date",
            },
            render: (h, params) => {
                return h("div", _dateFormat(params.row.timestamp, "YYYY-MM-DD HH:mm:ss.SSS"));
            },
            sortable: false,
            exportOptions: {
                canExport: true,
                callback: (value) => _dateFormat(value, "YYYY-MM-DD HH:mm:ss.SSS"),
            },
        },
        {
            searchViews: ["WafEvent"],
            key: "proxyType",
            title: "proxyType",
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "isp",
            title: "isp",
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "originHostResolve",
            title: "originHostResolve",
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "raw_uri",
            title: vm.$t("wafevent.rawUri"),
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "raw_args",
            title: vm.$t("wafevent.rawArgs"),
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
        },
        {
            searchViews: ["WafEvent"],
            key: "ssl_ja3",
            title: vm.$t("wafevent.ssl_ja3"),
            withTable: true,
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
            render: (h, params) => {
                return h("p", {}, params.row.ssl_ja3 || "");
            },
        },
        {
            searchViews: ["WafEvent"],
            key: "ssl_ja3_hash",
            title: vm.$t("wafevent.ssl_ja3_hash"),
            withTable: true,
            showInTable: false,
            filter: {
                type: "string",
            },
            sortable: false,
            render: (h, params) => {
                return h("p", {}, params.row.ssl_ja3_hash || "");
            },
        },
        {
            searchViews: ["CertsManage"],
            key: "certpemUpload",
            title: vm.$i18n.t("certsmanage.certpemUpload"),
            description: "<p>Upload your cert here.</p>",
            showInTable: false,
            withForm: true,
            isFormSlot: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "FormUpload",
            uploadEndPoint: "certpem",
            fieldType: "textarea",
            defaultVal: "",
            handleUpload: {
                format: ["pem", "crt", "cer"],
                // 上傳成功後更新的欄位
                successUpdateFields: [
                    "commonName",
                    "issuerCommonName",
                    "sanDns",
                    "validityStart",
                    "validityEnd",
                    "emailAddress",
                    "certpemRaw",
                ],
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.certpemRaw")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "certkeyUpload",
            title: vm.$i18n.t("certsmanage.certkeyUpload"),
            description: "<p>Upload your private key here.</p>",
            showInTable: false,
            withForm: true,
            isFormSlot: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            minWidth: 60,
            formRender: "FormUpload",
            uploadEndPoint: "certkey",
            fieldType: "textarea",
            defaultVal: "",
            handleUpload: {
                format: ["key"],
                successUpdateFields: ["certkeyRaw"],
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.certkeyRaw")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "commonName",
            title: vm.$i18n.t("certsmanage.commonName"),
            description:
                "<p>This is the commonName of your cert, the system will determine whether the uploaded cert can be used base on this field.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            disabled: true,
            minWidth: 160,
            defaultVal: "",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.commonName")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "sanDns",
            title: vm.$i18n.t("certsmanage.sanDns"),
            description: "<p>If your cert has sanDns, it will show over here.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            // formRender: "FormInput",
            formRender: "TextOnly",
            disabled: true,
            formRenderTextFormat: {
                filter: "arrayToString",
            },
            minWidth: 160,
            defaultVal: "",
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.sanDns || [],
                        maxShow: "1",
                        title: vm.$i18n.t("certsmanage.sanDns"),
                        width: "350",
                    },
                });
            },
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["CertsManage"],
            key: "issuerCommonName",
            title: vm.$i18n.t("certsmanage.issuerCommonName"),
            description: "<p>The issuer of your cert.</p>",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            disabled: true,
            minWidth: 180,
            defaultVal: "",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.issuerCommonName")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "validityStart",
            title: vm.$i18n.t("certsmanage.validityStart"),
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            disabled: true,
            formRenderTextFormat: {
                filter: "dateFormat",
                format: "YYYY-MM-DD HH:mm:ss",
            },
            minWidth: 200,
            defaultVal: "",
            filter: {
                type: "date",
            },
            vShowFieldVal: () => 1,
            render: (h, params) => {
                return h("div", _dateFormat(params.row.validityStart, "YYYY-MM-DD HH:mm:ss"));
            },
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.validityStart")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "validityEnd",
            title: vm.$i18n.t("certsmanage.validityEnd"),
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRenderTextFormat: {
                filter: "dateFormat",
                format: "YYYY-MM-DD HH:mm:ss",
            },
            formRender: "FormInput",
            disabled: true,
            minWidth: 200,
            defaultVal: "",
            filter: {
                type: "date",
            },
            vShowFieldVal: () => 1,
            render: (h, params) => {
                return h("div", _dateFormat(params.row.validityEnd, "YYYY-MM-DD HH:mm:ss"));
            },
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("certsmanage.validityEnd")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "expiredDays",
            title: vm.$i18n.t("certsmanage.expiredDays"),
            showInTable: true,
            withForm: false,
            formTypes: [],
            formRender: "",
            minWidth: 180,
            defaultVal: "",
            vShowFieldVal: () => 1,
            render: (h, params) => {
                return h(
                    "p",
                    {
                        style: {
                            color:
                                params.row.expiredDays > 30
                                    ? colorNames.green
                                    : params.row.expiredDays <= 30 && params.row.expiredDays > 0
                                    ? colorNames.orange
                                    : colorNames.red,
                        },
                    },
                    `${params.row.expiredDays} Days Left`
                );
            },
            sortable: false,
        },
        {
            searchViews: ["CertsManage"],
            key: "emailAddress",
            title: vm.$i18n.t("certsmanage.emailAddress"),
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            disabled: true,
            defaultVal: "",
            filter: {
                type: "string",
            },
            vShowFieldVal: () => 1,
        },
        {
            searchViews: ["CertsManage"],
            key: "certpemRaw",
            title: vm.$i18n.t("certsmanage.certpemRaw"),
            withTable: false,
            showInTable: false,
            sortable: false,
        },
        {
            searchViews: ["CertsManage"],
            key: "certkeyRaw",
            title: vm.$i18n.t("certsmanage.certkeyRaw"),
            withTable: false,
            showInTable: false,
            sortable: false,
        },
        {
            searchViews: ["CertsManage"],
            key: "certRaw",
            title: vm.$i18n.t("certsmanage.certRaw"),
            showInTable: true,
            minWidth: 120,
            render: (h, params) => {
                return params.row.keypairname.endsWith("_acme") &&
                    !vm.$can("other", "domainManagePermission", "CertsUpload", "downloadSystemCerts")
                    ? h("p")
                    : h("DownloadCertBtn", {
                          props: {
                              data: params.row,
                              fileName: `${params.row.keypairname}.zip`,
                          },
                      });
            },
            sortable: false,
        },
        // SystemLog
        {
            searchViews: ["SystemLog"],
            key: "category",
            title: vm.$i18n.t("systemLog.category"),
            showInTable: true,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["SystemLog"],
            key: "level",
            title: vm.$i18n.t("systemLog.level"),
            showInTable: true,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["SystemLog"],
            key: "message",
            title: vm.$i18n.t("systemLog.message"),
            showInTable: true,
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["SystemLog"],
            key: "remoteip",
            title: "IP",
            showInTable: true,
            filter: {
                type: "string",
            },
        },

        /*Alert Rules*/
        {
            searchViews: ["AlertRules"],
            key: "alertRuleQuery",
            title: vm.$i18n.t("alertrules.alertRuleQuery"),
            sectionKey: "essential",
            description: "<p>Query <strong>anything</strong> from the events page</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            defaultVal: "timestamp: [now-10m TO now]",
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        luceneValidate(value).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error(result.data.message));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "alertChannelName",
            title: vm.$i18n.t("alertrules.alertChannelName"),
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            // formRender: "MultiSelect",
            formRender: "FormSelect",
            multiple: true,
            selectAllowCreate: false,
            multiSelectWithToolbox: false,
            defaultVal: [],
            validate: [
                {
                    required: false,
                    type: "array",
                    max: 3,
                    message: "Max 3 Channels",
                    trigger: "change",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
            remoteOptions: async (queryValue, queryParams) => {
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`notificationchannels?${queryString}`);
                return result.data.options;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
        },
        {
            searchViews: ["AlertRules"],
            key: "alertWhen",
            title: vm.$i18n.t("alertrules.alertWhen"),
            sectionKey: "essential",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            options: ["above", "below"],
            defaultVal: "above",
            validate: [],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "alertValue",
            title: vm.$i18n.t("alertrules.alertValue"),
            sectionKey: "essential",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            inputMinNum: 0,
            defaultVal: 0,
            validate: [],
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "alertValueInfo",
            title: vm.$i18n.t("alertrules.alertValueInfo"),
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                return h(
                    "div",
                    `${params.row.currentValue} ${params.row.alertWhen} ${params.row.alertValue} ${params.row.alertValueUnit}`
                );
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "alertValueUnit",
            title: vm.$i18n.t("alertrules.alertValueUnit"),
            sectionKey: "essential",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            // options: ["count", "bytes", "KB", "MB", "GB"],
            options: [
                { label: "Count", value: "count" },
                { label: "Sum Bytes", value: "sumBytes" },
                { label: "Sum KB", value: "sumKBytes" },
                { label: "Sum MB", value: "sumMBytes" },
                { label: "Sum GB", value: "sumGBytes" },
            ],
            defaultVal: "count",
            validate: [],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "isAlerting",
            title: vm.$i18n.t("alertrules.isAlerting"),
            minWidth: 120,
            showInTable: true,
            withForm: false,
            watchField: function (item) {},
            filter: {
                type: "boolean",
            },
            render: (h, params) => {
                let Icon = params.row.isAlerting
                    ? h("BlinkingIcon", {
                          props: {
                              type: "md-megaphone",
                              color: "red",
                              size: 24,
                          },
                      })
                    : h("Icon", {
                          props: {
                              type: "ios-megaphone-outline",
                              color: "grey",
                              size: 24,
                          },
                      });
                return Icon;
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "currentValue",
            title: vm.$i18n.t("alertrules.currentValue"),
            showInTable: false,
            withForm: false,
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "alertMessage",
            title: vm.$i18n.t("alertrules.alertMessage"),
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            fieldType: "textarea",
            autosize: { minRows: 8 },
            defaultVal: "Alert: {{ruleName}}",
            validate: [
                {
                    required: true,
                    message: "Missing Message",
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["AlertRules"],
            key: "viewLogs",
            title: "Logs",
            minWidth: 100,
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                return h("ViewLogsBtn", {
                    props: { ruleData: params.row },
                });
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["AlertRules"],
            key: "ruleTest",
            title: vm.$i18n.t("alertrules.ruleTest"),
            minWidth: 100,
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                return h("RuleTestBtn", {
                    props: { ruleData: params.row },
                });
            },
            exportOptions: { canExport: false, callback: (value) => value },
        },
        /*Notification Channels*/
        {
            searchViews: ["NotificationChannels"],
            key: "channelName",
            title: vm.$i18n.t("notificationChannels.channelName"),
            minWidth: 180,
            sectionKey: "essential",
            description: "<p>The <strong>Name</strong> of your Channel.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    required: true,
                    message: "Missing Channel Name",
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (!/^[a-z0-9\-\_]*$/.test(value))
                            return callback(
                                new Error(`${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a - z 0 - 9 - _`)
                            );
                        if (value.length > 35)
                            return callback(new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 35`));
                        checkDocumentExist({
                            topic: "notificationChannelName",
                            fieldVal: value,
                            groupName: vm.formData.groupName,
                        }).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error("Duplicate ruleName"));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["NotificationChannels"],
            key: "channelType",
            title: vm.$i18n.t("notificationChannels.channelType"),
            minWidth: 120,
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            options: [
                { label: "Email", value: "email" },
                { label: "Telegram", value: "telegram" },
                { label: "Line", value: "line" }
            ],
            defaultVal: "email",
            validate: [],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["NotificationChannels"],
            key: "channelLineBotToken",
            title: vm.$i18n.t("notificationChannels.channelLineBotToken"),
            sectionKey: "essential",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    required: true,
                    message: "Missing Token",
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.channelType == "line" && value.length == 0)
                            return callback(new Error("Missing Token"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            vShowFieldVal: (item) => (item.channelType == "line" ? 1 : 0),
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["NotificationChannels"],
            key: "channelTelegramBotToken",
            title: vm.$i18n.t("notificationChannels.channelTelegramBotToken"),
            sectionKey: "essential",
            description: "",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    required: true,
                    message: "Missing Token",
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.channelType == "telegram" && value.length == 0)
                            return callback(new Error("Missing Token"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            vShowFieldVal: (item) => (item.channelType == "telegram" ? 1 : 0),
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["NotificationChannels"],
            key: "channelSendTo",
            title: vm.$i18n.t("notificationChannels.channelSendTo"),
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    required: true,
                    message: "Missing Send To",
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.channelType == "email" && value.indexOf("@") == -1)
                            return callback(new Error("Invalid Email"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["NotificationChannels"],
            key: "channelTest",
            title: vm.$i18n.t("notificationChannels.channelTest"),
            minWidth: 100,
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: false,
            render: (h, params) => {
                return h("ChannelTestBtn", {
                    props: { channelData: params.row, disabled: vm.isEditBtnDisabled },
                });
            },
            exportOptions: { canExport: false, callback: (value) => value },
            sortable: false,
        },
        {
            searchViews: ["RolesManage"],
            key: "roleName",
            fixed: "left",
            title: vm.$i18n.t("rolesmanage.roleName"),
            description: "<p>The <strong>Name</strong> of your role.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm"],
            formRender: "FormInput",
            defaultVal: "",
            validate: [
                {
                    required: true,
                    message: "Missing Role Name",
                    trigger: "blur",
                },
                {
                    validator: (rule, value, callback, source, options) => {
                        if (!/^[a-z0-9\-\_]*$/.test(value))
                            return callback(
                                new Error(`${vm.$i18n.t("rcmtable.validateIllegalCharsPrefix")}: a - z 0 - 9 - _`)
                            );
                        if (value.length > 35)
                            return callback(new Error(`${vm.$i18n.t("rcmtable.validateExceedLengthPrefix")}: 35`));
                        checkDocumentExist({
                            topic: "roleName",
                            fieldVal: value,
                            groupName: vm.formData.groupName,
                        }).then((result) => {
                            if (!result.data.success) {
                                return callback(new Error("Duplicate roleName"));
                            }
                            callback();
                        });
                    },
                    trigger: "blur",
                },
            ],
            watchField: function (item) {},
            filter: {
                type: "string",
            },
        },
        {
            searchViews: ["RolesManage"],
            key: "dashboardPermission",
            description: "<p></p>",
            title: "Dashboards",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "TableCheckbox",
            options: [],
            defaultVal: "",
            watchField: function (value, item) {},
            filter: {
                type: "string",
            },
            validate: [],
            render: (h, params) => {
                var tableData = [];
                tableData.push(params.row.dashboardPermission);

                return h(
                    "RolePermissionBtn",
                    {
                        props: {
                            tableData: tableData,
                            title: "Dashboard Permission",
                        },
                    },
                    ""
                );
            },
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["RolesManage"],
            key: "domainManagePermission",
            description: "<p></p>",
            title: "Domain Manage",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "TableCheckbox",
            options: [],
            defaultVal: "",
            watchField: function (value, item) {},
            filter: {
                type: "string",
            },
            validate: [],
            render: (h, params) => {
                var tableData = [];
                tableData.push(params.row.domainManagePermission);

                return h(
                    "RolePermissionBtn",
                    {
                        props: {
                            tableData: tableData,
                            title: "Domain Manage Permission",
                        },
                    },
                    ""
                );
            },
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["RolesManage"],
            key: "groupResourcesPermission",
            description: "<p></p>",
            title: vm.$i18n.t(`routesMenu.permissionItems.GroupResource.showName`),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "TableCheckbox",
            options: [],
            defaultVal: "",
            watchField: function (value, item) {},
            filter: {
                type: "string",
            },
            validate: [],
            render: (h, params) => {
                var tableData = [];
                tableData.push(params.row.groupResourcesPermission);

                return h(
                    "RolePermissionBtn",
                    {
                        props: {
                            tableData: tableData,
                            title: "Group Manage Permission",
                        },
                    },
                    ""
                );
            },
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["RolesManage"],
            key: "intelligentProtectionPermission",
            description: "<p></p>",
            title: vm.$i18n.t(`routesMenu.IntelligentProtection`),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "TableCheckbox",
            options: [],
            defaultVal: "",
            watchField: function (value, item) {},
            filter: {
                type: "string",
            },
            validate: [],
            render: (h, params) => {
                var tableData = [];
                tableData.push(params.row.intelligentProtectionPermission);

                return h(
                    "RolePermissionBtn",
                    {
                        props: {
                            tableData: tableData,
                            title: "Intelligent Protection Permission",
                        },
                    },
                    ""
                );
            },
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["RolesManage"],
            key: "billingPermission",
            description: "<p></p>",
            title: vm.$i18n.t(`routesMenu.permissionItems.Billing.showName`),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "TableCheckbox",
            options: [],
            defaultVal: "",
            watchField: function (value, item) {},
            filter: {
                type: "string",
            },
            validate: [],
            render: (h, params) => {
                var defaultBillingPermission = {
                    name: "Billing",
                    read: false,
                    write: false,
                    _showChildren: false,
                    children: [],
                };

                var billingPermission = params.row.billingPermission
                    ? params.row.billingPermission
                    : defaultBillingPermission;

                var tableData = [];
                tableData.push(billingPermission);

                return h(
                    "RolePermissionBtn",
                    {
                        props: {
                            tableData: tableData,
                            title: "Billing Permission",
                        },
                    },
                    ""
                );
            },
        },
        {
            searchViews: ["SlbSetting", "VirtualSlbSetting", "RecordLineGroups"],
            key: "recordLineGroupName",
            minWidth: tableColumnWidth.recordLineGroupName,
            title: vm.$i18n.t("recordLineGroups.recordLineGroupName"),
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            options: [],
            defaultVal: "",
            filter: {
                type: "string",
            },
            validate: [
                {
                    required: true,
                    message: `${vm.$i18n.t("recordLineGroups.recordLineGroupName")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "blur",
                },
            ],
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "nodeConnectSSHKey",
            title: "SSH PRIVATE KEY",
            sectionKey: "node_connect",
            description: "<p>SSH Key for node connection.</p>",
            explainPoptip: "ExplainPrivateKeys",
            showInTable: false,
            withForm: false,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            multiple: false,
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.sshKeyName = `*${queryValue}*`;
                queryParams.withDefault = true;
                let queryString = new URLSearchParams(queryParams).toString();
                let result = await getFormOptions(`/sshkeynames?${queryString}`);
                return result.data.options;
            },
            // queryParamsFields: [{ fieldName: "groupName", queryIf: val => val !== "" }],
            defaultVal: "default",
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "nodeConnectSSHUser",
            title: "SSH USER",
            sectionKey: "node_connect",
            description: "<p>SSH User for node connection.</p>",
            showInTable: false,
            withForm: false,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: "ubuntu",
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "nodeConnectSSHHost",
            title: "SSH HOST",
            sectionKey: "node_connect",
            description: "<p>SSH ip for node connection.</p>",
            showInTable: false,
            withForm: false,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInput",
            defaultVal: (item) => item.externalIP,
        },
        {
            searchViews: ["SlbSetting", "EdgeSetting", "NinjaSetting"],
            key: "nodeConnectSSHPort",
            title: "SSH PORT",
            sectionKey: "node_connect",
            description: "<p>SSH port for node connection.</p>",
            showInTable: false,
            withForm: false,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            inputMaxNum: 65535,
            inputMinNum: 1,
            defaultVal: 22,
        },
        {
            searchViews: ["Scroll"],
            key: "directoryUpload",
            title: "Directory Upload",
            sectionKey: "essential",
            description: "<p>Upload your directory here.</p>",
            showInTable: true,
            withForm: true,
            withTable: true,
            formTypes: [],
            minWidth: 60,
            // formRender: "UploadPath",
            formRender: "FormUpload",
            uploadEndPoint: "minio",
            fieldType: "textarea",
            defaultVal: "",
            handleUpload: {
                format: [],
                // 上傳前更新的欄位
                hasbeforeUpdateFields: true,
                // 上傳成功後更新的欄位
                successUpdateFields: [],
                webkitdirectory: true,
                multiple: true,
                showUploadList: false,
            },
            // exportOptions: { canExport: false, callback: (value) => value },
        },
        {
            searchViews: ["Scroll"],
            key: "fileUpload",
            title: "File Upload",
            sectionKey: "essential",
            description: "<p>Upload your file here.</p>",
            showInTable: true,
            withForm: true,
            withTable: true,
            formTypes: [],
            minWidth: 60,
            // formRender: "UploadPath",
            formRender: "FormUpload",
            uploadEndPoint: "minio",
            fieldType: "textarea",
            defaultVal: "",
            handleUpload: {
                format: [],
                // 上傳前更新的欄位
                hasbeforeUpdateFields: true,
                // 上傳成功後更新的欄位
                successUpdateFields: [],
                showUploadList: false,
            },
        },
        {
            searchViews: ["Scroll"],
            key: "minioCodeEditer",
            title: "Scroll Content",
            sectionKey: "essential",
            description: "<p></p>",
            showInTable: false,
            withForm: true,
            withTable: false,
            formTypes: ["createForm", "editForm"],
            formRender: "CodeEditorV3",
            defaultVal: [],
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (value.length == 0) {
                            return callback(new Error("Add Scrolls"));
                        }
                        let actionType = vm.formData.actionType;
                        let directoryUpload = vm.formData.directoryUpload;
                        let directoryName = vm.formData.minioCodeEditer[0].path.split("/")[0];
                        let tableData = vm.tableData;
                        if (actionType == "edit" && directoryName == directoryUpload) {
                            tableData = vm.tableData.filter((e) => e.directoryUpload != directoryName);
                        }
                        let findDirectory = tableData.find((e) => e.directoryUpload == directoryName);
                        if (findDirectory) {
                            return callback(new Error("Reuse Scrolls Name"));
                        }
                        callback();
                    },
                },
            ],
        },
        {
            searchViews: ["GroupResource"],
            key: "maxCertManageCount",
            title: vm.$i18n.t("groupresource.maxCertManageCount"),
            align: "center",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormInputNumber",
            inputMinNum: 0,
            inputMaxNum: resourceLimits.groupMaxCertManageCount,
            defaultVal: resourceLimits.groupMaxCertManageCount,
            filter: {
                type: "range",
            },
            validate: [
                {
                    required: true,
                    type: "number",
                    message: `${vm.$i18n.t("groupresource.maxCertManageCount")} ${vm.$i18n.t(
                        "rcmtable.validateEmptyText"
                    )}`,
                    trigger: "change",
                },
            ],
        },
        {
            searchViews: ["CertsManage"],
            key: "certUsedBy",
            title: vm.$i18n.t("certsmanage.certUsedBy"),
            sectionKey: "essential",
            description: "",
            showInTable: true,
            withForm: false,
            formTypes: [],
            minWidth: 200,
            tooltip: false,
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.certUsedBy,
                        maxShow: "5",
                        title: vm.$i18n.t("certsmanage.certUsedBy"),
                        width: "350",
                    },
                });
            },
            options: [],
            defaultVal: [],
        },
        {
            searchViews: ["owasp"],
            key: "owaspThresholdLimit",
            title: vm.$i18n.t("owasp.owaspThresholdLimit"),
            description: "<p>The request has reached the set threshold.</p>",
            align: "center",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            minWidth: 180,
            options: [
                { label: "10", value: 10 },
                { label: "7", value: 7 },
                { label: "4", value: 4 },
                { label: "1", value: 1 },
            ],
            defaultVal: 5,
            disabled: () => true,
            validate: [
                // {
                //     required: true,
                //     message: `${JSON.stringify(vm.formData)} :::${vm.$i18n.t("owasp.owaspThresholdLimit")} ${vm.$i18n.t(
                //         "rcmtable.validateEmptyText"
                //     )}`,
                //     trigger: "blur",
                // },
            ],
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["owasp"],
            key: "owaspParanoiaLevel",
            title: vm.$i18n.t("owasp.owaspParanoiaLevel"),
            description:
                "<p>OWASP Paranoia Level refers to a security setting that determines the strictness of security checks.</p>",
            align: "center",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: "FormSelect",
            minWidth: 180,
            options: [
                { label: "5", value: 5 },
                { label: "4", value: 4 },
                { label: "3", value: 3 },
                { label: "2", value: 2 },
                { label: "1", value: 1 },
            ],
            defaultVal: 2,
            disabled: () => true,
            validate: [
                // {
                //     required: true,
                //     message: `${vm.$i18n.t("owasp.owaspParanoiaLevel")} ${vm.$i18n.t("rcmtable.validateEmptyText")}`,
                //     trigger: "blur",
                // },
            ],
            filter: {
                type: "range",
            },
        },
        {
            searchViews: ["owasp"],
            key: "owaspDetectRules",
            title: vm.$i18n.t("owasp.owaspDetectRules"),
            description: "<p>Trigger OWASP's rules.</p>",
            align: "center",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: () => import("@/components/FormItems/OwaspDetectRulesTable"),
            minWidth: 180,
            defaultVal: [
                "REQUEST-930-APPLICATION-ATTACK-LFI",
                "REQUEST-931-APPLICATION-ATTACK-RFI",
                "REQUEST-932-APPLICATION-ATTACK-RCE",
                "REQUEST-941-APPLICATION-ATTACK-XSS",
                "REQUEST-942-APPLICATION-ATTACK-SQLI",
            ],
            filter: {
                type: "string",
                searchKey: "owaspDetectRules",
            },
            render: (h, params) => {
                return h("TagList", {
                    props: {
                        listData: params.row.owaspDetectRules,
                        maxShow: "5",
                        title: vm.$i18n.t("owasp.owaspDetectRules"),
                        width: "350",
                    },
                });
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "isUseOwasp",
            title: vm.$i18n.t("domainsetting.isUseOwasp"),
            sectionKey: "owasp",
            description: "<p>Toggle the switch on to allow OWASP.</p>",
            showInTable: false,
            withTable:
                vm.$store.state.queryGroupName == "all"
                    ? true
                    : vm.$store.state.platformPlan.multiTenancyConfigs.owaspSetting.includes(
                          vm.$store.state.queryGroupName
                      ),
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSwitch",
            defaultVal: (item) => {
                return false;
            },
            type: "boolean",
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.owaspSetting.includes(item.groupName) ? 1 : 0;
            },
        },
        {
            searchViews: ["DomainSetting", "AppProfile"],
            key: "owaspRuleName",
            title: vm.$i18n.t("domainsetting.owaspRuleName"),
            sectionKey: "owasp",
            description: "<p>OWASP usage rule name.</p>",
            showInTable: false,
            withTable:
                vm.$store.state.queryGroupName == "all"
                    ? true
                    : vm.$store.state.platformPlan.multiTenancyConfigs.owaspSetting.includes(
                          vm.$store.state.queryGroupName
                      ),
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "FormSelect",
            defaultVal: "",
            filter: {
                type: "select",
            },
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.owaspSetting.includes(item.groupName) &&
                    item.isUseOwasp == true
                    ? 1
                    : 0;
            },
            queryParamsFields: [{ fieldName: "groupName", queryIf: (val) => val !== "" }],
            remoteOptions: async (queryValue, queryParams) => {
                queryValue = queryValue || "*";
                queryParams.owaspRuleName = `*${queryValue}*`;
                const queryString = new URLSearchParams(queryParams).toString();
                const result = await getFormOptions(`owasprulenames?${queryString}`);
                return result.data.options;
            },
            validate: [
                {
                    validator: (rule, value, callback, source, options) => {
                        if (vm.formData.isUseOwasp == true && value.length == 0)
                            return callback(new Error("Missing OWASP Rule Name"));
                        callback();
                    },
                    trigger: "blur",
                },
            ],
            explainPoptip: "ExplainPoptip",
            explainPoptipProps: {
                title: `OWASP ${vm.$t("global.object")}${vm.$t("global.explanation")}`,
                tableData: [
                    {
                        object: "sys_default",
                        explain: vm.$t("domainsetting.explainObject.sys_default"),
                    },
                ],
            },
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "rewriteUrlRules",
            title: vm.$i18n.t("domainsetting.rewriteUrlRules"),
            sectionKey: "advanced",
            description: "<p>Origins URL Path Rewrite Rules.</p>",
            explainPoptip: "ExplainURLRewrite",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "RewriteURLModal",
            defaultVal: [],
            disabled: () => !vm.$can("write", "domainManagePermission", "FQDNConfig"),
            vShowFieldVal: () => 1,
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },
        },
        {
            searchViews: ["AppProfile", "DomainSetting"],
            key: "cacheUrlRules",
            title: vm.$i18n.t("domainsetting.cacheUrlRules"),
            sectionKey: "advanced",
            description: "<p>Keep Specified Args.</p>",
            explainPoptip: "ExplainCacheUrlRule",
            showInTable: false,
            withForm: true,
            formTypes: ["createForm", "editForm", "multiEditForm"],
            formRender: "CacheURLModal",
            defaultVal: [],
            disabled: () => !vm.$can("write", "domainManagePermission", "FQDNConfig"),
            vShowFieldVal: (item) => {
                return vm.$store.state.platformPlan.multiTenancyConfigs.isPresentCacheUrlRules.includes(item.groupName)
                    ? 1
                    : 0;
            },
        },
        {
            searchViews: ["DDoSAnalyze", "BehaviorAnalyze"],
            key: "settingType",
            title: vm.$i18n.t("intelligentProtectionSettings.settingType"),
            showInTable: false,
            withForm: true,
            vShowFieldVal: () => 0,
        },
        {
            searchViews: ["DDoSAnalyze", "BehaviorAnalyze"],
            key: "wafRuleList",
            title: vm.$i18n.t("intelligentProtectionSettings.wafRuleList"),
            sectionKey: "essential",
            description:
                "<p>Implement custom logic atop automatically applied WAF rules during intelligent protection.</p>",
            showInTable: true,
            withForm: true,
            formTypes: ["createForm", "editForm"],
            formRender: () => import("@/components/FormItems/wafRuleList/wafRuleList"),
            defaultVal: [],
            render: (h, params) => {
                return h("JsonPoptip", {
                    props: {
                        jsonData: params.row.wafRuleList,
                        title: vm.$i18n.t("intelligentProtectionSettings.wafRuleList"),
                        width: "350",
                        buttonText: "",
                        buttonIcon: "md-eye",
                    },
                });
            },
            exportOptions: { canExport: true, callback: (value) => JSON.stringify(value) },    
        },
    ];
}



// WEBPACK FOOTER //
// ./src/data/rcmtableFieldsV2.js